// auditpm-v7
import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase, AUDIT_TYPES, JOB_STATUSES, SHMS_SCOPES, auditorMatchesJobType, auditScopeCheck, AUDITOR_SCOPE_NAMES } from '../lib/supabase'
import { calcBizSAFEMandays, calcSHMSMandays, complexityBasisText, resolveCT17Industry } from '../lib/ct17'
import { selectTemplate, generateAuditPlanRows, parseStartTime, remapAuditorNames } from '../lib/auditPlanEngine'
import { format, parseISO, subMonths } from 'date-fns'
import { getTemplate, applyTemplate } from './Settings'
import { emailHtml, textEmailHtml } from '../lib/emailHtml'
import { useAuth } from '../lib/AuthContext'
import { toast, failed } from '../lib/toast'
import CT17Reference from '../components/CT17Reference'
import PackageViewer from '../components/PackageViewer'
import BizsafeApplicationPanel from '../components/BizsafeApplicationPanel'
import PackageGenerator from '../components/PackageGenerator'
import { isPreparingStale } from '../lib/reportPackage'

import CombinedAuditPanel from '../components/CombinedAuditPanel'
import { cancelContract, reopenContract, lastCancelNote } from '../lib/contractCancel'
import { oneLine } from '../lib/oneLine'
import { canActAsDecisionMaker } from '../lib/personnel'
import { workflowState, displayState, ownerOf, nextActionOf, stateChip } from '../lib/workflow'
// isWshcWindowOpen: renamed on import — this page already has its own `wshcWindowOpen` (the window DATE).
import { isPortalJob, portalStepText, portalNextAction, portalChip, canEndJob, isEnded, wshcWindowOpen as isWshcWindowOpen, wshcWindowOpensOn, daysSinceApplyEmail, fmtDay, todaySg } from '../lib/bizsafePortal'
import { isNewApplication, applyingLevel } from '../lib/quoteTitle'

const STATUSES = Object.values(JOB_STATUSES)
const FIRM_NAME = () => localStorage.getItem('firm_name') || 'CCIS Singapore Pte Ltd'
const DEFAULT_CHECKLIST_URL = 'https://www.tal.sg/wshc/resources/publications/checklists-and-articles/bizsafe-level-3-risk-management-audit-checklist'
const ANNEX_HJ_URL = 'https://www.tal.sg/wshc/-/media/tal/wshc/resources/publications/codes-of-practice/files/code-of-practice-risk-management-third-revision-2021.ashx'
const FATIGUE_URL = 'https://www.tal.sg/wshc/-/media/tal/wshc/programmes/files/fatigue-management.ashx'

// Formatted (rich HTML) audit-plan email for the clipboard. mailto bodies are plain text only, so this is what
// lets the office paste into Outlook with bold headings, single line-spacing and true nested bullets intact.
function buildAuditPlanHtml({ greet, auditDate, auditTime, auditSite, ackUrl, copLink, firm }) {
  const esc = s => (s == null ? '' : String(s)).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))
  // All paragraph margins are 0 and spacing comes from explicit blank-line paragraphs, so the gaps survive
  // even when Outlook/Gmail normalise <p> margins on paste (the reason margins alone rendered cramped before).
  const p = 'margin:0;line-height:1.5'
  const gap = '<p style="margin:0;line-height:1.5">&nbsp;</p>'   // a real, always-visible blank line
  const li = 'margin:0 0 4px 0'
  const ul = 'margin:0;padding-left:26px'
  return `<div style="font-family:Calibri,'Segoe UI',Arial,sans-serif;font-size:11pt;line-height:1.5;color:#1f1f1f">
<p style="${p}">Dear ${esc(greet)},</p>
${gap}
<p style="${p}">Please acknowledge receipt of this Audit Plan by clicking the link below:<br><a href="${esc(ackUrl)}">${esc(ackUrl)}</a></p>
${gap}
<p style="${p}"><b>Audit Date &amp; Time:</b> ${esc(auditDate)}, ${esc(auditTime)}<br><b>Audit Location:</b> ${esc(auditSite)}</p>
${gap}
<p style="${p}">Kindly ensure the following preparations are made in advance:</p>
<ul style="${ul}">
<li style="${li}">bizSAFE Level 1 / TEWP Certificate</li>
<li style="${li}">bizSAFE Level 2 Certificate</li>
<li style="${li}">Ensure all RM documents including Risk Assessments and Safe Work Procedures are updated to the current year</li>
<li style="${li}">NEW: Have at least 1 programme to improve health of workers in general (e.g., through health screening and health coaching, healthy diet, active lifestyle, smoking cessation, weight management and stress management)</li>
<li style="${li}">NEW: Must include mental well-being RA, personal health-risk RA: <a href="${ANNEX_HJ_URL}">Refer to Annex H to J for examples</a></li>
<li style="${li}">NEW: If you have full-time drivers, ensure that a <a href="${FATIGUE_URL}">Fatigue Management Plan</a> is in place</li>
<li style="${li}">NEW: Ensure RM Procedure is updated to the latest <a href="${esc(copLink)}">Code of Practice</a></li>
<li style="${li}">The following people will need to be present:
<ul style="margin:4px 0 0 0;padding-left:26px">
<li style="${li}">RA Approver</li>
<li style="${li}">RM/RA Team Leader</li>
<li style="${li}">RM/RA Members</li>
<li style="${li}">Implementation Person for Additional Control Measures</li>
<li style="${li}">At least 3 additional workers/equivalent (if company has less than 3 workers, we will declare accordingly on the audit report)</li>
<li style="${li}">At least 1 supervisor/equivalent (if company does not have a supervisor, we will declare accordingly on the audit report)</li>
</ul>
</li>
</ul>
${gap}
<p style="${p}">Thank you.</p>
</div>`
}

export default function JobDetail() {
  const { id } = useParams()
  const { profile, role, logAction, user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // View (default) vs Edit — STRICTLY a rendering/interaction toggle. No save/signature/COI logic runs
  // on switching; in view mode the whole body is wrapped in a disabled <fieldset> so nothing is editable.
  const [mode, setMode] = useState(searchParams.get('edit') ? 'edit' : 'view')
  // Plan & Prepare presentation state (Phase 1 — reveal-on-intent artifacts; no workflow/logic change).
  const [ct17Open, setCt17Open] = useState(false)       // CT17 basis details expander
  const [schedOpen, setSchedOpen] = useState(false)     // Audit schedule shown in full (else folded)
  const [schedMode, setSchedMode] = useState('preview') // 'preview' | 'edit' (edit mode only)
  const [planOpen, setPlanOpen] = useState(false)       // "More details" open
  const [job, setJob] = useState(null)
  // A cancelled job is read-only (Reopen first) — also when opened with ?edit from the Jobs list.
  useEffect(() => { if (job?.status === 'Cancelled') setMode('view') }, [job?.status])
  // A job whose audit plan hasn't been sent yet opens straight in Edit — that is the work to do; a sent plan opens
  // read-only. Once per job, so "Save & close" and "Cancel" still return to view mode afterwards.
  const autoEditFor = useRef(null)
  useEffect(() => {
    if (!job || job.id === 'new' || autoEditFor.current === job.id) return
    autoEditFor.current = job.id
    const sent = !!(job.audit_plan_sent_at || job.plan_sent_date)
    if (!sent && role !== 'Auditor' && !['Cancelled', 'Completed'].includes(job.status)) setMode('edit')
  }, [job, role])
  const [client, setClient] = useState(null)
  const [newExpiry, setNewExpiry] = useState('')
  const [finalising, setFinalising] = useState(false)
  const [a1Days, setA1Days] = useState('')   // per-auditor on-site days, allocated at finalisation
  const [a2Days, setA2Days] = useState('')
  const [allAuditors, setAllAuditors] = useState([])
  const [coiResponses, setCoiResponses] = useState([])
  const [ackResponse, setAckResponse] = useState(null)
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [calc, setCalc] = useState(null)

  // Audit plan state
  const [startTime, setStartTime] = useState('0900')
  const [numLocations, setNumLocations] = useState(1)
  // Explicit planning inputs for template selection (no inference from man-days alone).
  const [auditStructure, setAuditStructure] = useState('Parallel') // 2-auditor day-span: Parallel(1 day)/Split(2 days)
  const [halfDayPosition, setHalfDayPosition] = useState('Day2')    // 1-auditor 1.5 md: which day is the half
  const [secondAuditorBlock, setSecondAuditorBlock] = useState('Morning') // 2-auditor 1.5 md same-day: AM/PM
  const [auditDateDay2, setAuditDateDay2] = useState('')            // Day-2 date for 2-day spans
  const [day2Start, setDay2Start] = useState('0900')               // Day-2 start time for 2-day spans
  const [blockedReason, setBlockedReason] = useState('')           // set when no approved template applies
  const [twoDaySpan, setTwoDaySpan] = useState(false)
  // Current audit-planning basis (distinct from the original questionnaire values in form_data).
  // Drives the live CT17 recalculation and Lead-Auditor requirement at the planning stage.
  const [planningEmployees, setPlanningEmployees] = useState('')
  const [planningIndustry, setPlanningIndustry] = useState(null)
  const [planningBasisReason, setPlanningBasisReason] = useState('')
  const [planRows, setPlanRows] = useState([])
  const [editedRows, setEditedRows] = useState(null) // null = use auto, array = current rows
  const [manuallyEdited, setManuallyEdited] = useState(false) // true ONLY after a real user row edit
  const [planType, setPlanType] = useState('')
  // Schedule inputs as last loaded/saved (+ the auditor names then). An edited schedule only prompts when the
  // CURRENT inputs differ from this — never just because the page finished loading. null while (re)loading.
  const planBaseline = useRef(null)
  const [planBaselineKey, setPlanBaselineKey] = useState(0)   // bumped at the end of every load
  const [planChanges, setPlanChanges] = useState([])          // what changed since the baseline, e.g. ['auditors']

  // Audit plan fields (editable)
  const [auditObjective, setAuditObjective] = useState('')
  const [auditCriteriaField, setAuditCriteriaField] = useState('')
  const [auditScopeField, setAuditScopeField] = useState('')
  const [projectName, setProjectName] = useState('')
  const [auditApproachNote, setAuditApproachNote] = useState('')   // why office-based / combined-approach etc. (NOT the project name)
  // Audit type + report reference — needed for manually-created jobs (no questionnaire / quotation behind them).
  const [applicationType, setApplicationType] = useState('New Application')
  const [reportRef, setReportRef] = useState('')
  const [auditSiteAddress, setAuditSiteAddress] = useState('')   // NEVER auto-filled from HQ — explicit action only
  const [copiedFromHq, setCopiedFromHq] = useState(false)        // true only right after the user clicks "Copy HQ address"
  const [rmLeaderName, setRmLeaderName] = useState('')
  const [rmLeaderPhone, setRmLeaderPhone] = useState('')
  // Field-level required validation, populated only on a save/send attempt (not on blur).
  const [fieldErrors, setFieldErrors] = useState({})
  const hasErr = k => !!fieldErrors[k]
  const errInput = k => hasErr(k) ? { borderColor: 'var(--danger)', boxShadow: '0 0 0 1px var(--danger)' } : undefined
  const errLabel = k => hasErr(k) ? { color: 'var(--danger)' } : undefined
  const clearErr = k => setFieldErrors(e => e[k] ? { ...e, [k]: false } : e)
  const [checklistUrl, setChecklistUrl] = useState('')
  const [planFieldsDirty, setPlanFieldsDirty] = useState(false)
  const [mandayJustification, setMandayJustification] = useState('')
  const [justificationFromQuote, setJustificationFromQuote] = useState(false)
  const [auditDecision, setAuditDecision] = useState(null)
  const [decisionForm, setDecisionForm] = useState({ outcome: 'Proceed', remarks: '' })
  const [savingDecision, setSavingDecision] = useState(false)
  const [coiDecisions, setCoiDecisions] = useState([])
  // STRICTLY display-only flag for the cosmetic "COI completed" checklist indicator on a combined MEMBER job.
  // True when the primary holds a completed declaration for the same auditor(s). It is computed into this
  // separate boolean and never merged into coiResponses/coiDecisions, so conflict logic, the Validate-tab dot,
  // and the plan-send high-risk check are completely untouched. It feeds ONE display line and nothing else.
  const [combinedCoiOk, setCombinedCoiOk] = useState(false)
  const [coiDecisionForm, setCoiDecisionForm] = useState({})
  const [savingCoiDecision, setSavingCoiDecision] = useState(false)
  // Office checkpoint (post-submission workflow): current user's Personnel record + their pending mandatory
  // competency gap, used to gate "Approve & Submit to WSHC". Plus the package viewer + action forms.
  const [myPerson, setMyPerson] = useState(null)
  const [myPendingMandatory, setMyPendingMandatory] = useState(false)
  const [showPackage, setShowPackage] = useState(false)
  const [returnForm, setReturnForm] = useState({ open: false, reason: '' })
  // SAC review sign-off: four fixed items, ticked by default ("Reviewed and approved"). A missing key = ticked.
  const REVIEW_ITEMS = ['Audit report overall', 'Audit checklist', 'Interview sheet', 'Physical Inspection & Findings']
  const [reviewChecks, setReviewChecks] = useState({})
  const [wshcForm, setWshcForm] = useState({ reason: '' })
  const [officeBusy, setOfficeBusy] = useState(false)
  // Client report acknowledgement (gate inside Pending Office Review): latest ack + current report version.
  const [reportAck, setReportAck] = useState(null)
  const [reportVersion, setReportVersion] = useState(0)
  const [reportSnapOld, setReportSnapOld] = useState(false)    // latest snapshot predates the complete-checklist fix
  const [pkgSnap, setPkgSnap] = useState(null)                 // latest snapshot's package_* fields (status/path/error)
  const [pkgFlag, setPkgFlag] = useState(true)                 // firm_settings.official_package_enabled (kill switch)
  // New WSHC client-portal process (bizSAFE): the switch, and the office's inputs on new-process jobs.
  const [portalOn, setPortalOn] = useState(false)              // firm_settings.bizsafe_client_portal
  const [pfAnswer, setPfAnswer] = useState('')                 // Finalise question: '' | 'yes' | 'no' (never remembered)
  const [endReason, setEndReason] = useState('')
  const [ending, setEnding] = useState(false)
  const [reopenForm, setReopenForm] = useState({ open: false, reason: '' })
  const [applicantForm, setApplicantForm] = useState({ open: false, name: '', email: '' })
  const [resendForm, setResendForm] = useState({ open: false, name: '', email: '' })   // apply email to someone else / again
  const [pkgRenderer, setPkgRenderer] = useState('chromium')   // 'chromium' (server) | 'raster' (html2canvas fallback)
  const [pkgBusy, setPkgBusy] = useState(false)                // a server render was requested; awaiting ready/error
  const [genKey, setGenKey] = useState(0)                      // >0 mounts PackageGenerator (raster/html2canvas fallback)
  const [photoConsent, setPhotoConsent] = useState(null)       // 'Yes' | 'No' | null — legal photo consent (snapshot)
  const [photoAnswer, setPhotoAnswer] = useState(null)         // 'Yes' | 'No' | null — the auditor's own answer (from 4 Nov 2026); wins when set
  const [revisions, setRevisions] = useState([])               // per-version revision history (internal/SAC traceability)
  const [everHadPkg, setEverHadPkg] = useState(false)          // any version has a rendered official package
  const [ackEmail, setAckEmail] = useState('')
  const [ackSending, setAckSending] = useState(false)
  const [mpReport, setMpReport] = useState(null)   // audit_reports manpower control row
  const [mpReason, setMpReason] = useState('')
  const [mpBusy, setMpBusy] = useState(false)
  // Outstanding items the auditor logged during execution (read-only office view, so the office can see WHY a
  // report hasn't been submitted yet without logging into the auditor's account).
  const [outstandingItems, setOutstandingItems] = useState([])
  // Session evidence (internal only — proof of site attendance; NOT in the formal report / client view)
  const [sessionPhoto, setSessionPhoto] = useState(null)     // audit_evidence_files row (evidence_type='session_photo')
  const [sessionPhotoUrl, setSessionPhotoUrl] = useState(null)
  const [noPhotoReason, setNoPhotoReason] = useState(null)   // audit_conduct.no_session_photo_reason
  // Invoice clearance gate (lightweight control before sending the report to the client)
  const [invoiceModal, setInvoiceModal] = useState(false)
  const [invCleared, setInvCleared] = useState(false)
  const [invReason, setInvReason] = useState('')
  const [invoiceAction, setInvoiceAction] = useState('send')   // which action the invoice gate continues into: 'send' | 'wshc'
  // WSHC 2-month submission-window gate: manager override (typed reason, logged). Ref holds the confirmed
  // reason across the invoice-modal chain so the too-early check isn't re-triggered mid-flow.
  const [wshcOverrideModal, setWshcOverrideModal] = useState(false)
  const [wshcOverrideReason, setWshcOverrideReason] = useState('')
  const wshcOverrideRef = useRef('')
  // Post-approval routing: the Decision-Maker hands the downstream office work to a "report handler".
  const [handlers, setHandlers] = useState([])     // active user_profiles with can_handle_reports = true
  const [routeTo, setRouteTo] = useState('')       // selected handler id at approval (blank = keep with the approver)
  const [jtab, setJtab] = useState(null)           // JobDetail workflow tab: plan | validate | report | finalise
  // Application acceptance is recorded at QUOTATION SEND (no separate review form). Read it from the linked
  // quotation for a minimal read-only traceability line — informational only.
  const [quoteAccept, setQuoteAccept] = useState(null)
  // Signed agreement, resolved from the originating quotation (authoritative) so the "View signed agreement"
  // link + dates are available at EVERY job stage — even for jobs where convert-to-job didn't copy the token.
  const [signedQuote, setSignedQuote] = useState(null)
  // Eligible report handlers for the "Route to" dropdown (curated in Settings → User management).
  useEffect(() => {
    supabase.from('user_profiles').select('id, name, role').eq('can_handle_reports', true).order('name')
      .then(({ data }) => setHandlers(data || []))
  }, [])
  // Default office handler: the job's current handler, else Joanne Ho (the usual handler — Winnie, 20 Oct 2026),
  // else the Document Controller. Changeable before approving.
  useEffect(() => {
    const usual = handlers.find(h => /^joanne ho$/i.test((h.name || '').trim())) || handlers.find(h => h.role === 'Document Controller')
    setRouteTo(job?.assigned_to_id || usual?.id || '')
  }, [job?.assigned_to_id, handlers])
  useEffect(() => {
    if (!job?.quote_ref) { setQuoteAccept(null); return }
    supabase.from('quotations').select('application_accepted_by, application_accepted_at')
      .eq('quote_ref', job.quote_ref).not('application_accepted_at', 'is', null)
      .order('application_accepted_at', { ascending: false }).limit(1)
      .then(({ data }) => setQuoteAccept(data?.[0] || null))
  }, [job?.quote_ref])
  // Resolve the signed agreement from the originating quotation (the token/dates live there and are always
  // present once signed — the job row may not have them if it was created before/without the token copy).
  useEffect(() => {
    if (!job?.quote_ref) { setSignedQuote(null); return }
    supabase.from('quotations').select('signature_token, signed_date, sent_date, status')
      .eq('quote_ref', job.quote_ref)
      .order('signed_date', { ascending: false, nullsFirst: false }).limit(1)
      .then(({ data }) => setSignedQuote(data?.[0] || null))
  }, [job?.quote_ref])

  // Session evidence: the latest session_photo (with a signed URL) and/or the auditor's no-photo reason.
  // Internal office use only — never rendered in the formal report or shown to the client.
  async function loadSessionEvidence() {
    const { data: ev } = await supabase.from('audit_evidence_files')
      .select('id, storage_path, file_name, mime, size_bytes, created_at')
      .eq('job_id', id).eq('evidence_type', 'session_photo').is('archived_at', null)
      .order('created_at', { ascending: false }).limit(1)
    const photo = ev?.[0] || null
    setSessionPhoto(photo)
    if (photo?.storage_path) {
      const { data: u } = await supabase.storage.from('audit-evidence').createSignedUrl(photo.storage_path, 1800)
      setSessionPhotoUrl(u?.signedUrl || null)
    } else setSessionPhotoUrl(null)
    const { data: cd } = await supabase.from('audit_conduct').select('*').eq('job_id', id).maybeSingle()
    setNoPhotoReason((cd?.no_session_photo_reason || '').trim() || null)
    setPhotoAnswer(cd?.photo_consent === 'Yes' || cd?.photo_consent === 'No' ? cd.photo_consent : null)
  }
  // Outstanding items are captured on the EXECUTION job (the primary, for a combined audit). Read-only here —
  // the auditor edits them on their side; the office just needs visibility into what's holding up submission.
  async function loadOutstanding() {
    let execId = id
    if (job?.combined_group_id && !job?.is_combined_primary) {
      const { data: pj } = await supabase.from('jobs').select('id').eq('combined_group_id', job.combined_group_id).eq('is_combined_primary', true).maybeSingle()
      if (pj?.id) execId = pj.id
    }
    const { data } = await supabase.from('audit_outstanding_items')
      .select('id, description, status, sort_order').eq('job_id', execId).is('archived_at', null).order('sort_order')
    setOutstandingItems(data || [])
  }
  // Force-download signed URL for a stored object (private bucket; signed URL only, never a public URL).
  async function downloadEvidence(storage_path, file_name) {
    if (!storage_path) return
    const { data } = await supabase.storage.from('audit-evidence').createSignedUrl(storage_path, 120, { download: file_name || true })
    if (data?.signedUrl) window.open(data.signedUrl, '_blank')
  }
  // Latest submitted report version + latest client acknowledgement + manpower control (office decision).
  async function loadReportAck() {
    const [{ data: snaps }, { data: acks }, { data: ar }, { data: allSnaps }, { data: returns }, { data: fsFlag }, { data: fsRend }] = await Promise.all([
      supabase.from('audit_report_snapshots').select('version, submitted_at, full:snapshot->>checklist_full, participants:snapshot->participants, package_status, package_path, package_started_at, package_generated_at, package_error').eq('job_id', id).order('version', { ascending: false }).limit(1),
      supabase.from('report_ack_responses').select('*').eq('job_id', id).order('submitted_at', { ascending: false }).limit(1),
      supabase.from('audit_reports').select('id, manpower_control, manpower_decision, manpower_decision_by, manpower_decision_at, manpower_decision_reason, verified_manpower').eq('job_id', id).maybeSingle(),
      supabase.from('audit_report_snapshots').select('version, submitted_by, submitted_at, package_path').eq('job_id', id).order('version', { ascending: true }),
      supabase.from('audit_trail').select('performer_name, created_at, details, action').eq('entity_id', id).in('action', ['Audit package returned to auditor', 'Package returned by WSHC']).order('created_at', { ascending: true }),
      supabase.from('firm_settings').select('official_package_enabled').eq('id', 1).maybeSingle(),
      // Separate query so a missing renderer column (SQL not yet run) defaults to 'chromium' WITHOUT breaking
      // the kill-switch read above.
      supabase.from('firm_settings').select('official_package_renderer').eq('id', 1).maybeSingle(),
    ])
    setReportVersion(snaps?.[0]?.version || 0)
    setPkgSnap(snaps?.[0] || null)
    setPkgFlag(fsFlag?.official_package_enabled !== false)
    setPkgRenderer(fsRend?.official_package_renderer || 'chromium')
    // Separate read: a missing column (before BIZSAFE_PORTAL_S1.sql) just leaves the new process off.
    supabase.from('firm_settings').select('bizsafe_client_portal').eq('id', 1).maybeSingle()
      .then(({ data }) => setPortalOn(data?.bizsafe_client_portal === true))
    // A server render settles (ready/error) → stop showing "preparing".
    { const st = snaps?.[0]?.package_status; if (st === 'ready' || st === 'error') setPkgBusy(false) }
    // Photo consent (legal, from the frozen snapshot participants): Yes ONLY if every opening+closing
    // participant consented; any unchecked (or none) = No. Null until a report is submitted.
    { const parts = snaps?.[0]?.participants
      setPhotoConsent(snaps?.[0]?.version ? ((Array.isArray(parts) && parts.length > 0 && parts.every(p => !!p.photo_consent)) ? 'Yes' : 'No') : null) }
    // Old snapshots (built before the complete-checklist fix) lack the flag — flag them so the office knows
    // to have the auditor re-submit for the full checklist (incl. N/A questions).
    setReportSnapOld(!!snaps?.[0]?.version && snaps?.[0]?.full !== 'true')
    setReportAck(acks?.[0] || null)
    setMpReport(ar || null)
    // Revision history (internal/SAC traceability): each submitted version + the return that triggered the next
    // revision. Reason/who/when come from the audit trail (return_reason on the job only keeps the latest).
    const rets = returns || []
    // Has this job EVER had an official package rendered? If so the retired on-screen draft view must
    // never be reachable for it, whatever else is stale.
    setEverHadPkg((allSnaps || []).some(x => !!x.package_path))
    setRevisions((allSnaps || []).map((s, i) => {
      const next = (allSnaps || [])[i + 1]
      const ret = rets.find(r => new Date(r.created_at) > new Date(s.submitted_at) && (!next || new Date(r.created_at) < new Date(next.submitted_at)))
      return {
        version: s.version, submitted_by: s.submitted_by, submitted_at: s.submitted_at,
        return_reason: ret?.details?.reason || null, returned_by: ret?.performer_name || null,
        return_source: ret ? (ret.action === 'Package returned by WSHC' ? 'WSHC' : 'Office') : null,
        returned_at: ret?.created_at || null,
      }
    }))
  }
  async function decideManpower(decision) {
    if (decision === 'Approved' && !mpReason.trim()) { toast('Enter the reason or new arrangement', 'warning'); return }
    setMpBusy(true)
    const updates = { manpower_decision: decision, manpower_decision_by: profile?.name || role,
      manpower_decision_at: new Date().toISOString(), manpower_decision_reason: mpReason.trim() || null }
    const { error } = await supabase.from('audit_reports').update(updates).eq('job_id', id)
    setMpBusy(false)
    if (error) { failed("Couldn't record the decision", error.message); return }
    setMpReport(r => ({ ...r, ...updates })); setMpReason('')
    await logAction(`Manpower arrangement ${decision.toLowerCase()}`, 'job', id, client?.name || job.quote_ref, { reason: updates.manpower_decision_reason })
  }
  useEffect(() => { if (id && id !== 'new') { loadReportAck(); loadSessionEvidence() } }, [id])
  // Outstanding items depend on the loaded job (combined audits resolve to the primary); reload on job change.
  useEffect(() => { if (job?.id) loadOutstanding() }, [job?.id]) // eslint-disable-line

  // Resolve the signed-in user to their Personnel record (decision-maker eligibility) + any pending
  // mandatory competency event that would block them from approving.
  useEffect(() => {
    (async () => {
      const email = (user?.email || profile?.email || '').trim()
      if (!profile?.id && !profile?.name && !email) { setMyPerson(null); return }
      // Link the signed-in account to its Personnel record, most reliable key first:
      // user_id → email (case-insensitive, the dependable bridge) → name (last resort; can differ in order).
      let { data: me } = await supabase.from('auditors').select('*').eq('user_id', profile.id).maybeSingle()
      if (!me && email) { const r = await supabase.from('auditors').select('*').ilike('email', email).maybeSingle(); me = r.data }
      if (!me && profile?.name) { const r = await supabase.from('auditors').select('*').eq('name', profile.name).maybeSingle(); me = r.data }
      setMyPerson(me || null)
      if (me) {
        const { data: rows } = await supabase.from('competency_event_people')
          .select('completed, competency_events(mandatory)').eq('auditor_id', me.id).eq('completed', false)
        setMyPendingMandatory((rows || []).some(r => r.competency_events?.mandatory))
      } else setMyPendingMandatory(false)
    })()
  }, [profile?.id, profile?.name, profile?.email, user?.email])
  // Overall audit review & approval (SAC). Reviewer & approver may be the same person, but never
  // the auditor who conducted the audit. Must be recorded before the certification decision.
  const [reviewForm, setReviewForm] = useState({ reviewedBy: '', approvedBy: '' })
  const [savingReview, setSavingReview] = useState(false)

  useEffect(() => { if (id !== 'new') loadJob() }, [id])

  // Pre-fill the report-ack recipient with the client's contact email (the address tied to the quotation),
  // unless already sent to a specific address. The office can still edit it before approving.
  useEffect(() => {
    if (ackEmail) return
    const def = job?.report_ack_sent_to || client?.contact_email || job?.form_data?.pic_email || ''
    if (def) setAckEmail(def)
  }, [client?.contact_email, job?.report_ack_sent_to, job?.form_data?.pic_email])

  // Prefill per-auditor on-site days when the job loads: stored value if present, else an EQUAL SHARE of the
  // total audit man-days across the assigned auditors — so two auditors on a 1-man-day audit prefill 0.5 each
  // (matching the audit plan), never 1 each (which would double-count). Office reviews/adjusts before finalising.
  useEffect(() => {
    if (!job) return
    const n = 1 + (job.auditor2_id ? 1 : 0)
    const total = job.mandays != null ? parseFloat(job.mandays) : null
    const dflt = (total != null && !isNaN(total)) ? String(Math.round((total / n) * 100) / 100) : ''
    setA1Days(job.auditor_mandays != null ? String(job.auditor_mandays) : dflt)
    setA2Days(job.auditor2_mandays != null ? String(job.auditor2_mandays) : dflt)
  }, [job?.id])

  // Live man-day sync: man-days follows the live CT17 unless the preparer manually overrode it.
  // "Overridden" is seeded deterministically in loadJob (stored man-days deviates from the CT17
  // baseline) and set true on field edit; "Apply CT 17" resets it to false.
  const mandaysTouchedRef = useRef(false)

  // Recompute the CT17 planning basis whenever the current planning inputs change (employee count
  // or locations). This re-derives total man-days and the Lead-Auditor requirement, which in turn
  // re-filters the auditor dropdown. Original questionnaire values in form_data are untouched.
  useEffect(() => {
    if (!job || loading) return
    const fd = job.form_data || {}
    // Empty planning field falls back to the questionnaire count, so CT17 is never left stale —
    // this prevents the Lead-Auditor requirement from getting "stuck" on an old value.
    const emp = (planningEmployees === '' || planningEmployees == null) ? fd.num_employees : planningEmployees
    let result = null
    if (job.audit_type === AUDIT_TYPES.BIZSAFE && planningIndustry != null) {
      result = calcBizSAFEMandays(planningIndustry, emp, numLocations || fd.num_locations || 1)
      setCalc(result)
    } else if (job.audit_type === AUDIT_TYPES.SHMS) {
      setCalc(calcSHMSMandays(job.scope, emp, fd.audit_sequence))
    }
    // #7 — keep the editable man-days (and therefore the template selector + deviation below) in step
    // with the live CT17 the moment planning inputs change: at planning stage, follow the CT17 figure
    // unless the preparer manually set man-days. Skip the first calc so a stored value isn't reset on load.
    if (result && result.totalMandays != null) {
      // Follow live CT17 unless the preparer overrode man-days (mandaysTouchedRef). Only at planning
      // stage (don't disturb a sent/finalised plan). This moves the CT17 summary, the editable field,
      // template selection/warning, deviation/justification and schedule-structure controls together.
      const planningStage = !['Scheduled', 'In progress', 'Completed', 'Cancelled'].includes(job.status)
      if (planningStage && !mandaysTouchedRef.current && parseFloat(job.mandays) !== result.totalMandays) {
        updateField('mandays', result.totalMandays)
      }
    }
  }, [planningEmployees, numLocations, planningIndustry, job?.audit_type, job?.scope, loading])

  // Auto-reload public-submitted state (COI responses + client acknowledgement) when staff returns to
  // this tab, so COI and ACK both reflect real status immediately — no manual refresh.
  useEffect(() => {
    function handleVisibility() {
      if (document.visibilityState === 'visible' && id !== 'new') { reloadCOI(); reloadAckState(); loadReportAck(); loadOutstanding() }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [id])

  // Unsaved audit-plan changes: warn before refresh/close (beforeunload) and before an in-app
  // route change (capture link clicks, since BrowserRouter has no useBlocker). Only active while dirty.
  useEffect(() => {
    if (!planFieldsDirty) return
    const LEAVE_MSG = 'You have unsaved changes. Are you sure you want to leave without saving?'
    const onBeforeUnload = e => { e.preventDefault(); e.returnValue = ''; return '' }
    const onClickCapture = e => {
      const a = e.target.closest && e.target.closest('a[href]')
      if (!a) return
      const href = a.getAttribute('href')
      if (!href || a.target === '_blank' || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('#')) return
      if (href === window.location.pathname) return   // same page
      if (!window.confirm(LEAVE_MSG)) { e.preventDefault(); e.stopPropagation() }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    document.addEventListener('click', onClickCapture, true)
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      document.removeEventListener('click', onClickCapture, true)
    }
  }, [planFieldsDirty])

  useEffect(() => {
    if (!job) return
    rebuildPlan()
  }, [job?.mandays, job?.auditor_id, job?.auditor2_id, job?.auditor_name, job?.auditor2_name, startTime, numLocations, auditStructure, halfDayPosition, secondAuditorBlock, day2Start, calc?.requiresLeadAuditor, allAuditors])


  // 2 auditors + 1.0 Split: Auditor 2's afternoon start is held in day2Start. Default it to a PM time the
  // first time this combination applies, so the "Afternoon" control and the generated plan use the same
  // value (the engine anchors Auditor 2's block at day2Start). Only nudges a stale AM default; never an
  // already-PM choice. 1.5 / 2.0 cases are untouched.
  useEffect(() => {
    if (!job) return
    const numAud = (job.auditor_id ? 1 : 0) + (job.auditor2_id && job.auditor2_id !== job.auditor_id ? 1 : 0)
    const md = parseFloat(job.mandays) || 0
    const a1Lead = allAuditors.find(a => a.id === job.auditor_id)?.grade === 'Lead Auditor'
    const a2Lead = allAuditors.find(a => a.id === job.auditor2_id)?.grade === 'Lead Auditor'
    const isSplit = auditStructure === 'Split' && (!calc?.requiresLeadAuditor || (a1Lead && a2Lead))
    if (numAud >= 2 && md > 0 && md <= 1 && isSplit && parseInt(day2Start) < 1200) setDay2Start('1400')
  }, [job?.auditor_id, job?.auditor2_id, job?.mandays, auditStructure, day2Start, calc?.requiresLeadAuditor, allAuditors])

  // Keep the selector honest: if "Split" is not permitted (a Lead is required but both auditors aren't
  // Leads), switch the actual selection to Parallel so the UI never shows Split while the system uses
  // Parallel. Not marked dirty, so it never blocks sending.
  useEffect(() => {
    if (!job || auditStructure !== 'Split') return
    const a1Lead = allAuditors.find(a => a.id === job.auditor_id)?.grade === 'Lead Auditor'
    const a2Lead = allAuditors.find(a => a.id === job.auditor2_id)?.grade === 'Lead Auditor'
    const splitAllowed = !calc?.requiresLeadAuditor || (a1Lead && a2Lead)
    if (!splitAllowed) setAuditStructure('Parallel')
  }, [auditStructure, calc?.requiresLeadAuditor, allAuditors, job?.auditor_id, job?.auditor2_id])

  // After every load (first open, Cancel, reload) the loaded inputs become the baseline. Declared after the
  // schedule effect, so it runs after that effect has built the schedule for this render.
  useEffect(() => {
    if (planBaselineKey) planBaseline.current = { inputs: planInputs(), names: currentAuditorNames() }
  }, [planBaselineKey])

  // An edited schedule saved before its auditors were assigned still shows stand-ins ("Auditor 1",
  // "Auditor 1 / Auditor 2"). Put the real names in on screen as soon as it is opened for editing.
  useEffect(() => {
    if (mode !== 'edit' || !manuallyEdited || !editedRows || !allAuditors.length) return
    const filled = remapAuditorNames(editedRows, { to: currentAuditorNames() })
    if (filled !== editedRows) { setEditedRows(filled); setPlanFieldsDirty(true) }
  }, [mode, manuallyEdited, editedRows, allAuditors, job?.auditor_id, job?.auditor2_id])

  // "Split" structures (split-by-days, or 1.0 same-day AM/PM split) run each session/day by a SINGLE
  // auditor ALONE — not alongside the other. So when a Lead Auditor is required (>200 employees), every
  // standalone session needs a Lead present, which means BOTH auditors must be Lead (Auditor 1 is already
  // forced to a Lead; Auditor 2 must be too). Parallel is unaffected — both auditors are on site together,
  // so Auditor 1's Lead presence covers the whole audit.
  function structureStatus() {
    const a1Lead = allAuditors.find(a => a.id === job?.auditor_id)?.grade === 'Lead Auditor'
    const a2Lead = allAuditors.find(a => a.id === job?.auditor2_id)?.grade === 'Lead Auditor'
    const bothLead = a1Lead && a2Lead
    const leadRequired = !!calc?.requiresLeadAuditor
    const splitAllowed = !leadRequired || bothLead
    const effective = (auditStructure === 'Split' && splitAllowed) ? 'Split' : 'Parallel'
    return { bothLead, leadRequired, splitAllowed, effective }
  }

  // Current auditor names, taken from the SELECTED auditors (by id) — the separately stored name can be blank or
  // out of date. Auditor 2 counts only when distinct and a real auditor (same rule as the schedule below).
  function currentAuditorNames() {
    if (!job) return [null, null]
    const nameOf = auditorId => allAuditors.find(a => a.id === auditorId)?.name || null
    const a2Real = !!(job.auditor2_id && job.auditor2_id !== job.auditor_id && allAuditors.some(a => a.id === job.auditor2_id))
    return [
      job.auditor_id ? (nameOf(job.auditor_id) || job.auditor_name || null) : null,
      a2Real ? (nameOf(job.auditor2_id) || job.auditor2_name || null) : null,
    ]
  }

  // The inputs the automatic schedule is built from, compared with planBaseline to see what really changed.
  function planInputs() {
    return {
      auditors: [job?.auditor_id || '', (job?.auditor2_id && job.auditor2_id !== job.auditor_id) ? job.auditor2_id : ''].join('|'),
      mandays: String(parseFloat(job?.mandays) || ''),
      startTime: [startTime, day2Start].join('|'),
      locations: String(numLocations),
      structure: [auditStructure, halfDayPosition, secondAuditorBlock].join('|'),
    }
  }
  const PLAN_CHANGE_LABEL = { auditors: 'auditors', mandays: 'man-days', startTime: 'start time', locations: 'number of locations', structure: 'audit structure' }

  function rebuildPlan() {
    if (!job) return
    // Auditor 2 counts only when distinct AND resolving to a real auditor (matches the select + warning).
    const a2Real = !!(job.auditor2_id && job.auditor2_id !== job.auditor_id && allAuditors.some(a => a.id === job.auditor2_id))
    const numAud = ((job.auditor_id ? 1 : 0) + (a2Real ? 1 : 0)) || 1
    const md = parseFloat(job.mandays) || 0.5
    // Explicit day span: 2 auditors → from the structure choice (Parallel=1, Split=2); 1 auditor → 2 when >1 md.
    const daySpan = numAud >= 2 ? (structureStatus().effective === 'Split' ? 2 : 1) : (md > 1 ? 2 : 1)
    // 2 auditors + 1.5 parallel: Auditor 2's half is morning vs afternoon, derived from its start time
    // (day2Start) — no separate AM/PM control. AM (<12:00) → onehalf_two, PM (>=12:00) → onehalf_two_pm.
    const secondBlock = (parseInt(day2Start) || 900) < 1200 ? 'Morning' : 'Afternoon'
    const sel = selectTemplate({ auditors: numAud, mandays: md, locations: numLocations, daySpan, halfDayPosition, secondAuditorBlock: secondBlock })
    if (sel.blocked) {
      setBlockedReason(sel.reason); setTwoDaySpan(false); setPlanType('BLOCKED'); setPlanRows([]); setPlanChanges([])
      return
    }
    setBlockedReason(''); setTwoDaySpan(!!sel.twoDay); setPlanType(sel.planType)
    const [a1Name, a2Name] = currentAuditorNames()
    const rows = generateAuditPlanRows(sel.planType, {
      day1Start: parseStartTime(startTime),
      day2Start: parseStartTime(day2Start || startTime),
      a1: a1Name, a2: a2Name, auditors: numAud,
    })
    setPlanRows(rows)

    // Auto-update by default. With REAL manual row edits, keep them and ask — but only about inputs that
    // actually changed since the plan was loaded or saved (never just because the page finished loading).
    if (manuallyEdited && editedRows) {
      const base = planBaseline.current
      const now = planInputs()
      setPlanChanges(base ? Object.keys(now).filter(k => now[k] !== base.inputs[k]) : [])
    } else {
      // No manual edits → regenerate and show the fresh schedule immediately (no banner).
      if (editedRows) setEditedRows(null)
      setPlanChanges([])
    }
  }

  // The current inputs become the new baseline (after the prompt is answered, or after a save).
  function settlePlanBaseline() {
    planBaseline.current = { inputs: planInputs(), names: currentAuditorNames() }
    setPlanChanges([])
  }

  // Move each auditor's rows to the auditor now in that slot; times, activities and typed names stay.
  function updateScheduleNames() {
    const rows = editedRows || planRows
    const next = remapAuditorNames(rows, { from: planBaseline.current?.names || [], to: currentAuditorNames() })
    if (next !== rows) { setEditedRows(next); setPlanFieldsDirty(true) }
  }

  // "Keep my edits" — the edits stay; if the auditors changed too, their names are still updated.
  function keepScheduleEdits() {
    if (planChanges.includes('auditors')) updateScheduleNames()
    settlePlanBaseline()
  }

  function applyStandardSchedule() {
    resetToAuto()
    settlePlanBaseline()
  }

  async function saveDecision() {
    if (!decisionForm.outcome) return
    // Enforce: decision maker cannot be the auditor on this job
    if (profile?.name === job.auditor_name || profile?.name === job.auditor2_name) {
      toast("The auditor can't approve their own audit", 'warning', { detail: 'Choose a different decision-maker' })
      return
    }
    if (!job.reviewed_by || !job.approved_by) {
      toast('Approve the review first', 'warning', { detail: 'Then record the certification decision' })
      return
    }
    setSavingDecision(true)
    const { data: dec, error: decErr } = await supabase.from('audit_decisions').insert({
      job_id: id,
      decision_by_id: profile?.id,
      decision_by_name: profile?.name || role,
      decision_by_role: role,
      decision_outcome: decisionForm.outcome,
      decision_remarks: decisionForm.remarks || null,
    }).select().single()
    if (decErr) { failed("Couldn't record the decision", decErr.message); setSavingDecision(false); return }
    if (dec) {
      await supabase.from('jobs').update({
        decision_id: dec.id,
        status: decisionForm.outcome === 'Issue report' ? 'Completed' : job.status,
      }).eq('id', id)
      setAuditDecision(dec)
      setJob(j => ({ ...j, decision_id: dec.id,
        status: decisionForm.outcome === 'Issue report' ? 'Completed' : j.status }))
      await logAction('Audit decision recorded', 'job', id, client?.name || job.quote_ref, {
        outcome: decisionForm.outcome, decided_by: profile?.name, role,
      })
    }
    setSavingDecision(false)
  }

  // Auditor on this job cannot review or approve their own audit (SAC independence).
  function isAuditorOnJob() {
    return profile?.name && (profile.name === job?.auditor_name || profile.name === job?.auditor2_name)
  }

  // ---------- Office checkpoint (Pending Office Review → Returned / Pending WSHC → Completed) ----------
  const assignedAuditorIds = [job?.auditor_id, job?.auditor2_id].filter(Boolean)
  const dmGate = myPerson
    ? canActAsDecisionMaker(myPerson, { assignedAuditorIds, hasPendingMandatory: myPendingMandatory })
    : { ok: false, gaps: ['Your account is not linked to a Personnel record'] }

  async function returnToAuditor() {
    const reason = (returnForm.reason || '').trim()
    if (!reason) { toast('Enter a return reason', 'warning'); return }
    setOfficeBusy(true)
    const updates = {
      workflow_state: 'Returned for Revision', report_submitted: false,
      report_signatures: {},   // a revised report is a fresh attestation — both auditors re-sign
      returned_by: profile?.name || role, return_source: 'Office', return_reason: reason, returned_at: new Date().toISOString(),
      // Void the prior approval — the re-submitted report must be reviewed & approved afresh.
      office_review_at: null, office_review_by: null, office_review_notes: null,
      // Reset the client-send state: the old acknowledgement link belonged to the PREVIOUS version. Clearing it
      // so the revised + re-approved report shows "Ready to send" (a fresh link) instead of "Awaiting" — else the
      // office never sees that the new version still needs sending. (Ack HISTORY stays in the audit trail.)
      report_ack_sent_at: null, report_ack_sent_to: null, report_ack_version: null,
      // New WSHC-portal process: the revised report gets a fresh "please apply" email and reminders.
      ...(isPortalJob(job) ? { wshc_apply_email_sent_at: null, wshc_apply_sent_to: null, client_wshc_submitted_at: null } : {}),
    }
    // Group-scoped return: a combined audit is ONE shared execution, so returning any member returns the
    // whole group (all UENs → Returned for Revision, all prior reviews voided). The auditor corrects the
    // shared execution on the primary and resubmits, which fans out fresh snapshots to every member.
    let targetIds = [id]
    if (job?.combined_group_id) {
      const { data: gj } = await supabase.from('jobs').select('id').eq('combined_group_id', job.combined_group_id)
      targetIds = Array.from(new Set([...(gj || []).map(g => g.id), id]))
    }
    const { error } = await supabase.from('jobs').update(updates).in('id', targetIds)
    setOfficeBusy(false)
    if (error) { failed("Couldn't return it", error.message); return }
    setJob(j => ({ ...j, ...updates })); setReturnForm({ open: false, reason: '' })
    await logAction('Audit package returned to auditor', 'job', id, client?.name || job.quote_ref, { reason, combined_group: job?.combined_group_id || null })
    notifyAuditorsOfReturn(reason)
  }

  // Client report acknowledgement is valid only for the CURRENT report version (re-submission invalidates it).
  const reportAckValid = !!(reportAck && reportVersion > 0 && reportAck.report_version === reportVersion)
  // New WSHC-portal process: the plain-English step for this job (null = earlier process / usual wording).
  const portalText = portalStepText(job)

  // ── WSHC 2-MONTH SUBMISSION WINDOW ─────────────────────────────────────────────────────────────────────
  // WSHC rejects a renewal submission made earlier than 2 months before the client's CURRENT bizSAFE expiry.
  // We block "record WSHC submission" until that window opens. Source of truth is clients.bizsafe_expiry_date
  // (already the single source of truth; loaded with the job). Rules that keep this SAFE and additive:
  //   • New applications have no active expiry (null) → never gated.
  //   • We only block when we POSITIVELY know the window hasn't opened → missing/bad data fails OPEN.
  //   • Date-only string compare (yyyy-MM-dd) → timezone-proof.
  const wshcWindowOpen = client?.bizsafe_expiry_date ? subMonths(parseISO(client.bizsafe_expiry_date), 2) : null
  const wshcWindowOpenStr = wshcWindowOpen ? format(wshcWindowOpen, 'yyyy-MM-dd') : null
  const wshcTooEarly = !!(wshcWindowOpenStr && format(new Date(), 'yyyy-MM-dd') < wshcWindowOpenStr)
  const wshcOpenLabel = wshcWindowOpen ? format(wshcWindowOpen, 'd MMM yyyy') : null

  // ── Report readiness in PLAIN language (no "package" vocabulary anywhere in the UI). The stored report PDF
  // is an internal implementation detail; the office just sees a report that is preparing / ready / failed.
  // A "historical / legacy" job (already acknowledged, already sent to WSHC, or a link already issued for this
  // version) is left exactly as-is — never force-prepared — and shows as plain (just View report).
  // The WSHC submission stamp belongs to the version that was submitted, NOT to the job forever. A later
  // revision (office return → auditor resubmits → new version) is a NEW, unsubmitted report: if we kept
  // treating the job as "already with WSHC", the new version's package would never be rendered and
  // "View report" would fall back to the retired on-screen draft. So the stamp only counts while it is at
  // least as recent as the newest submitted version.
  const latestSubmittedAt = pkgSnap?.submitted_at ? new Date(pkgSnap.submitted_at).getTime() : null
  // New WSHC-portal process: the PDF is frozen once the client has been emailed the steps to apply (they upload it).
  const frozenAt = job?.submitted_to_wshc_at || job?.wshc_apply_email_sent_at
  const wshcStampCurrent = !!(frozenAt && latestSubmittedAt && new Date(frozenAt).getTime() >= latestSubmittedAt)
  const historicalNoPkg = !!(
    reportAckValid ||
    wshcStampCurrent ||
    (job?.report_ack_sent_at && job?.report_ack_version === reportVersion)
  )
  // A version is FROZEN once the office has approved it (office_review_at recorded for THIS version): the stored
  // package must not be regenerated — a problem is fixed by RETURN → new version, never by re-render.
  const approvedForVersion = !!(job?.office_review_at && job?.report_ack_version === reportVersion)

  // ── STAMP-FRESHNESS GUARD ──────────────────────────────────────────────────────────────────────────────
  // The report body carries stamps that only appear as the workflow progresses:
  //   • Reviewer's name                        → filled at OFFICE APPROVAL (office_review_at)
  //   • Management Representative + Signature  → filled at CLIENT ACKNOWLEDGEMENT (ack.submitted_at)
  // The package is rendered at SUBMISSION, i.e. before either exists — so after each of those events the stored
  // copy is missing that stamp and MUST be re-rendered. Until it is, the package is never opened, sent to the
  // client, or submitted to WSHC. That is what stops an unstamped/unsigned copy ever leaving the office.
  // (Content can't drift: every render reads the same immutable snapshot — only these stamps fill in.)
  const stampTimes = [
    job?.office_review_at,
    reportAckValid ? reportAck?.submitted_at : null,
  ].filter(Boolean).map(d => new Date(d).getTime()).filter(t => !Number.isNaN(t))
  const latestStampAt = stampTimes.length ? Math.max(...stampTimes) : null
  const pkgStale = !!(latestStampAt && pkgSnap?.package_generated_at &&
    new Date(pkgSnap.package_generated_at).getTime() < latestStampAt)

  // reportPrep.state: 'ready' | 'preparing' | 'failed' | 'plain'
  const reportPrep = (() => {
    if (!pkgFlag || reportVersion < 1) return { state: 'plain' }
    const st = pkgSnap?.package_status
    // Client just signed → the stored copy is the unsigned one; it is re-rendering.
    if (pkgStale) return { state: 'preparing' }
    if (st === 'ready') return { state: 'ready' }
    if (st === 'error') return { state: 'failed' }
    if (pkgBusy || genKey > 0 || st === 'preparing') return { state: 'preparing' }
    // st 'none' (or no snapshot row yet): a NEW pre-issue report self-heals into preparation; legacy = plain.
    return { state: historicalNoPkg ? 'plain' : 'preparing' }
  })()

  // Ask the SERVER (Netlify background function) to render + store the official package for the current version.
  // Auth = the office user's Supabase session; the function drives package_status. We poll below until it settles.
  async function triggerServerRender() {
    try {
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token
      if (!token) return
      setPkgBusy(true)
      await fetch(`${window.location.origin}/.netlify/functions/render-report-package-background`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
        body: JSON.stringify({ jobId: id, siteOverride: window.location.origin }),
      })
    } catch (e) { /* the poll reflects status; office can Retry */ }
  }
  // One entry point to (re)start preparation, honouring the renderer toggle: server Chromium (default) or the
  // old client-side html2canvas fallback.
  function startRender() {
    if (pkgRenderer === 'raster') setGenKey(k => k + 1)
    else triggerServerRender()
  }

  // Self-heal: the SYSTEM owns report currency. A new, pre-issue report with no prepared copy (or a stale
  // 'preparing') starts preparing automatically — no Generate button. Never for legacy/historical or APPROVED
  // (frozen) versions, never on 'error' (office-only Retry), never while already busy.
  useEffect(() => {
    if (!pkgFlag || reportVersion < 1 || genKey > 0 || pkgBusy) return
    if (wshcStampCurrent) return                             // hard stop: WSHC has THIS version — never re-render
    // The client signed after this package was rendered → re-render ONCE so the stored copy is the SIGNED one.
    if (pkgStale) { startRender(); return }
    // A historical / already-approved version is normally left frozen. The exception: this job HAS had
    // official packages before but the CURRENT version has none — leaving it would make that version
    // unviewable (the retired draft view is no longer offered). Render it once. The snapshot is immutable,
    // so this only materialises the document that already exists as data; it changes no content.
    const needsFirstRender = everHadPkg && (!pkgSnap || pkgSnap.package_status === 'none')
    if ((historicalNoPkg || approvedForVersion) && !needsFirstRender) return
    const st = pkgSnap?.package_status
    if (st === 'ready' || st === 'error') return
    if (!pkgSnap || st === 'none' || (st === 'preparing' && isPreparingStale(pkgSnap))) startRender()
  }, [pkgFlag, reportVersion, genKey, pkgBusy, pkgSnap, historicalNoPkg, approvedForVersion, pkgRenderer, pkgStale, wshcStampCurrent, everHadPkg])

  // While a server render is in flight, poll status so preparing → ready/failed updates without a manual refresh.
  // Capped (~4 min) so a stuck render never polls forever — the office can still hit "Refresh status".
  useEffect(() => {
    if (reportPrep.state !== 'preparing') return
    let n = 0
    const iv = setInterval(() => { if (++n > 30) { clearInterval(iv); return } loadReportAck() }, 8000)
    return () => clearInterval(iv)
  }, [reportPrep.state])

  // "View report" — when the stored PDF is ready, open it in a FULL BROWSER TAB (native viewer: full width,
  // zoom, thumbnails, search — the comfortable surface for approval review), NOT the cramped modal. Legacy /
  // not-yet-prepared jobs (no stored PDF) fall back to the modal preview. Popup-blocker-safe: open the tab
  // synchronously, then point it at the signed URL once it resolves.
  async function openReport() {
    // Never hand out a copy that is missing the latest stamp (reviewer / client signature) — it is re-rendering.
    if (pkgStale) {
      toast('The report is being updated', 'info', { detail: 'Try again in a minute' })
      return
    }
    if (pkgFlag && pkgSnap?.package_status === 'ready' && pkgSnap?.package_path) {
      const w = window.open('', '_blank')
      const { data } = await supabase.storage.from('report-packages').createSignedUrl(pkgSnap.package_path, 1800)
      if (w && data?.signedUrl) { w.location = data.signedUrl; return }
      if (w) w.close()
    }
    // Package-era reports: NEVER fall back to the classic inline preview — the office must review ONLY the
    // official WSHC-format package (the exact document the client signs and WSHC receives), so a reviewer can
    // never form a judgment on a different-looking draft. Pre-package legacy jobs keep the modal (their only render).
    if (pkgFlag && (!historicalNoPkg || everHadPkg)) {
      toast('The report is still being prepared', 'info', { detail: 'Try again in a minute or two' })
      return
    }
    setShowPackage(true)   // legacy (pre-package) jobs only
  }

  // Office handler's DISPATCH step. The invoice-clearance gate lives here (not at approval): if clearance was
  // never recorded, open the modal first; on confirm, record it, generate/reuse the token, copy the client
  // link and draft the client email. The client link is exposed ONLY after clearance is confirmed/released.
  async function sendReportToClient(inv) {
    const typed = (ackEmail || '').trim()
    // Write-once: once the link has been issued for the CURRENT report version, clicking "Copy link to send"
    // again is a READ — re-copy the same link and re-draft the email, but DO NOT move the "prepared for /
    // sent at" record, the recipient, or write another audit-log entry. (A new report version re-issues.)
    const alreadyIssued = !!job.report_ack_sent_at && !!job.report_ack_token && job.report_ack_version === reportVersion
    // New WSHC-portal process: decided ONCE, on the FIRST send of a report (a re-copy of an issued link never
    // switches an in-flight job; jobs already in flight at go-live are moved by a reviewed one-off SQL).
    const plan = alreadyIssued ? { flow: job.wshc_flow || null, write: false } : await planWshcFlow()
    if (!plan) return
    const flow = plan.flow
    // New-process sign-off email: three versions — new applicant / renewal before WSHC's window / renewal window open.
    const portalVars = (() => {
      const expiry = client?.bizsafe_expiry_date
      const opens = wshcWindowOpensOn(expiry)
      const long = (d) => { try { return format(parseISO(String(d).slice(0, 10)), 'd MMMM yyyy') } catch (e) { return d } }
      // Follows Step 3 on the same line (owner's wording, 2 Nov 2026: "assigned staff").
      if (!opens) return { REPORT_KIND: 'audit report', SIGN_NOW: '', AFTER_SIGN: 'We will email your assigned staff the bizSAFE application steps.' }
      if (!isWshcWindowOpen(expiry)) return { REPORT_KIND: 'audit report', SIGN_NOW: '',
        AFTER_SIGN: `We will email your assigned staff the application steps on ${long(opens)}, when the renewal window opens.` }
      return { REPORT_KIND: 'audit report', SIGN_NOW: '',
        AFTER_SIGN: `We will email your assigned staff the bizSAFE application steps. They must apply before your bizSAFE expires on ${long(expiry)}.` }
    })()
    const clientTemplate = (link) => flow === 'client_portal'
      ? applyTemplate(getTemplate('reportClientPortal'), { CLIENT_NAME: client?.contact_name || client?.name || '', COMPANY: client?.name || '', REPORT_LINK: link, ...portalVars, FIRM_NAME: FIRM_NAME() })
      : applyTemplate(getTemplate('reportClient'), { CLIENT_NAME: client?.contact_name || client?.name || '', REPORT_LINK: link, FIRM_NAME: FIRM_NAME() })
    if (alreadyIssued) {
      // Resend goes to the address in the box (blank box = the original recipient). The original "sent to / at"
      // record stays as it was; a resend to someone else is noted in the audit trail.
      const to = typed || job.report_ack_sent_to || ''
      if (typed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(typed)) { toast("Check the client's email address", 'warning'); return }
      const link = `${window.location.origin}/report-ack/${job.report_ack_token}`
      try { await navigator.clipboard.writeText(link) } catch (e) {}
      if (to && to.toLowerCase() !== String(job.report_ack_sent_to || '').toLowerCase()) {
        await logAction('Report acknowledgement link resent to another recipient', 'job', id, client?.name || job.quote_ref,
          { to, originally_sent_to: job.report_ack_sent_to || null, version: reportVersion })
      }
      toast('Link copied', 'success', { detail: to ? `Paste it into your email to ${to}` : '' })
      const rt = clientTemplate(link)
      if (to) window.location.href = `mailto:${to}?subject=${encodeURIComponent(rt.subject)}&body=${encodeURIComponent(rt.body)}`
      return
    }
    // Checkpoint 6: block ISSUING a NEW acknowledgement link until the official package is 'ready'. The EXEMPT
    // (historical) cases are handled by POSITIVE signals, not by 'none': already-issued resends return above,
    // and already-acknowledged versions are excluded via !reportAckValid. So for a genuinely NEW / pre-ack job,
    // any non-'ready' status — INCLUDING 'none' (not generated yet) — blocks and prompts generation.
    if (pkgFlag && !reportAckValid && pkgSnap && pkgSnap.package_status !== 'ready') {
      toast('The report is still being prepared', 'info', { detail: 'Try again in a minute' })
      return
    }
    // The client must receive the REVIEWER-STAMPED report: the stored copy predates the approval, so it is being
    // re-rendered. Block the send until it carries the stamp.
    if (pkgFlag && pkgStale) {
      toast('The report is being updated', 'info', { detail: 'Try again in a minute, then send the link' })
      return
    }
    const email = typed
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast("Enter the client's email", 'warning'); return }
    if (reportVersion < 1) { toast('No report submitted yet', 'info'); return }
    if (!inv && !job.invoice_clearance_checked_at) { setInvCleared(false); setInvReason(''); setInvoiceAction('send'); setInvoiceModal(true); return }
    // HARD guard (not just the disabled button): never record a release without a reason when uncleared.
    if (inv && !inv.cleared && !(inv.reason || '').trim()) { toast('Invoices not cleared — give a reason', 'warning', { detail: "It's recorded with the release" }); return }
    setAckSending(true)
    const now = new Date().toISOString()
    // New links use a proper random token (crypto) — the link opens the client's report.
    const token = job.report_ack_token || (window.crypto?.randomUUID ? window.crypto.randomUUID().replace(/-/g, '') : (Math.random().toString(36).slice(2) + Date.now().toString(36)))
    const updates = {
      report_ack_token: token, report_ack_sent_to: email, report_ack_sent_at: now, report_ack_version: reportVersion,
      ...(inv ? invoiceUpdates(inv, now) : {}),
      ...(plan.write ? { wshc_flow: flow } : {}),
    }
    const { error } = await supabase.from('jobs').update(updates).eq('id', id)
    setAckSending(false)
    if (error) { failed("Couldn't create the link", error.message); return }
    if (plan.write) await logAction('bizSAFE job on the new WSHC-portal process (client applies on WSHC)', 'job', id, client?.name || job.quote_ref, {})
    setJob(j => ({ ...j, ...updates })); setInvoiceModal(false)
    await logAction('Report acknowledgement link generated for client', 'job', id, client?.name || job.quote_ref, { email, version: reportVersion, invoice_cleared: inv ? !!inv.cleared : null })
    try { await navigator.clipboard.writeText(`${window.location.origin}/report-ack/${token}`) } catch (e) {}
    toast('Link copied', 'success', { detail: flow === 'client_portal'
      ? `Send it to ${email}. After they sign, AuditPM emails them the steps to apply on WSHC.`
      : `Send it to ${email}. WSHC submission unlocks once they acknowledge.` })
    // Draft the client endorsement email in Outlook (client-facing → sent manually by the handler).
    const rTmpl = clientTemplate(`${window.location.origin}/report-ack/${token}`)
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(rTmpl.subject)}&body=${encodeURIComponent(rTmpl.body)}`
  }

  // New WSHC-portal process — which process does this job follow? Decided ONCE, when its report is sent while
  // the switch is on: bizSAFE only, not already submitted by CCIS, current version not yet signed. A combined
  // group follows ONE process: a member already on a process (or already sent the earlier way) sets it.
  // Returns { flow: 'client_portal' | null, write: true when the flag must be saved now } — or null on error.
  async function planWshcFlow() {
    if (job.wshc_flow) return { flow: job.wshc_flow, write: false }
    if (job.audit_type !== AUDIT_TYPES.BIZSAFE || job.submitted_to_wshc_at || reportAckValid) return { flow: null, write: false }
    // A combined audit follows its group, whatever the switch says now.
    if (job.combined_group_id) {
      const { data: g, error } = await supabase.from('jobs').select('id, wshc_flow, report_ack_sent_at').eq('combined_group_id', job.combined_group_id)
      if (error) { failed("Couldn't check the combined audit", error.message); return null }
      const others = (g || []).filter(m => m.id !== id)
      const set = others.find(m => m.wshc_flow)
      if (set) return { flow: set.wshc_flow, write: true }
      if (others.some(m => m.report_ack_sent_at)) return { flow: null, write: false }   // group already sent the earlier way
    }
    // The switch is read fresh here (not the copy loaded with the page).
    const { data: fs } = await supabase.from('firm_settings').select('bizsafe_client_portal').eq('id', 1).maybeSingle()
    return fs?.bizsafe_client_portal === true ? { flow: 'client_portal', write: true } : { flow: null, write: false }
  }

  // Step ① — Decision-Maker approves the report AND issues it to the client (one action). Stays in
  // Pending Office Review; the client then acknowledges, which unlocks Submit to WSHC (step ③).
  // Invoice-clearance fields to write — captured ONCE (the first external release that records it). If a
  // clearance was already recorded earlier, return {} so the existing record is never overwritten.
  function invoiceUpdates(inv, now) {
    if (job.invoice_clearance_checked_at) return {}
    return {
      invoice_cleared_confirmed: !!inv.cleared,
      invoice_clearance_reason: inv.cleared ? null : (inv.reason || null),
      invoice_clearance_checked_by: user?.id || null,
      invoice_clearance_checked_by_name: profile?.name || role,
      invoice_clearance_checked_at: now,
    }
  }

  // Best-effort internal email to the routed handler. The edge function resolves the recipient's email from
  // their user id (service role — user_profiles has no email). Wrapped so a missing/failing function NEVER
  // blocks the approval: routing is already saved by the time this runs.
  async function notifyHandler(handler) {
    if (!handler || handler.id === profile?.id) return   // no email to self
    try {
      await supabase.functions.invoke('send-internal-email', {
        body: {
          user_id: handler.id,
          subject: `Audit report ready to send to client – ${client?.name || job.quote_ref || 'client'}`,
          html: emailHtml({
            title: 'Audit report approved — ready to send',
            greeting: `Dear ${handler.name},`,
            lines: [`The audit report for ${client?.name || job.quote_ref || 'the client'} has been reviewed and approved and is ready to be sent to the client.`,
              'Next action: check invoice / payment clearance, then copy the client acknowledgement link and send it to the client.'],
            details: [['Company', client?.name || job.quote_ref || '—'], ['Approved by', profile?.name || '—']],
            button: { label: 'Open job →', url: `${window.location.origin}/jobs/${id}` },
          }),
        },
      })
    } catch (e) { /* best-effort — non-blocking */ }
  }

  // Best-effort: tell the assigned auditor(s) their report was returned and needs revision (office OR WSHC
  // return). Never blocks the return — that's already saved & logged by the time this runs.
  async function notifyAuditorsOfReturn(reason) {
    const auditLink = `${window.location.origin}/jobs/${id}/execute`
    const recips = [{ id: job.auditor_id, name: job.auditor_name }, { id: job.auditor2_id, name: job.auditor2_name }].filter(a => a.id)
    for (const a of recips) {
      const email = allAuditors.find(x => x.id === a.id)?.email
      if (!email) continue
      const tmpl = applyTemplate(getTemplate('reportReturned'), {
        AUDITOR_NAME: a.name || '', CLIENT_NAME: client?.name || job.quote_ref || '',
        RETURN_REASON: reason || '', AUDIT_LINK: auditLink, FIRM_NAME: FIRM_NAME(),
      })
      try {
        await supabase.functions.invoke('send-internal-email', { body: { to: email, subject: tmpl.subject,
          html: textEmailHtml({ title: 'Audit report returned for revision', text: tmpl.body, buttonLabel: 'Open the audit →' }) } })
      } catch (e) { /* best-effort — non-blocking */ }
    }
  }

  // Step ① — Decision-Maker APPROVAL ONLY. Records the certification decision and routes the next step
  // (client dispatch) to the selected office handler. NO invoice check, NO client link, NO email draft here —
  // those belong to the office handler's send step (sendReportToClient).
  async function approveAndSendToClient() {
    if (isAuditorOnJob()) { toast('You conducted this audit', 'warning', { detail: 'Another Decision-Maker must approve it' }); return }
    if (!dmGate.ok) { toast("You can't approve this yet", 'warning', { detail: '• ' + dmGate.gaps.join('\n• ') }); return }
    if (!REVIEW_ITEMS.every(it => reviewChecks[it] !== false)) { toast('Tick all four review items', 'warning'); return }
    if (reportVersion < 1) { toast('No report submitted yet', 'info'); return }
    // New WSHC-portal process: a renewal needs the client's CURRENT expiry on file — it sets the date the client
    // may apply on WSHC. Caught here, not months later when the apply email is due.
    if (portalOn && job.audit_type === AUDIT_TYPES.BIZSAFE && !client?.bizsafe_expiry_date
        && ['Renewal', 'Progression'].includes(job.application_type || job.form_data?.application_type)) {
      toast('Add the current bizSAFE expiry on the client first', 'warning', { detail: 'It sets the date the client can apply on WSHC' })
      return
    }
    // A3: cannot approve until the official package for THIS version is READY — office approves the exact package
    // the client will acknowledge and WSHC will receive. Legacy / kill-switch jobs (no stored package) are exempt.
    if (pkgFlag && !historicalNoPkg && (!pkgSnap || pkgSnap.package_status !== 'ready')) {
      toast('The report is still being prepared', 'info', { detail: 'If it failed, click Retry on the report tab' })
      return
    }
    setOfficeBusy(true)
    const now = new Date().toISOString()
    const reviewNote = 'Reviewed and approved: ' + (REVIEW_ITEMS.filter(it => reviewChecks[it] !== false).join(', ') || '—')
    // Route the downstream office work to the chosen handler (soft — they/you/Admin can still act). Blank = keep with the approver.
    const handler = handlers.find(h => h.id === routeTo) || null
    // 1) Record the approval FIRST — office_review_at must land BEFORE the decision insert (the guard checks it).
    const updates = {
      office_review_by: profile?.name || role, office_review_at: now, office_review_notes: reviewNote,
      report_ack_version: reportVersion,   // version marker ("this version is approved") — NOT the token/link
      ...(handler ? { assigned_to_id: handler.id, assigned_to_name: handler.name, assigned_at: now, assigned_by_name: profile?.name || role } : {}),
    }
    const { error } = await supabase.from('jobs').update(updates).eq('id', id)
    if (error) { setOfficeBusy(false); failed("Couldn't approve", error.message); return }
    // 2) Record the certification decision (Issue report) — REQUIRED; the finalisation guard refuses to
    //    complete a job without one. Now that office_review_at is set, the requires-review guard passes.
    const { data: dec, error: decErr } = await supabase.from('audit_decisions').insert({
      job_id: id, decision_by_id: profile?.id, decision_by_name: profile?.name || role, decision_by_role: role,
      decision_outcome: 'Issue report', decision_remarks: reviewNote,
    }).select().maybeSingle()
    if (decErr || !dec) { setOfficeBusy(false); failed("Approved, but the decision wasn't recorded", decErr, 'Click Approve again'); return }
    // 3) Link the decision to the job.
    await supabase.from('jobs').update({ decision_id: dec.id }).eq('id', id)
    setOfficeBusy(false)
    setAuditDecision(dec)
    setJob(j => ({ ...j, ...updates, decision_id: dec.id })); setReviewChecks({})
    await logAction('Report approved — routed to office handler for dispatch', 'job', id, client?.name || job.quote_ref, { by: profile?.name, version: reportVersion, routed_to: handler?.name || null })
    if (handler) notifyHandler(handler)   // best-effort internal email
    toast('Report approved', 'success', { detail: handler && handler.id !== profile?.id ? `Sent to ${handler.name} for the next steps` : '' })
    // Report preparation is handled by the SELF-HEAL effect (it starts as soon as a submitted report has no
    // prepared copy), so approval needs no explicit trigger. By the time a report is approved it is normally
    // already prepared; if not, the self-heal effect brings it up to date. Approval never blocks on it.
  }

  // Step ③ — record the WSHC online submission. Same invoice-clearance gate: if it was never recorded,
  // require it here too. Captures WHO recorded the submission + when (the handler, usually — not the approver).
  async function submitToWSHC(inv) {
    if (!reportAckValid) { toast("The client hasn't acknowledged yet", 'warning', { detail: 'Wait for them to acknowledge this version' }); return }
    // WSHC 2-month window gate. Blocked until the window opens, unless a manager overrides with a typed
    // reason (captured in the override modal → wshcOverrideRef). The ref persists the reason across the
    // invoice-modal chain so this check passes on the follow-through call rather than looping.
    if (wshcTooEarly && !wshcOverrideRef.current) {
      setWshcOverrideReason(''); setWshcOverrideModal(true); return
    }
    // Checkpoint 7: WSHC submits the STORED official package. For a NEW official-package job, the acknowledged
    // snapshot's package must be 'ready' first — we record/submit that exact package, we never rebuild it here.
    // A legacy job (package_status 'none') was acknowledged via the old raster flow and submits the old way, so
    // it is NOT blocked. A NEW job whose package is preparing/error IS blocked (regenerate on the report tab).
    if (pkgFlag && pkgSnap && pkgSnap.package_status !== 'ready' && pkgSnap.package_status !== 'none') {
      toast('The report is still being prepared', 'info', { detail: 'Try again in a minute' })
      return
    }
    // HARD GUARD: WSHC must receive the fully-stamped, SIGNED report. The stored copy predates the reviewer
    // stamp / client signature — block until the updated copy has finished re-rendering.
    if (pkgStale) {
      toast("The client's signature is being added", 'info', { detail: 'Try again in a minute' })
      return
    }
    if (!inv && !job.invoice_clearance_checked_at) { setInvCleared(false); setInvReason(''); setInvoiceAction('wshc'); setInvoiceModal(true); return }
    if (inv && !inv.cleared && !(inv.reason || '').trim()) { toast('Invoices not cleared — give a reason', 'warning', { detail: "It's recorded with the WSHC submission" }); return }
    setOfficeBusy(true)
    const now = new Date().toISOString()
    // Freeze WHICH package went to WSHC (immutable history). Stored package for a new job; NULL for legacy raster.
    const usingStoredPkg = pkgFlag && pkgSnap && pkgSnap.package_status === 'ready' && pkgSnap.package_path
    const updates = {
      workflow_state: 'Pending WSHC Outcome', submitted_to_wshc_at: now,
      submitted_to_wshc_by_id: user?.id || null, submitted_to_wshc_by_name: profile?.name || role,
      wshc_package_version: usingStoredPkg ? (pkgSnap.version || null) : null,
      wshc_package_path: usingStoredPkg ? pkgSnap.package_path : null,
      // Early-submission override (only present when a manager bypassed the 2-month window lock).
      ...(wshcOverrideRef.current ? {
        wshc_early_override_reason: wshcOverrideRef.current,
        wshc_early_override_by: profile?.name || role,
        wshc_early_override_at: now,
      } : {}),
      ...(inv ? invoiceUpdates(inv, now) : {}),
    }
    const { error } = await supabase.from('jobs').update(updates).eq('id', id)
    setOfficeBusy(false)
    if (error) { failed("Couldn't record the WSHC submission", error.message); return }
    setJob(j => ({ ...j, ...updates })); setInvoiceModal(false)
    const earlyOverride = wshcOverrideRef.current || null
    wshcOverrideRef.current = ''   // clear so it can't leak into a later submission attempt
    await logAction('WSHC submission recorded', 'job', id, client?.name || job.quote_ref, { by: profile?.name, package_version: usingStoredPkg ? (pkgSnap.version || null) : null, package_path: usingStoredPkg ? pkgSnap.package_path : null, early_override_reason: earlyOverride })
    toast('WSHC submission recorded', 'success', { detail: 'An Outlook email is ready — check it and send' })
    // Draft the "application submitted" email to the client in Outlook (client-facing → you send it manually).
    const wTmpl = applyTemplate(getTemplate('wshcSubmitted'), { CLIENT_NAME: client?.contact_name || client?.name || '', FIRM_NAME: FIRM_NAME() })
    const wEmail = client?.contact_email || job.form_data?.pic_email || ''
    window.location.href = `mailto:${wEmail}?subject=${encodeURIComponent(wTmpl.subject)}&body=${encodeURIComponent(wTmpl.body)}`
  }

  async function wshcReturn() {
    const reason = (wshcForm.reason || '').trim()
    if (!reason) { toast('Enter the WSHC return reason', 'warning'); return }
    setOfficeBusy(true)
    const updates = {
      workflow_state: 'Returned for Revision', report_submitted: false, report_signatures: {}, wshc_outcome: 'Returned', wshc_outcome_at: new Date().toISOString(),
      returned_by: 'WSHC', return_source: 'WSHC', return_reason: reason, returned_at: new Date().toISOString(),
      // Void the prior approval — the re-submitted report must be reviewed & approved afresh.
      office_review_at: null, office_review_by: null, office_review_notes: null,
      // Reset the client-send state so the revised report shows "Ready to send" (see returnToAuditor).
      report_ack_sent_at: null, report_ack_sent_to: null, report_ack_version: null,
      // New WSHC-portal process: the revised report gets a fresh "please apply" email and reminders.
      ...(isPortalJob(job) ? { wshc_apply_email_sent_at: null, wshc_apply_sent_to: null, client_wshc_submitted_at: null } : {}),
    }
    const { error } = await supabase.from('jobs').update(updates).eq('id', id)
    setOfficeBusy(false)
    if (error) { failed("Couldn't record the WSHC return", error.message); return }
    setJob(j => ({ ...j, ...updates })); setWshcForm({ reason: '' })
    await logAction('Package returned by WSHC', 'job', id, client?.name || job.quote_ref, { reason })
    notifyAuditorsOfReturn(reason)
  }

  // ── New WSHC-portal process (bizSAFE, wshc_flow = 'client_portal') ─────────────────────────────────────
  // "End job — no new certificate": the auditors' on-site days go to their experience log (CT17) as with
  // Finalise; the client's expiry is NOT changed; no feedback request. Done server-side (end_bizsafe_job).
  async function endJobNoCert() {
    const reason = endReason.trim()
    if (!reason) { toast('Enter the reason', 'warning', { detail: 'Why is the job ending without a certificate?' }); return }
    const hasA2 = !!job.auditor2_id
    const a1 = parseFloat(a1Days)
    const a2 = hasA2 ? parseFloat(a2Days) : null
    if (!(a1 > 0) || (hasA2 && !(a2 > 0))) { toast("Enter each auditor's on-site days", 'warning', { detail: 'Must be more than 0' }); return }
    setEnding(true)
    const { error: dErr } = await supabase.from('jobs').update({ auditor_mandays: a1, auditor2_mandays: a2 }).eq('id', id)
    if (dErr) { setEnding(false); failed("Couldn't save the on-site days", dErr.message); return }
    const { error } = await supabase.rpc('end_bizsafe_job', { p_job_id: id, p_reason: reason })
    setEnding(false)
    if (error) { failed("Couldn't end the job", error.message); return }
    const now = new Date().toISOString()
    setJob(j => ({ ...j, auditor_mandays: a1, auditor2_mandays: a2, status: 'Completed', workflow_state: 'Completed', auditor_closed: true,
      auditor_closed_at: j.auditor_closed_at || now, wshc_outcome: 'Ended – no certificate', wshc_outcome_at: now,
      wshc_ended_at: now, wshc_ended_by: profile?.name || role, wshc_end_reason: reason }))
    setEndReason(''); setPfAnswer('')
    toast('Job ended — no new certificate', 'success', { detail: 'Finalise later if the client applies' })
  }

  // Admin-only "Reopen job" (a mistake, or WSHC returned the report). Server-side (reopen_bizsafe_job).
  async function reopenPortalJob() {
    const reason = reopenForm.reason.trim()
    if (!reason) { toast('Enter the reason', 'warning'); return }
    setOfficeBusy(true)
    const { error } = await supabase.rpc('reopen_bizsafe_job', { p_job_id: id, p_reason: reason })
    setOfficeBusy(false)
    if (error) { failed("Couldn't reopen the job", error.message); return }
    setJob(j => ({ ...j, status: 'Confirmed', workflow_state: 'Pending WSHC Outcome', auditor_closed: false, wshc_outcome: null,
      wshc_outcome_at: null, wshc_new_expiry: null, wshc_ended_at: null, wshc_ended_by: null, wshc_end_reason: null }))
    setReopenForm({ open: false, reason: '' }); setPfAnswer('')
    toast('Job reopened', 'success', { detail: 'Back to waiting for the client' })
  }

  // The apply email went to the wrong person / address: correct it and send it again straight away
  // (server-side: clears this version's apply-email + reminder records so the new person gets the full sequence).
  async function resendApplyEmail() {
    const name = resendForm.name.trim()
    const email = resendForm.email.trim().toLowerCase()
    if (!name) { toast('Enter the name', 'warning'); return }
    if (!/^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/.test(email)) { toast('Enter a valid email address', 'warning'); return }
    setOfficeBusy(true)
    const { error } = await supabase.rpc('resend_bizsafe_apply_email', { p_job_id: id, p_name: name, p_email: email })
    setOfficeBusy(false)
    if (error) { failed("Couldn't arrange the resend", error.message); return }
    setJob(j => ({ ...j, wshc_applicant_name: name, wshc_applicant_email: email, wshc_apply_email_sent_at: null, wshc_apply_sent_to: null,
      client_wshc_submitted_at: null, workflow_state: 'Pending Office Review' }))
    setResendForm({ open: false, name: '', email: '' })
    // Send it now (the email function re-checks the rules). If that fails, the daily run sends it.
    let sentNow = false
    try {
      const { data } = await supabase.functions.invoke('send-report-ack-reminders', { body: { applyNow: true, jobId: id } })
      sentNow = !!(data?.portal?.apply || []).length
    } catch (e) { /* daily run is the safety net */ }
    if (sentNow) setJob(j => ({ ...j, wshc_apply_email_sent_at: new Date().toISOString(), wshc_apply_sent_to: email, workflow_state: 'Pending WSHC Outcome' }))
    toast(sentNow ? 'Apply email sent again' : 'Apply email will be sent again', 'success', { detail: sentNow ? `To ${email}` : `To ${email}, as soon as possible (at the latest the next morning)` })
  }

  // Who applies on WSHC (the client names them when signing; the office can correct it before the apply email).
  async function saveApplicant() {
    const name = applicantForm.name.trim()
    const email = applicantForm.email.trim().toLowerCase()
    if (!name) { toast('Enter the name', 'warning'); return }
    if (!/^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/.test(email)) { toast('Enter a valid email address', 'warning'); return }
    setOfficeBusy(true)
    const { error } = await supabase.from('jobs').update({ wshc_applicant_name: name, wshc_applicant_email: email }).eq('id', id)
    setOfficeBusy(false)
    if (error) { failed("Couldn't save", error.message); return }
    setJob(j => ({ ...j, wshc_applicant_name: name, wshc_applicant_email: email }))
    setApplicantForm({ open: false, name: '', email: '' })
    await logAction('WSHC applicant changed by office', 'job', id, client?.name || job.quote_ref, { name, email })
    toast('Saved', 'success')
  }

  async function saveReviewApproval() {
    if (isAuditorOnJob()) {
      toast("You can't review your own audit", 'warning', { detail: 'Ask another reviewer' })
      return
    }
    const rb = (reviewForm.reviewedBy || '').trim()
    const ab = (reviewForm.approvedBy || '').trim()
    if (!rb || !ab) { toast('Fill in Reviewed by and Approved by', 'warning'); return }
    setSavingReview(true)
    const now = new Date().toISOString()
    const updates = { reviewed_by: rb, reviewed_at: now, approved_by: ab, approved_at: now }
    const { error } = await supabase.from('jobs').update(updates).eq('id', id)
    setSavingReview(false)
    if (error) { failed("Couldn't save the review", error.message); return }
    setJob(j => ({ ...j, ...updates }))
    await logAction('Audit review & approval recorded', 'job', id, client?.name || job.quote_ref, { reviewed_by: rb, approved_by: ab })
  }

  // COI completeness: every assigned auditor must have submitted a COI declaration before the audit
  // can be finalised. Returns the list of assigned auditor names still missing a declaration.
  // A COI counts as present only if a response exists for the slot AND was declared by the auditor
  // currently assigned to it — so changing the auditor automatically invalidates the old declaration.
  function coiValidFor(slot, name) {
    if (!name) return false
    return coiResponses.some(r => r.auditor_slot === slot && r.auditor_name === name)
  }
  function coiMissingList() {
    const missing = []
    if (job?.auditor_id && !coiValidFor('auditor1', job.auditor_name)) missing.push(job.auditor_name || 'Auditor 1')
    if (job?.auditor2_id && !coiValidFor('auditor2', job.auditor2_name)) missing.push(job.auditor2_name || 'Auditor 2')
    return missing
  }

  // Office/Admin/QM only — reopen auditor access if the expiry was keyed wrongly or the case is reopened.
  async function reopenAuditorAccess() {
    if (!window.confirm('Reopen auditor access for this audit? The assigned auditor will be able to view and edit it again.')) return
    const { error } = await supabase.from('jobs').update({ auditor_closed: false, auditor_closed_at: null }).eq('id', id)
    if (error) { failed("Couldn't reopen", error.message); return }
    setJob(j => ({ ...j, auditor_closed: false, auditor_closed_at: null }))
    await logAction('Auditor access reopened', 'job', id, client?.name || job.quote_ref, {})
  }

  // Office/Admin/QM finalisation: record the client's new bizSAFE expiry from THIS job. Writing
  // clients.bizsafe_expiry_date (the single source of truth) fires trg_client_expiry_closes_audit,
  // which closes the latest finalised RM job to the auditor AND creates the countable CT17 record.
  // Eligible only when THIS job is genuinely finalised — the same condition the closure trigger uses
  // (status Completed OR a positive certification decision). Mirrors the server so the UI can't imply
  // a closure/log that won't actually happen.
  // Finalisation (= recording the new expiry) represents WSHC "Accepted". It unlocks when the package has
  // been approved & submitted to WSHC (Pending WSHC Outcome) or via the legacy certification decision.
  // New WSHC-portal process: only once the client has been sent the apply steps (no approval-time shortcut).
  const auditFinalised = isPortalJob(job)
    ? (job.status === 'Completed' || job.workflow_state === 'Pending WSHC Outcome')
    : (job?.status === 'Completed' || job?.workflow_state === 'Pending WSHC Outcome'
      || ['Proceed', 'Issue report'].includes(auditDecision?.decision_outcome))

  async function finaliseAudit() {
    if (!newExpiry || !job.client_id) { toast('Enter the new bizSAFE expiry date', 'warning'); return }
    // A bizSAFE certificate expiry is always in the future (audit date + 3 years). Reject a blank/past date.
    if (newExpiry <= new Date().toISOString().slice(0, 10)) { toast('Expiry date must be in the future', 'warning'); return }
    if (!auditFinalised) {
      toast('Record the certification decision first', 'warning', { detail: 'Then enter the new expiry' })
      return
    }
    // Per-auditor on-site days (each person's own days — not a split). Required for every assigned auditor.
    const hasA2 = !!job.auditor2_id
    const a1 = parseFloat(a1Days)
    const a2 = hasA2 ? parseFloat(a2Days) : null
    if (!(a1 > 0) || (hasA2 && !(a2 > 0))) {
      toast("Enter each auditor's on-site days", 'warning', { detail: 'Must be more than 0' })
      return
    }
    if (!window.confirm('Record the new bizSAFE expiry and finalise this audit cycle?\n\nThis stores each auditor’s on-site days, updates the client record (and every expiry-driven page), closes the audit to the auditor, and creates each auditor’s CT17 maintenance record.')) return
    setFinalising(true)
    // 1) Store the per-auditor on-site days on the job — the finalisation reads these for the CT17 log.
    const { error: dErr } = await supabase.from('jobs').update({ auditor_mandays: a1, auditor2_mandays: a2 }).eq('id', id)
    if (dErr) { setFinalising(false); failed("Couldn't save the on-site days", dErr.message); return }
    setJob(j => ({ ...j, auditor_mandays: a1, auditor2_mandays: a2 }))
    // 2) Finalise THIS specific job (deterministic), in ONE server-side call: close to auditor + CT17
    //    record(s) + client expiry + package Completed (WSHC Accepted). Done in the RPC (definer) so it
    //    can't no-op on an unchanged date, attribute closure to a different job, or be blocked by jobs RLS.
    const { error } = await supabase.rpc('finalise_audit_job', { p_job_id: id, p_expiry: newExpiry })
    if (error) { setFinalising(false); failed("Couldn't finalise", error.message); return }
    const now = new Date().toISOString()
    setClient(c => c ? { ...c, bizsafe_expiry_date: newExpiry } : c)
    setJob(j => ({ ...j, workflow_state: 'Completed', status: 'Completed', wshc_outcome: 'Accepted', wshc_outcome_at: now, wshc_new_expiry: newExpiry, auditor_closed: true, auditor_closed_at: now }))
    setFinalising(false)
    setNewExpiry('')
    await logAction('Audit finalised — bizSAFE expiry recorded', 'job', id, client?.name || job.quote_ref, { expiry: newExpiry, auditor_mandays: a1, auditor2_mandays: a2 })
    // 3) Client feedback request — the audit cycle is closed, so ask the client how it went. Best-effort:
    //    a mail failure must never undo a finalisation that has already been recorded. Sent once per job
    //    (feedback_request_sent_at), through send-client-email so it is logged in outbound_emails like every
    //    other client email. Feeds the client-feedback input to monitoring (M&P 3.5) and Management Review.
    sendFeedbackRequest()
    toast('Audit finalised', 'success', { detail: 'bizSAFE expiry and CT17 log updated' })
  }

  // Post-audit client feedback request (JotForm). Fire-and-forget by design — never blocks or reverses a
  // finalisation that has already been recorded. The email, its wording and the once-only rule all live in
  // the send-feedback-request function, so the same send is used here and by the catch-up run.
  async function sendFeedbackRequest() {
    try {
      const { data } = await supabase.functions.invoke('send-feedback-request', { body: { job_id: id } })
      if (data?.result?.sent) {
        setJob(j => ({ ...j, feedback_request_sent_at: new Date().toISOString() }))
        await logAction('Client feedback request sent', 'job', id, client?.name || job.quote_ref, { to: data.result.to })
      }
    } catch (e) { /* best-effort */ }
  }

  async function reloadCOI() {
    const [{ data: cois }, { data: decs }] = await Promise.all([
      supabase.from('auditor_coi_responses').select('*').eq('job_id', id),
      supabase.from('coi_decisions').select('*').eq('job_id', id),
    ])
    setCoiResponses(cois || [])
    setCoiDecisions(decs || [])
  }

  // Client acknowledgement is submitted on the public /ack page, so the job + its ACK response change
  // outside this view. Refetch BOTH the ACK response row (the panel's actual source — same reactive
  // pattern as reloadCOI refetching coiResponses) AND the status/sent fields, so the ACK switches to
  // "Received" and "Audit plan last sent" stays correct, immediately, without a manual refresh.
  async function reloadAckState() {
    const [{ data: j }, { data: ackData }] = await Promise.all([
      supabase.from('jobs')
        .select('ack_signature, plan_signature, status, client_ack_received, client_ack_date, client_ack_token, plan_sent_date')
        .eq('id', id).maybeSingle(),   // audit_plan_sent_at omitted (optional col); preserved local value still drives "last sent"
      supabase.from('client_ack_responses').select('*').eq('job_id', id).order('created_at', { ascending: false }).limit(1),
    ])
    if (j) setJob(p => ({ ...p, ...j }))
    setAckResponse(Array.isArray(ackData) ? ackData[0] || null : ackData)
  }

  // Flagged COI conflicts on the CURRENTLY assigned auditor(s) that need a proceed/replace decision.
  // A conflict is "resolved" once a 'Proceed' decision exists for that slot + auditor name.
  function coiConflictList() {
    const proceed = (slot, name) => coiDecisions.find(d => d.auditor_slot === slot && d.auditor_name === name && d.decision === 'Proceed')
    const flag = (coi, name, slot) => {
      if (!coi || !name || coi.auditor_name !== name) return null   // ignore stale COI after auditor change
      const risk = coi.risk_level || (coi.flagged ? 'Medium' : 'None')
      if (risk !== 'High' && risk !== 'Medium') return null
      return { slot, name, risk, resolved: !!proceed(slot, name) }
    }
    // Match the COI row to the CURRENTLY assigned auditor by name — after a replacement the slot has
    // several rows (old + new auditor), so an unqualified .find() would return the stale old row and the
    // new auditor's conflict would never surface. Tie detection to the current auditor, every time.
    const c1 = coiResponses.find(r => r.auditor_slot === 'auditor1' && r.auditor_name === job?.auditor_name)
    const c2 = coiResponses.find(r => r.auditor_slot === 'auditor2' && r.auditor_name === job?.auditor2_name)
    return [flag(c1, job?.auditor_name, 'auditor1'), flag(c2, job?.auditor2_name, 'auditor2')].filter(Boolean)
  }

  async function recordCoiProceed(conflict) {
    const just = (coiDecisionForm[conflict.slot]?.justification || '').trim()
    if (!just) { toast('Enter a justification', 'warning'); return }
    setSavingCoiDecision(true)
    const { error } = await supabase.from('coi_decisions').insert({
      job_id: id, auditor_slot: conflict.slot, auditor_name: conflict.name,
      coi_risk_level: conflict.risk, decision: 'Proceed', justification: just,
      decided_by_id: profile?.id, decided_by_name: profile?.name || role, decided_by_role: role,
    })
    setSavingCoiDecision(false)
    if (error) { failed("Couldn't record the decision", error.message); return }
    await logAction('COI decision — proceed despite conflict', 'job', id, client?.name || job.quote_ref, {
      auditor: conflict.name, risk: conflict.risk, justification: just,
    })
    setCoiDecisionForm(f => ({ ...f, [conflict.slot]: {} }))
    reloadCOI()
  }

  async function recordCoiReplace(conflict) {
    setSavingCoiDecision(true)
    const { error } = await supabase.from('coi_decisions').insert({
      job_id: id, auditor_slot: conflict.slot, auditor_name: conflict.name,
      coi_risk_level: conflict.risk, decision: 'Replace', justification: null,
      decided_by_id: profile?.id, decided_by_name: profile?.name || role, decided_by_role: role,
    })
    if (error) { setSavingCoiDecision(false); failed("Couldn't record the decision", error.message); return }
    await logAction('COI decision — replace auditor', 'job', id, client?.name || job.quote_ref, {
      auditor: conflict.name, risk: conflict.risk,
    })
    // Force reassignment — unassign that auditor slot
    await updateField(conflict.slot === 'auditor2' ? 'auditor2_id' : 'auditor_id', '', true)
    setSavingCoiDecision(false)
    setCoiDecisionForm(f => ({ ...f, [conflict.slot]: {} }))
    reloadCOI()
  }

  async function loadJob() {
    planBaseline.current = null   // no schedule prompts while (re)loading — the baseline is set again at the end
    const [{ data: j }, { data: cois }, { data: ackData }, { data: alertData }, { data: coiDecs }] = await Promise.all([
      supabase.from('jobs').select('*, clients(*)').eq('id', id).maybeSingle(),
      supabase.from('auditor_coi_responses').select('*').eq('job_id', id),
      supabase.from('client_ack_responses').select('*').eq('job_id', id).order('created_at', { ascending: false }).limit(1),
      supabase.from('admin_alerts').select('*').eq('job_id', id).eq('resolved', false),
      supabase.from('coi_decisions').select('*').eq('job_id', id)
    ])
    if (!j) { navigate('/jobs'); return }
    // Guard against a stale duplicate (auditor 2 == auditor 1) that would make the schedule logic think
    // two auditors are assigned (and wrongly fire the 2-auditor blocked notice). Treat it as one auditor.
    if (j.auditor2_id && j.auditor2_id === j.auditor_id) { j.auditor2_id = null; j.auditor2_name = null; j.auditor2_grade = null }
    // A fresh load reflects persisted DB values — start from a clean, non-blocked state.
    setPlanFieldsDirty(false)
    setFieldErrors({})
    setJob(j)
    setClient(j.clients)
    setCoiResponses(cois || [])          // this job's OWN responses — unchanged; feeds ALL conflict/dot/gate logic
    setCoiDecisions(coiDecs || [])
    // Cosmetic, STRICTLY display-only: for a combined MEMBER job, mark the "COI completed" indicator satisfied
    // if the PRIMARY holds a completed declaration for the same auditor(s). Computed into a SEPARATE boolean —
    // never merged into coiResponses/coiDecisions — so conflict logic, the Validate dot, and the plan-send
    // high-risk check are untouched. Read-only: nothing is written and no gate consults this flag.
    let combinedOk = false
    if (j.combined_group_id && !j.is_combined_primary) {
      const { data: pj } = await supabase.from('jobs').select('id').eq('combined_group_id', j.combined_group_id).eq('is_combined_primary', true).maybeSingle()
      if (pj?.id) {
        const { data: pc } = await supabase.from('auditor_coi_responses').select('auditor_slot, auditor_name').eq('job_id', pj.id)
        const has = (slot, name) => (pc || []).some(r => r.auditor_slot === slot && r.auditor_name === name)
        const anyMissing = (j.auditor_id && !has('auditor1', j.auditor_name)) || (j.auditor2_id && !has('auditor2', j.auditor2_name))
        combinedOk = !anyMissing
      }
    }
    setCombinedCoiOk(combinedOk)
    setAckResponse(Array.isArray(ackData) ? ackData[0] || null : ackData)
    if (j?.decision_id) {
      const { data: dec } = await supabase.from('audit_decisions').select('*').eq('id', j.decision_id).maybeSingle()
      setAuditDecision(dec || null)
    }
    setAlerts(alertData || [])

    if (j.start_time) setStartTime(j.start_time)
    if (j.num_locations) setNumLocations(j.num_locations)
    setAuditStructure(j.audit_structure || 'Parallel')
    setHalfDayPosition(j.half_day_position || 'Day2')
    setSecondAuditorBlock(j.second_auditor_block || 'Morning')
    setAuditDateDay2(j.audit_date_day2 || '')
    setDay2Start(j.day2_start || j.start_time || '0900')

    // Load audit plan fields
    const fd = j.form_data || {}
    // New application going straight for Level 4 / STAR → the level picked on the quote (else Level 3, as before).
    const objectiveLevel = isNewApplication(j) ? applyingLevel(j) : 'bizSAFE Level 3'
    setAuditObjective(j.audit_objective || `The objective of this audit is to verify that the company has achieved satisfactory WSH and Security outcomes that suggests eligibility of ${objectiveLevel} recognition`)
    // Auto-populate audit criteria from SAC Schedule if not already set
    const auditCriteriaDefaults = {
      'bizSAFE Risk Management Audit': 'Code of Practice on Workplace Safety and Health (WSH) Risk Management',
      'Construction Worksite': 'Requirements for the Construction Safety Audit Scoring System (ConSASS)',
      'Metalworking Industry': 'Requirements on the Implementation of Safety Management System for the Metalworking Industry',
      'Shipyard': 'Workplace Safety & Health Requirements for Marine Industries',
      'Oil Refinery or Petrochemical Plant': 'SS 651:2019',
      'Semiconductor Wafer Fabrication Plant': 'SS 651:2019',
      'Chemical Manufacturing Plant': 'SS 651:2019',
      'Pharmaceutical Plant': 'SS 651:2019',
    }
    const defaultCriteria = auditCriteriaDefaults[j.audit_type] || auditCriteriaDefaults[j.scope] || 'Code of Practice on Workplace Safety and Health (WSH) Risk Management'
    setAuditCriteriaField(j.audit_criteria || defaultCriteria)
    setAuditScopeField(j.audit_scope_text || fd.business_nature || j.business_nature || '')
    setProjectName(j.project_name || '')
    setAuditApproachNote(j.audit_approach_note || '')
    setApplicationType(j.application_type || fd.application_type || 'New Application')
    setReportRef(j.report_reference_no || '')
    // TEMP TRACE (audit-site auto-fill bug): show the RAW backend value vs the HQ address, before any UI.
    console.log('[audit-site trace] backend j.audit_site_address =', JSON.stringify(j.audit_site_address),
      '| HQ (clients.address) =', JSON.stringify(j.clients?.address),
      '| form_data.company_address =', JSON.stringify(j.form_data?.company_address),
      '| job.id =', j.id, '| created_at =', j.created_at)
    setAuditSiteAddress(j.audit_site_address || '')   // load stored value only; empty stays empty (no HQ default)
    setCopiedFromHq(false)
    setMandayJustification(j.manday_justification || '')
    setJustificationFromQuote(false)
    // Backward-compat: older jobs were created before the justification was copied across.
    // Display-only fallback — pull it from the originating quotation, never write it back.
    if (!j.manday_justification && j.quote_ref) {
      const { data: relQ } = await supabase.from('quotations')
        .select('manday_justification').eq('quote_ref', j.quote_ref)
        .order('created_at', { ascending: false }).limit(1)
      const qj = relQ?.[0]?.manday_justification
      if (qj) { setMandayJustification(qj); setJustificationFromQuote(true) }
    }
    setReviewForm({ reviewedBy: j.reviewed_by || '', approvedBy: j.approved_by || '' })
    setRmLeaderName(j.rm_leader_name || '')
    setRmLeaderPhone(j.rm_leader_phone || '')
    setChecklistUrl(j.checklist_url || DEFAULT_CHECKLIST_URL)

    // Load saved plan rows. They only count as "manual edits" (prompt-worthy) if the saved flag says so;
    // rows auto-persisted on a previous send are NOT manual edits.
    if (j.plan_rows_edited) setEditedRows(j.plan_rows_edited)
    setManuallyEdited(!!j.plan_rows_manually_edited)

    const { data: aus } = await supabase.from('auditors').select('*').eq('active', true).order('name')
    setAllAuditors(aus || [])

    // Current planning workforce: use the saved planning value if present, else the original
    // questionnaire count. form_data (the client-submitted basis) is never altered.
    const planEmp = (j.planning_num_employees != null && j.planning_num_employees !== '')
      ? j.planning_num_employees : (fd.num_employees ?? '')
    setPlanningEmployees(planEmp)
    setPlanningBasisReason(j.planning_basis_reason || '')

    // CT17 calc — resolve the industry once, store it, then compute from the planning basis.
    if (j.audit_type === AUDIT_TYPES.BIZSAFE && fd) {
      // Single source of truth: fixed scope overrides generic; only "Other Industries" uses complexity.
      const cm = { 'High': 'Other - High complexity', 'Medium': 'Other - Medium complexity', 'Low': 'Other - Low complexity' }
      // Prefer the authoritative complexity OVERRIDE (complexity_classification, set at the quotation stage)
      // over the original questionnaire value in form_data — otherwise a job recomputes on the pre-override
      // (e.g. keyword-matched "High") complexity and shows a false man-day deviation. Mirrors the quotation.
      const ccOv = (() => { try { return typeof j.complexity_classification === 'string' ? JSON.parse(j.complexity_classification) : j.complexity_classification } catch (e) { return null } })()
      // Only a MANUAL override (has override_reason) supersedes form_data — so jobs without an override keep
      // their exact existing baseline; overridden jobs stop recomputing on the pre-override complexity.
      const overrideIndustry = ccOv?.override_reason ? (ccOv.industry || cm[ccOv.complexity]) : null
      const industry = resolveCT17Industry(fd.audit_scope, {
        complexityIndustry: overrideIndustry || cm[fd.complexity],
        ssicCode: fd.ssic_code,
        businessNature: fd.business_nature,
      })
      setPlanningIndustry(industry)
      const initialCalc = calcBizSAFEMandays(industry, planEmp, j.num_locations || fd.num_locations || 1)
      setCalc(initialCalc)
      // Deterministic override detection: man-days is a MANUAL override only if it deviates from the
      // CT17 baseline at load. If it matches (auto), it will follow live CT17 when planning inputs change.
      mandaysTouchedRef.current = (j.mandays != null && j.mandays !== '' && parseFloat(j.mandays) !== initialCalc.totalMandays)
    }
    if (j.audit_type === AUDIT_TYPES.SHMS && fd) {
      setCalc(calcSHMSMandays(j.scope, planEmp, fd.audit_sequence))
    }
    setLoading(false)
    setPlanBaselineKey(k => k + 1)
  }

  // Plan-defining fields whose change (via updateField) invalidates a prior review/approval.
  // The DB trigger jobs_invalidate_review is the authority; this set just mirrors it client-side.
  const REVIEW_INVALIDATING_KEYS = new Set(['auditor_id', 'auditor2_id', 'audit_date', 'mandays', 'num_locations'])

  // Re-read the (possibly trigger-cleared) review/approval fields so the UI matches the DB exactly,
  // without a full reload. Only worth calling when an approval currently exists.
  async function syncReviewFromDb() {
    const { data } = await supabase.from('jobs')
      .select('reviewed_by, reviewed_at, approved_by, approved_at, plan_signature, ack_signature')
      .eq('id', id).maybeSingle()
    if (data) {
      setJob(j => ({ ...j, ...data }))
      setReviewForm({ reviewedBy: data.reviewed_by || '', approvedBy: data.approved_by || '' })
    }
  }
  // Fires after any edit that may have invalidated review/approval OR shifted the plan signature
  // (re-acknowledgement) — so the UI reflects it without a manual reload.
  const stateNeedsSync = () => !!(job?.reviewed_by || job?.approved_by || job?.ack_signature)

  // Field edits update LOCAL state only and mark the form dirty — nothing is persisted until the user
  // clicks Save (which runs the existing save logic, plan_signature recompute and COI/ack invalidation).
  // The `persist` flag is used only by internal flows that must write immediately (e.g. COI "replace
  // auditor"); it is never set from a normal field onChange.
  async function updateField(key, value, persist = false) {
    // Treat empty string as null for ID fields so DB stores null not ''
    const cleanValue = (key.endsWith('_id') && value === '') ? null : value
    const updates = { [key]: cleanValue }
    if (key === 'auditor_id') {
      const a = allAuditors.find(a => a.id === cleanValue)
      if (a) { updates.auditor_name = a.name; updates.auditor_grade = a.grade }
      else { updates.auditor_name = null; updates.auditor_grade = null }
      // Never let auditor 2 stay as a duplicate of auditor 1 — a stale duplicate makes the schedule
      // think there are two auditors and wrongly triggers the 2-auditor blocked warning.
      if (cleanValue && job.auditor2_id === cleanValue) {
        updates.auditor2_id = null; updates.auditor2_name = null; updates.auditor2_grade = null
      }
    }
    if (key === 'auditor2_id') {
      if (!cleanValue) {
        updates.auditor2_name = null
        updates.auditor2_grade = null
      } else {
        const a = allAuditors.find(a => a.id === cleanValue)
        if (a) { updates.auditor2_name = a.name; updates.auditor2_grade = a.grade }
      }
    }
    if (!persist) {
      // Local-only: defer the DB write (and any signature/COI/ack invalidation) to Save.
      setJob(j => ({ ...j, ...updates }))
      setPlanFieldsDirty(true)
      return
    }
    const { error } = await supabase.from('jobs').update(updates).eq('id', id)
    if (error) { failed("Couldn't save the change", error.message); return }
    setJob(j => ({ ...j, ...updates }))
    if (REVIEW_INVALIDATING_KEYS.has(key) && stateNeedsSync()) await syncReviewFromDb()
  }

  // True when the planning-stage workforce has been set to something other than the original
  // questionnaire count — which makes a short planning-basis reason mandatory.
  function planningDiffers() {
    const orig = job?.form_data?.num_employees
    return planningEmployees !== '' && planningEmployees != null && String(planningEmployees) !== String(orig ?? '')
  }

  // Required audit-detail fields. Returns a map of {fieldKey: true} for each missing one.
  function collectRequiredErrors() {
    const md = mandayDeviation()
    const errs = {}
    if (!job.audit_date) errs.audit_date = true
    if (!startTime) errs.startTime = true
    if (!rmLeaderName.trim()) errs.rmLeaderName = true
    if (!rmLeaderPhone.trim()) errs.rmLeaderPhone = true
    if (!oneLine(auditSiteAddress).trim()) errs.auditSiteAddress = true
    // Justification is valid if the displayed value OR the value already persisted/carried from the
    // quotation is non-empty — it must NOT be flagged just because it wasn't manually typed this session.
    const effectiveJustification = (mandayJustification || job?.manday_justification || '').trim()
    if (md.changed && !effectiveJustification) errs.mandayJustification = true
    // A changed planning workforce requires a short planning-basis reason.
    if (planningDiffers() && !planningBasisReason.trim()) errs.planningBasisReason = true
    // Schedule completeness: when the chosen structure spans two days, the Day 2 date is required.
    // (Day 2 start time always carries a default, so it can never be missing.)
    if (twoDaySpan && !auditDateDay2) errs.auditDateDay2 = true
    return errs
  }

  async function saveAuditPlanFields() {
    const md = mandayDeviation()
    // Draft save: surface missing required fields (red highlight) but DO NOT block saving.
    // Full enforcement happens on Send only.
    const errs = collectRequiredErrors()
    setFieldErrors(errs)
    // Man-days from local state, guarded so an empty/NaN input is stored as null (not a failed write).
    const mdVal = job.mandays
    const mandaysClean = (mdVal === '' || mdVal == null || (typeof mdVal === 'number' && Number.isNaN(mdVal))) ? null : mdVal
    // COI + client-acknowledgement tokens are NOT minted on Save. Save persists ONLY planning fields;
    // issuance (COI/ACK links + auditor email) happens exclusively on "Send audit plan". Tokens already
    // issued by a previous Send are preserved because this update simply doesn't touch those columns.
    const updates = {
      // Job-level fields edited via updateField persist HERE on Save (no longer auto-saved on change),
      // so the DB trigger recomputes plan_signature and runs COI/ack invalidation once, on Save.
      status: job.status,
      auditor_id: job.auditor_id || null,
      auditor_name: job.auditor_name || null,
      auditor_grade: job.auditor_grade || null,
      // Persist auditor 2 only when effectively assigned; heal a stale/unresolvable id to null. Guarded
      // with allAuditors.length so a valid id is never erased during the brief window before auditors load.
      auditor2_id: (hasAuditor2 || allAuditors.length === 0) ? (job.auditor2_id || null) : null,
      auditor2_name: (hasAuditor2 || allAuditors.length === 0) ? (job.auditor2_name || null) : null,
      auditor2_grade: (hasAuditor2 || allAuditors.length === 0) ? (job.auditor2_grade || null) : null,
      audit_date: job.audit_date || null,
      mandays: mandaysClean,
      observer_name: job.observer_name || null,
      audit_objective: auditObjective,
      audit_criteria: auditCriteriaField,
      audit_scope_text: auditScopeField,
      project_name: projectName,
      audit_approach_note: auditApproachNote.trim() || null,
      application_type: applicationType || null,
      report_reference_no: reportRef.trim() || null,
      rm_leader_name: rmLeaderName.trim(),
      rm_leader_phone: rmLeaderPhone.trim(),
      audit_site_address: oneLine(auditSiteAddress).trim(),
      checklist_url: checklistUrl,
      start_time: startTime,
      num_locations: numLocations,
      audit_structure: auditStructure,
      half_day_position: halfDayPosition,
      second_auditor_block: secondAuditorBlock,
      audit_date_day2: auditDateDay2 || null,
      day2_start: day2Start,
      planning_num_employees: (planningEmployees === '' || planningEmployees == null) ? null : parseInt(planningEmployees),
      planning_basis_reason: planningDiffers() ? planningBasisReason.trim() : null,
      manday_justification: md.changed ? ((mandayJustification || job?.manday_justification || '').trim() || null) : null,
      plan_rows_edited: editedRows || planRows,
      plan_rows_manually_edited: manuallyEdited,
    }
    // Capture the previously-saved planning snapshot BEFORE merging the update.
    const prevEmp = job?.planning_num_employees ?? null
    const prevReason = job?.planning_basis_reason ?? null
    let { error } = await supabase.from('jobs').update(updates).eq('id', id)
    let planningSaved = true
    // Resilience: if the planning columns aren't migrated yet, don't let that block the whole audit-plan
    // save (which would leave the form permanently "dirty" and Send disabled). Retry without them.
    if (error && /planning_(num_employees|basis_reason)|audit_structure|half_day_position|second_auditor_block|audit_date_day2|day2_start|plan_rows_manually_edited|application_type|report_reference_no|could not find|does not exist|schema cache/i.test(error.message || '')) {
      const core = { ...updates }
      delete core.planning_num_employees
      delete core.planning_basis_reason
      delete core.audit_structure
      delete core.half_day_position; delete core.second_auditor_block; delete core.audit_date_day2; delete core.day2_start
      delete core.plan_rows_manually_edited
      delete core.auditor_coi_token_names   // pre-PHASE1_SAC DBs lack this column; tokens map still persists
      delete core.application_type; delete core.report_reference_no   // optional columns; safe if not yet migrated
      const retry = await supabase.from('jobs').update(core).eq('id', id)
      error = retry.error
      planningSaved = false
      if (!error) console.warn('Audit plan saved without planning fields — run the planning_num_employees / planning_basis_reason migration.')
    }
    if (error) { failed("Couldn't save", error.message); return }
    // Commit UI state IMMEDIATELY after the successful write — before any further awaits — so that a
    // failure in the (non-critical) audit-trail call below can never leave the form stuck "dirty" and
    // keep Send blocked. The dirty flag must clear the moment the data is persisted.
    setJob(j => ({ ...j, ...updates }))
    setPlanFieldsDirty(false)
    settlePlanBaseline()   // what was just saved is the new "unchanged" state for the schedule prompt
    // Plan changed → a prior review/approval or acknowledgement may have been auto-cleared by the DB
    // trigger; mirror it in the UI.
    if (stateNeedsSync()) await syncReviewFromDb()
    // Traceability (best-effort): log ONLY when the planning basis actually changed since the last save
    // AND still differs from the questionnaire. Wrapped so a logging failure never blocks save/send.
    const snapshotChanged = String(updates.planning_num_employees ?? '') !== String(prevEmp ?? '')
      || String(updates.planning_basis_reason ?? '') !== String(prevReason ?? '')
    if (logAction && planningSaved && planningDiffers() && snapshotChanged) {
      try {
        await logAction('Audit planning basis updated', 'job', id, client?.name || job.quote_ref, {
          questionnaire_employees: job?.form_data?.num_employees ?? null, planning_employees: updates.planning_num_employees,
          reason: updates.planning_basis_reason, lead_auditor_required: !!calc?.requiresLeadAuditor, by: profile?.name || 'Staff' })
      } catch (e) { /* audit log is non-blocking — never prevent saving/sending */ }
    }
    if (Object.keys(errs).length) toast('Draft saved', 'warning', { detail: 'Fill in the red fields before sending' })
    else toast('Audit plan saved', 'success')
  }

  // The schedule as shown, copied and sent. Stand-in names left in an edited schedule ("Auditor 1",
  // "Auditor 1 / Auditor 2") show as the assigned auditors, so what the office sees is what the client gets.
  function getDisplayRows() {
    return remapAuditorNames(editedRows || planRows, { to: currentAuditorNames() })
  }

  function updateRow(i, field, value) {
    const rows = [...(editedRows || planRows)]
    rows[i] = { ...rows[i], [field]: value }
    setEditedRows(rows)
    setManuallyEdited(true)
    setPlanFieldsDirty(true)
  }

  function deleteRow(i) {
    if (!window.confirm('Delete this row from the schedule?')) return
    const rows = [...(editedRows || planRows)]
    rows.splice(i, 1)
    setEditedRows(rows)
    setManuallyEdited(true)
    setPlanFieldsDirty(true)
  }

  function resetToAuto() {
    setEditedRows(null)
    setManuallyEdited(false)
    setPlanFieldsDirty(true)
    setPlanChanges([])
  }

  // Man-day deviation vs the CT 17 calculated baseline (drives the justification requirement)
  function mandayDeviation() {
    const base = calc ? (calc.totalMandays != null ? calc.totalMandays : calc.mandays) : null
    const changed = base != null && job?.mandays != null && job.mandays !== '' &&
      parseFloat(job.mandays) !== parseFloat(base)
    return { base, changed }
  }

  function getEndTime() {
    const rows = getDisplayRows()
    if (!rows.length) return ''
    const last = rows[rows.length - 1]
    return last?.time?.split('-')[1] || last?.time || ''
  }

  function buildAuditPlanText() {
    const j = job; const c = client
    const hqAddress = c?.address || j.form_data?.company_address || ''
    const auditDate = j.audit_date ? format(parseISO(j.audit_date), 'dd MMM yyyy') : 'TBD'
    const timeDisplay = `${startTime}-${getEndTime()}`
    // Day span derived from the actual generated rows.
    const planForText = getDisplayRows()
    const multiDay = planForText.some(r => r.isDayHeader)
    const _hm = m => String(Math.floor(m / 60)).padStart(2, '0') + String(m % 60).padStart(2, '0')
    const daySpanTxt = (n) => {
      let cur = 1, first = null, last = null
      for (const r of planForText) {
        if (r.isDayHeader) { const m = String(r.time).match(/(\d+)/); cur = m ? parseInt(m[1]) : cur + 1; continue }
        if (cur !== n) continue
        const rng = String(r.time).match(/^(\d{2})(\d{2})-(\d{2})(\d{2})$/); const one = String(r.time).match(/^(\d{2})(\d{2})$/)
        if (rng) { const s = (+rng[1]) * 60 + (+rng[2]), e = (+rng[3]) * 60 + (+rng[4]); if (first == null) first = s; last = e }
        else if (one) { const v = (+one[1]) * 60 + (+one[2]); if (first == null) first = v; last = v }
      }
      return first == null ? '' : `${_hm(first)}-${_hm(last)}`
    }
    const day2Date = auditDateDay2 ? format(parseISO(auditDateDay2), 'dd MMM yyyy') : 'TBD'

    let lines = []
    lines.push(`To\t${c?.name || ''}\t\t\t\tAttn\t${c?.contact_name || ''}`)
    lines.push(`Date\t${format(new Date(), 'dd MMM yyyy')}\t\t\t\tRef\t${j.quote_ref || ''}`)
    lines.push('')
    lines.push('AUDIT PLAN')
    lines.push('')
    lines.push(`Auditee Organisation Name\t${c?.name || ''}`)
    lines.push(`Audit Objectives\t${auditObjective}`)
    lines.push(`Audit Criteria\t${auditCriteriaField}`)
    lines.push(`Audit Scope\t${auditScopeField}`)
    lines.push(`HQ Address\t${hqAddress}`)
    lines.push(`Audit Site Address\t${oneLine(auditSiteAddress) || 'TBD'}`)
    if (multiDay) {
      lines.push(`Day 1\t${auditDate}${daySpanTxt(1) ? '  ' + daySpanTxt(1) : ''}`)
      lines.push(`Day 2\t${day2Date}${daySpanTxt(2) ? '  ' + daySpanTxt(2) : ''}`)
    } else {
      lines.push(`Date of Audit\t${auditDate}\t\tTime of Audit\t${timeDisplay}`)
    }
    lines.push(`Name of Auditing Organisation\t${FIRM_NAME()}`)
    lines.push(`Assigned Auditor 1\t${j.auditor_name || 'TBD'}`)
    lines.push(`Assigned Auditor 2\t${j.auditor2_name || 'Nil'}`)
    if (j.observer_name) lines.push(`SAC Observer\t${j.observer_name}`)
    if (rmLeaderName) lines.push(`RM Leader\t${rmLeaderName}${rmLeaderPhone ? ' · ' + rmLeaderPhone : ''}`)
    lines.push(`Audit Checklist (for reference only)\t${checklistUrl || DEFAULT_CHECKLIST_URL}`)
    lines.push('')
    lines.push('AUDIT PLANNING NOTES')
    lines.push(`Project Name\t${projectName || 'Nil'}`)
    lines.push(`Audit Approach Notes\t${auditApproachNote || 'Nil'}`)
    if (mandayDeviation().changed && mandayJustification.trim()) {
      lines.push(`Audit Duration Justification\t${mandayJustification.trim()}`)
    }
    const cc = (() => { try { return typeof j.complexity_classification === 'string' ? JSON.parse(j.complexity_classification) : j.complexity_classification } catch (e) { return null } })()
    if (cc && cc.complexity) {
      lines.push('')
      lines.push('COMPLEXITY')
      lines.push(`Classification\t${cc.complexity}`)
      lines.push(`Basis\t${cc.override_reason ? 'Reviewed and set by Auditing Organisation' : 'System-determined from application details'}`)
      if (cc.determined_by) lines.push(`${cc.override_reason ? 'Reviewed by' : 'Determined by'}\t${cc.determined_by}`)
    }
    lines.push('')
    lines.push('PREPARATION NOTES')
    lines.push(`• You may fail the audit for inadequate preparation and will need to pay ${FIRM_NAME()} additional fees for re-audits`)
    lines.push(`• RM Leader, RA Approver, RM Members + 3 implementation persons and 3 workers must be present`)
    lines.push('• Ensure that your documents are signed and ready. Please photocopy a non-returnable full set for the auditor')
    lines.push('• As this is an audit and not a consultancy session, please ensure that your organisation has implemented all control measures')
    lines.push('')
    lines.push('AUDIT SCHEDULE')

    const flat = s => String(s || '').replace(/\n/g, '; ').trim()
    getDisplayRows().forEach(row => {
      if (row.isDayHeader) { lines.push(''); lines.push(String(row.time)); return }
      if (row.time === 'PARALLEL_HEADER') {
        lines.push('Time\tAuditor 1\tAuditor 2')
        return
      }
      if (row.parallel) {
        lines.push(`${row.time}\t${flat(row.activity)}\t${flat(row.parallelActivity)}`)
        return
      }
      lines.push(`${row.time}\t${flat(row.activity)}\t${row.by || ''}`)
    })

    return lines.join('\n')
  }

  // SINGLE source of truth for COI tokens. Each token is tied to the auditor CURRENTLY assigned to its
  // slot (recorded in auditor_coi_token_names):
  //   • assigned slot with no token        → mint one          (first assignment)
  //   • token issued to a different auditor → mint a fresh one  (reassignment: the old token is dropped
  //                                                              from the map and therefore invalidated)
  //   • unassigned slot                     → clear token+name
  //   • unchanged auditor                   → keep token        (already-shared links preserved)
  function ensureCoiTokens(j) {
    const mint = () => Math.random().toString(36).slice(2) + Date.now().toString(36)
    const tokens = { ...(j.auditor_coi_tokens || {}) }
    const names = { ...(j.auditor_coi_token_names || {}) }
    const sync = (slot, assignedId, name) => {
      if (!assignedId) { delete tokens[slot]; delete names[slot]; return }
      if (!tokens[slot] || names[slot] !== name) { tokens[slot] = mint(); names[slot] = name || null }
    }
    sync('auditor1', j.auditor_id, j.auditor_name)
    sync('auditor2', j.auditor2_id, j.auditor2_name)
    return { tokens, names }
  }

  async function sendAuditPlan() {
    const _md = mandayDeviation()
    if (planType === 'BLOCKED' || blockedReason) {
      toast('No ready-made schedule for this setup', 'warning', { detail: 'Change the audit structure, or edit the schedule yourself' })
      return
    }
    if (planFieldsDirty) {
      toast('Save the audit plan first', 'warning')
      return
    }
    if (!job.audit_date || !job.auditor_id) {
      toast('Set the audit date and auditor first', 'warning')
      return
    }
    // CT17 Lead Auditor enforcement — block sending if Auditor 1 isn't a Lead Auditor when required.
    const _a1 = allAuditors.find(a => a.id === job.auditor_id)
    if (calc?.requiresLeadAuditor && _a1 && _a1.grade !== 'Lead Auditor') {
      await logAction('Send audit plan blocked — Lead Auditor required', 'job', id, client?.name || job.quote_ref, { auditor: _a1.name, grade: _a1.grade, outcome: 'BLOCKED' })
      toast('This audit needs a Lead Auditor', 'warning', { detail: 'Over 200 employees — change Auditor 1' })
      return
    }
    const reqErrs = collectRequiredErrors()
    setFieldErrors(reqErrs)
    if (Object.keys(reqErrs).length) {
      toast('Fill in the red fields first', 'warning')
      return
    }
    // Block only on an UNRESOLVED high-risk conflict. A recorded "Proceed despite conflict" decision
    // (with justification) resolves it and Send is allowed (Policy A).
    if (coiConflictList().some(c => c.risk === 'High' && !c.resolved)) {
      await logAction('Send audit plan blocked — unresolved high-risk COI', 'job', id, client?.name || job.quote_ref, { auditor: job.auditor_name, outcome: 'BLOCKED' })
      toast('High-risk conflict not resolved', 'warning', { detail: 'Record "Proceed despite conflict" or replace the auditor' })
      return
    }

    // Check for medium COI risk — requires QM approval before proceeding
    const c1Risk = coi1?.risk_level || (coi1?.flagged ? 'Medium' : 'None')
    const c2Risk = coi2?.risk_level || (coi2?.flagged ? 'Medium' : 'None')
    if (c1Risk === 'Medium' || c2Risk === 'Medium') {
      const mediumAuditor = c1Risk === 'Medium' ? job.auditor_name : job.auditor2_name
      const { data: approvedEsc } = await supabase.from('qm_escalations')
        .select('id').eq('job_id', id).eq('escalation_type', 'coi_medium_risk').eq('status', 'Approved').maybeSingle()
      if (!approvedEsc) {
        // Create escalation if not already exists
        const { data: existingEsc } = await supabase.from('qm_escalations')
          .select('id').eq('job_id', id).eq('escalation_type', 'coi_medium_risk').eq('status', 'Pending').maybeSingle()
        if (!existingEsc) {
          await supabase.from('qm_escalations').insert({
            job_id: id,
            escalation_type: 'coi_medium_risk',
            auditor_name: mediumAuditor,
            issue_detail: `Medium COI risk declared by ${mediumAuditor}. QM approval required before audit proceeds.`,
            raised_by_name: profile?.name || 'Staff',
            raised_by_role: role || 'Staff',
          })
          await logAction('QM escalation raised — COI medium risk', 'job', id, client?.name || job.quote_ref, { auditor: mediumAuditor, outcome: 'ESCALATED' })
        }
        toast('Sent to the QM for approval', 'warning', { detail: `${mediumAuditor} declared a medium-risk conflict. You can send once approved.` })
        return
      }
    }

    // Validate auditor compliance before sending
    const { data: aud1Data } = await supabase.from('auditors').select('*').eq('id', job.auditor_id).maybeSingle()
    if (aud1Data) {
      if (aud1Data.approval_status !== 'Approved') {
        toast(`${aud1Data.name} is not approved yet`, 'warning', { detail: 'Get Quality Manager approval first' })
        return
      }
      // Scope check — uses SCOPE_REGISTRY canonical matching (no string comparison)
      const approvedScopes = aud1Data.approved_scopes || []
      const scopeResult = auditScopeCheck(approvedScopes, job.audit_type)
      if (!scopeResult.pass) {
        // Create QM escalation if not already raised
        const { data: existingEsc } = await supabase.from('qm_escalations')
          .select('id, status').eq('job_id', id).eq('escalation_type', 'scope_mismatch').eq('status', 'Pending').maybeSingle()
        if (!existingEsc) {
          await supabase.from('qm_escalations').insert({
            job_id: id,
            escalation_type: 'scope_mismatch',
            auditor_name: aud1Data.name,
            auditor_id: job.auditor_id,
            issue_detail: `${scopeResult.reason}. Job: ${job.audit_type}. Auditor scopes: ${approvedScopes.join(', ') || 'none set'}.`,
            raised_by_name: profile?.name || 'Staff',
            raised_by_role: role || 'Staff',
          })
          await logAction('QM escalation raised — scope mismatch', 'job', id, client?.name || job.quote_ref, {
            auditor: aud1Data.name, job_type: job.audit_type, approved_scopes: approvedScopes.join(', '), reason: scopeResult.reason,
          })
        }
        const { data: approvedEsc } = await supabase.from('qm_escalations')
          .select('id, status').eq('job_id', id).eq('escalation_type', 'scope_mismatch').eq('status', 'Approved').maybeSingle()
        if (!approvedEsc) {
          await logAction('Send audit plan escalated — scope mismatch', 'job', id, client?.name || job.quote_ref, { auditor: aud1Data.name, outcome: 'ESCALATED' })
          toast(`${aud1Data.name} isn't approved for this audit type`, 'warning', { detail: 'Sent to the QM for approval' })
          return
        }
      }

      if (aud1Data.wsh_auditor_expiry) {
        const days = Math.floor((new Date(aud1Data.wsh_auditor_expiry) - new Date()) / 86400000)
        if (days < 0) {
          toast(`${aud1Data.name}'s WSH Auditor approval has expired`, 'warning', { detail: 'Renew it before assigning' })
          return
        }
      }
      if (aud1Data.wsho_status && aud1Data.wsho_licence_expiry) {
        const days = Math.floor((new Date(aud1Data.wsho_licence_expiry) - new Date()) / 86400000)
        if (days < 0) {
          toast(`${aud1Data.name}'s WSHO licence has expired`, 'warning', { detail: 'Renew it before assigning' })
          return
        }
      }
    }

    const ackToken = Math.random().toString(36).slice(2) + Date.now().toString(36)
    // COI tokens come from the single source of truth: reuse existing tokens (so a link already copied
    // after Save keeps working) and refresh only when the assigned auditor changed. Each token stays
    // tied to its auditor via auditor_coi_token_names, which the COI page checks to reject stale links.
    const { tokens: coiTokens, names: coiTokenNames } = ensureCoiTokens(job)
    const coi1Token = coiTokens.auditor1
    const coi2Token = coiTokens.auditor2 || null

    const timeDisplay = `${startTime}-${getEndTime()}`

    // Save plan rows — use edited rows if available, else save auto-generated rows (stand-in names filled in)
    const rowsToSave = getDisplayRows()

    // Denormalise auditor emails onto the job so the public/client plan can render mailto links
    // (the public RPC returns the whole job row).
    const auditor1Email = allAuditors.find(a => a.id === job.auditor_id)?.email || null
    const auditor2Email = job.auditor2_id ? (allAuditors.find(a => a.id === job.auditor2_id)?.email || null) : null

    const sendUpdates = {
      client_ack_token: ackToken,
      auditor_coi_tokens: coiTokens,
      auditor_coi_token_names: coiTokenNames,
      audit_time_display: timeDisplay,
      start_time: startTime,
      num_locations: numLocations,
      auditor_email: auditor1Email,
      auditor2_email: auditor2Email,
      status: 'Scheduled',
      plan_sent_date: new Date().toISOString().split('T')[0],
      audit_plan_sent_at: new Date().toISOString(),   // latest send date+time; refreshed on every resend
      audit_objective: auditObjective,
      audit_criteria: auditCriteriaField,
      audit_scope_text: auditScopeField,
      project_name: projectName,
      audit_approach_note: auditApproachNote.trim() || null,
      application_type: applicationType || null,
      report_reference_no: reportRef.trim() || null,
      rm_leader_name: rmLeaderName.trim(),
      rm_leader_phone: rmLeaderPhone.trim(),
      audit_site_address: oneLine(auditSiteAddress).trim(),
      checklist_url: checklistUrl,
      audit_structure: auditStructure,
      half_day_position: halfDayPosition,
      second_auditor_block: secondAuditorBlock,
      audit_date_day2: auditDateDay2 || null,
      day2_start: day2Start,
      planning_num_employees: (planningEmployees === '' || planningEmployees == null) ? null : parseInt(planningEmployees),
      planning_basis_reason: planningDiffers() ? planningBasisReason.trim() : null,
      manday_justification: _md.changed ? ((mandayJustification || job?.manday_justification || '').trim() || null) : null,
      plan_rows_edited: rowsToSave,
      plan_rows_manually_edited: manuallyEdited,
    }
    let { error: planErr } = await supabase.from('jobs').update(sendUpdates).eq('id', id)
    // Same resilience as save: never let an unmigrated optional column block sending the plan.
    if (planErr && /planning_(num_employees|basis_reason)|audit_structure|half_day_position|second_auditor_block|audit_date_day2|day2_start|auditor2?_email|auditor_coi_token_names|plan_rows_manually_edited|could not find|does not exist|schema cache/i.test(planErr.message || '')) {
      const core = { ...sendUpdates }
      delete core.planning_num_employees; delete core.planning_basis_reason; delete core.audit_structure; delete core.plan_rows_manually_edited
      delete core.half_day_position; delete core.second_auditor_block; delete core.audit_date_day2; delete core.day2_start
      delete core.auditor_email; delete core.auditor2_email; delete core.auditor_coi_token_names
      delete core.audit_plan_sent_at   // optional column; strip if not migrated yet (display falls back to plan_sent_date)
      const retry = await supabase.from('jobs').update(core).eq('id', id)
      planErr = retry.error
    }
    if (planErr) { failed("Couldn't send the audit plan", planErr.message); return }
    setJob(j => ({ ...j, plan_rows_edited: rowsToSave }))
    if (editedRows && rowsToSave !== editedRows) setEditedRows(rowsToSave)
    setJob(j => ({ ...j, client_ack_token: ackToken, auditor_coi_tokens: coiTokens, status: 'Scheduled', audit_plan_sent_at: sendUpdates.audit_plan_sent_at }))
    if (stateNeedsSync()) await syncReviewFromDb()
    await logAction('Audit plan sent', 'job', id, client?.name || job.quote_ref, {
      auditor: job.auditor_name,
      audit_date: job.audit_date,
      quote_ref: job.quote_ref,
    })

    // NOTE: auditor maintenance-log rows are NO LONGER created here. Sending/scheduling a plan is not a
    // finalised audit (clients postpone or cancel, auditors change, reports get rejected). The countable
    // auditor_audit_log row is created ONLY at FINALISATION — when office records the new bizSAFE expiry —
    // by trg_client_expiry_closes_audit (FINALISATION_AUDIT_LOG.sql), with role from the roster grade.

    const ackUrl = `${window.location.origin}/ack/${ackToken}`
    const coi1Url = `${window.location.origin}/coi/${coi1Token}`
    const coi2Url = coi2Token ? `${window.location.origin}/coi/${coi2Token}` : null
    const auditDateFmt = format(parseISO(job.audit_date), 'd MMM yyyy')
    const planText = buildAuditPlanText()
    const aud1Email = allAuditors.find(a => a.id === job.auditor_id)?.email || ''

    const clientTmpl = applyTemplate(getTemplate('auditPlanClient'), {
      CLIENT_NAME: client?.contact_name || client?.name || '',
      AUDIT_DATE: auditDateFmt,
      AUDIT_TIME: timeDisplay,
      AUDIT_SITE: oneLine(auditSiteAddress) || 'TBD',   // Audit SITE address (never HQ) — pulled from the plan field
      AUDIT_PLAN: planText,                    // still passed for any custom template that references it
      ACK_LINK: ackUrl,
      COP_LINK: checklistUrl || DEFAULT_CHECKLIST_URL,
      FIRM_NAME: FIRM_NAME(),
    })

    const audTmpl = applyTemplate(getTemplate('auditPlanAuditor'), {
      AUDITOR_NAME: job.auditor_name || '',
      CLIENT_NAME: client?.name || '',
      AUDIT_TYPE: job.audit_type || '',
      AUDIT_DATE: auditDateFmt,
      AUDIT_TIME: timeDisplay,
      AUDIT_SITE: oneLine(auditSiteAddress) || '',
      COI_LINK: coi1Url,
      FIRM_NAME: FIRM_NAME(),
    })

    // Client audit-plan acknowledgement — auto-copy the ACK link for quick pasting, AND open Outlook pre-filled with
    // the full plan email so you can copy the whole message straight into your own client thread. Client-facing →
    // manual send (the trail stays in your mailbox); the client reviews the plan & acknowledges on the linked page.
    // Copy a FORMATTED (rich-HTML) audit plan to the clipboard so pasting into the client's email thread keeps the
    // bold headings, single line-spacing and nested bullets. No Outlook draft is opened — the office pastes into the
    // existing thread. Plain-text fallback also placed on the clipboard for editors that don't accept HTML.
    const clientHtml = buildAuditPlanHtml({
      greet: client?.contact_name || client?.name || '', auditDate: auditDateFmt, auditTime: timeDisplay,
      auditSite: oneLine(auditSiteAddress) || 'TBD', ackUrl, copLink: checklistUrl || DEFAULT_CHECKLIST_URL, firm: FIRM_NAME(),
    })
    let planCopied = false
    try {
      await navigator.clipboard.write([new window.ClipboardItem({
        'text/html': new Blob([clientHtml], { type: 'text/html' }),
        'text/plain': new Blob([clientTmpl.body], { type: 'text/plain' }),
      })])
      planCopied = true
    } catch { try { await navigator.clipboard.writeText(clientTmpl.body); planCopied = true } catch {} }
    if (planCopied) toast('Audit plan copied', 'success', { detail: 'Paste it into the client’s email (Ctrl+V)' })
    else failed("Couldn't copy the audit plan")

    // COI to the auditor(s) — auto-sent internally via Resend (auditors are internal; recipient can be any domain,
    // incl. gmail/hotmail). Best-effort: never blocks (the plan is already saved). The plan message stays on the
    // clipboard regardless — we never overwrite it.
    const toHtml = (t) => textEmailHtml({ title: 'Audit assignment', text: t, buttonLabel: 'Complete declaration of impartiality →' })
    const coiFails = []
    const sendCoi = async (email, tmpl, who) => {
      if (!email) { coiFails.push(who); return }
      try {
        const { error } = await supabase.functions.invoke('send-internal-email', {
          body: { to: email, subject: tmpl.subject, html: toHtml(tmpl.body) },
        })
        if (error) coiFails.push(who)
      } catch { coiFails.push(who) }
    }

    await sendCoi(aud1Email, audTmpl, job.auditor_name || 'Auditor 1')
    if (coi2Url) {
      const aud2Email = allAuditors.find(a => a.id === job.auditor2_id)?.email || ''
      const aud2Tmpl = applyTemplate(getTemplate('auditPlanAuditor'), {
        AUDITOR_NAME: job.auditor2_name || '',
        CLIENT_NAME: client?.name || '',
        AUDIT_TYPE: job.audit_type || '',
        AUDIT_DATE: auditDateFmt,
        AUDIT_TIME: timeDisplay,
        AUDIT_SITE: oneLine(auditSiteAddress) || '',
        COI_LINK: coi2Url,
        FIRM_NAME: FIRM_NAME(),
      })
      await sendCoi(aud2Email, aud2Tmpl, job.auditor2_name || 'Auditor 2')
    }

    if (coiFails.length) {
      toast(`COI form wasn't emailed to ${coiFails.join(', ')}`, 'error', { detail: 'Send it to them yourself' })
    } else {
      toast('COI form sent to the auditor', 'success')
    }
  }

  async function resolveAlert(alertId) {
    await supabase.from('admin_alerts').update({ resolved: true }).eq('id', alertId)
    setAlerts(as => as.filter(a => a.id !== alertId))
  }

  if (loading) return <div style={{ color: 'var(--text3)' }}>Loading…</div>
  if (!job) return null

  // Match both legacy and current audit type strings
  const needsBizSAFEOrSHMS = !!(job.audit_type) && (
    job.audit_type === AUDIT_TYPES.BIZSAFE ||
    job.audit_type === AUDIT_TYPES.SHMS ||
    job.audit_type?.includes('bizSAFE') ||
    job.audit_type?.includes('SHMS') ||
    job.audit_type === 'Construction Worksite' ||
    job.audit_type === 'Metalworking Industry' ||
    job.audit_type === 'Shipyard' ||
    job.audit_type?.includes('Oil Refinery') ||
    job.audit_type?.includes('Semiconductor') ||
    job.audit_type?.includes('Chemical') ||
    job.audit_type?.includes('Pharmaceutical') ||
    job.audit_type === 'ISO Internal Audit'
  )
  // Auditor eligibility: approved + certs valid + scope matches job type
  const today = new Date()
  const isApproved = a => {
    if (a.approval_status !== 'Approved') return false
    if (a.wsh_auditor_expiry && new Date(a.wsh_auditor_expiry) < today) return false
    if (a.wsho_status && a.wsho_licence_expiry && new Date(a.wsho_licence_expiry) < today) return false
    return true
  }
  // Use canonical category matching — robust against string differences and legacy values
  const scopeMatches = allAuditors.filter(a => isApproved(a) && auditorMatchesJobType(a.approved_scopes, job.audit_type))
  // Fallback: if zero match, show ALL approved auditors with a warning (never leave dropdown empty)
  const eligibleAuditors = scopeMatches.length > 0 ? scopeMatches : allAuditors.filter(isApproved)
  const eligibilityWarning = scopeMatches.length === 0 && allAuditors.filter(isApproved).length > 0
  // CT17 Lead Auditor enforcement (>200 employees). `calc` is recomputed live on load and after
  // questionnaire/CT17 changes, so this filtering reacts to current job data, not stale values.
  const requiresLead = !!calc?.requiresLeadAuditor
  // SINGLE SOURCE OF TRUTH for "auditor 2 is effectively assigned": a DISTINCT id that resolves to a real
  // auditor. A stale duplicate of auditor 1, OR an id that no longer matches any auditor (so the select
  // shows "None"), does NOT count. Every 2-auditor decision — the count, the select's value, the blocked
  // warning, and what gets saved — derives from this, so the UI and the logic can never disagree.
  const auditor2Resolved = (job.auditor2_id && job.auditor2_id !== job.auditor_id)
    ? (allAuditors.find(a => a.id === job.auditor2_id) || null) : null
  const hasAuditor2 = !!auditor2Resolved
  // 2 auditors with an unsupported man-day count (< 1.0, i.e. 0.5, or > 2.0) → the Schedule block shows
  // its own blocked notice, so suppress the duplicate lower-section notice for exactly those cases.
  const blocked2Aud = hasAuditor2 && (parseFloat(job.mandays) || 0) > 0 && ((parseFloat(job.mandays) || 0) < 1 || (parseFloat(job.mandays) || 0) > 2)
  const auditor1Options = requiresLead ? eligibleAuditors.filter(a => a.grade === 'Lead Auditor') : eligibleAuditors
  const assignedAuditor1 = allAuditors.find(a => a.id === job.auditor_id)
  // True when a non-Lead auditor is currently assigned to slot 1 but a Lead is required.
  const leadAuditorViolation = requiresLead && !!job.auditor_id && !!assignedAuditor1 && assignedAuditor1.grade !== 'Lead Auditor'
  const noLeadAvailable = requiresLead && auditor1Options.length === 0
  // Current-state only: a COI counts for a slot solely if it was declared by the auditor CURRENTLY
  // assigned to it. A stale declaration from a previously-assigned auditor is ignored automatically,
  // so changing the auditor (or changing back) is always reflected without tracking change events.
  const coi1 = coiResponses.find(r => r.auditor_slot === 'auditor1' && r.auditor_name === job.auditor_name)
  const coi2 = coiResponses.find(r => r.auditor_slot === 'auditor2' && r.auditor_name === job.auditor2_name)

  // COI gate: all assigned auditors must have submitted COI with no conflict
  // High risk = hard block. Medium risk = QM escalation required.
  const coi1Risk = coi1?.risk_level || (coi1?.flagged ? 'Medium' : null)
  const coi2Risk = coi2?.risk_level || (coi2?.flagged ? 'Medium' : null)
  const coi1Pending = job.auditor_id && !coi1
  const coi2Pending = job.auditor2_id && !coi2
  const anyCoiPending = coi1Pending || coi2Pending
  const anyHighRisk = coi1Risk === 'High' || coi2Risk === 'High'
  const anyMediumRisk = coi1Risk === 'Medium' || coi2Risk === 'Medium'
  const coiConflicts = coiConflictList()
  // Policy A: a High-risk COI blocks ONLY while unresolved. A recorded 'Proceed despite conflict' decision
  // (with justification) resolves it — Send, the status label and the banner then treat it as cleared.
  const highRiskUnresolved = coiConflicts.some(c => c.risk === 'High' && !c.resolved)
  // COI is NOT a pre-send blocker. "Awaiting declaration" is informational. Only an UNRESOLVED High-risk
  // response hard-blocks; a recorded Proceed clears it to "Resolved"; Medium triggers QM escalation.
  const coiStatusLabel = highRiskUnresolved ? 'Submitted – conflict raised (blocked)'
    : anyHighRisk ? 'Resolved – proceed with justification'
    : anyMediumRisk ? 'Submitted – flagged (QM review)'
    : (anyCoiPending && (coi1 || coi2)) ? 'Awaiting declaration (partial)'
    : anyCoiPending ? 'Awaiting declaration'
    : (coi1 || coi2) ? 'Submitted – no issue' : 'Not sent'
  // Acknowledgement is valid only while the plan signature it was given against still matches the
  // CURRENT plan signature. Both are DB-computed (plan_signature is a generated column), so reverting
  // the plan to its prior values restores the match — no JS hashing, no version history.
  const ackValid = !!(job.ack_signature && job.plan_signature && job.ack_signature === job.plan_signature)
  const hqAddress = client?.address || job.form_data?.company_address || ''
  const displayRows = getDisplayRows()
  // Schedule layout: 2-auditor schedules have parallel rows; shared rows must use the SAME column
  // grid so Lunch/Opening/Closing etc. align with the parallel Lead/Auditor columns.
  const schedHasParallel = displayRows.some(r => r.parallel)
  const SCHED_GRID = schedHasParallel ? '84px 1fr 1fr 22px' : '84px 1fr 168px 22px'
  // Compact, single-row-tall editable cell: shows one line, scrolls internally, manually resizable.
  const cellTA = { fontSize: 13, padding: '6px 8px', width: '100%', resize: 'vertical', lineHeight: 1.4, height: '32px', minHeight: '32px', overflowY: 'auto', whiteSpace: 'pre-wrap' }

  // Parse the stored CT17 complexity classification (JSON string or object) → { complexity, override_reason,
  // determined_by, determined_at, reason } or null. Rendered inline in the CT17 basis line, not as a banner.
  function parseComplexity() {
    const cc = job?.complexity_classification
    if (!cc) return null
    try { return typeof cc === 'string' ? JSON.parse(cc) : cc } catch (e) { return null }
  }

  // Compact COI status line for the Assigned-auditor panel. Mirrors coiBadge's resolution rule: a
  // recorded 'Proceed' decision for this slot+auditor flips High/Medium to "Resolved — Proceed…".
  // Condensed one-line COI status for the planning workspace: Outstanding / Cleared <when> [View] / Flagged [View].
  // Details live behind [View] (the declaration page). Pre-send Outstanding shows no link (COI is issued on Send).
  function coiLine(slot, coi, name) {
    const token = job?.auditor_coi_tokens?.[slot]
    const fresh = !!token && job?.auditor_coi_token_names?.[slot] === name
    const view = token ? <button className="sm" style={{ marginLeft: 8, fontSize: 13 }} onClick={() => window.open(`${window.location.origin}/coi/${token}`, '_blank')}>View</button> : null
    let body
    if (coi) {
      const risk = coi.risk_level || (coi.flagged ? 'Medium' : 'None')
      const flagged = risk === 'High' || risk === 'Medium' || coi.flagged
      body = flagged
        ? <><span style={{ color: 'var(--danger)', fontWeight: 600 }}>Flagged</span>{view}</>
        : <><span style={{ fontWeight: 600 }}>Cleared</span>{coi.submitted_at && <span style={{ color: 'var(--text2)' }}> {format(parseISO(coi.submitted_at), 'd MMM yyyy, HH:mm')}</span>}{view}</>
    } else {
      body = <><span style={{ color: 'var(--text2)' }}>Outstanding</span>{fresh && <button className="sm" style={{ marginLeft: 8, fontSize: 13 }} onClick={() => navigator.clipboard.writeText(`${window.location.origin}/coi/${token}`)}>Copy link</button>}</>
    }
    return <div style={{ fontSize: 13, marginTop: 6 }}><span style={{ color: 'var(--text2)' }}>COI: </span>{body}</div>
  }

  // Cancel editing: discard unsaved local edits by re-reading persisted values, then return to view.
  async function cancelEdit() { await loadJob(); setMode('view') }
  // Save from the header uses the EXISTING save logic unchanged, then returns to view mode.
  async function saveAndView() { await saveAuditPlanFields(); setMode('view') }

  // ── Cancel contract / Reopen (same flow as Internal Audit — lib/contractCancel.js) ──
  // Cancels the job AND its signed quotation in one step, with the reason (who / when) in the job notes and the
  // audit trail. Only before the auditor submits a report, and never for a job inside a combined audit.
  const cancelNote = lastCancelNote(job?.notes)
  const canCancel = !!job && job.id !== 'new' && job.status !== 'Cancelled' && job.status !== 'Completed'
    && !job.report_submitted && (!job.workflow_state || job.workflow_state === 'Draft')
  async function cancelJob() {
    if (job.combined_group_id) return toast('This job is part of a combined audit', 'warning', { detail: 'Unlink the other companies first, then cancel' })
    const why = window.prompt('Cancel this contract? The job and its signed quotation will be cancelled.\n\nPlease give a reason:')
    if (why === null) return
    if (!why.trim()) return toast('Enter a reason', 'warning')
    try {
      const r = await cancelContract({ job, reason: why.trim(), by: profile?.name })
      await logAction('Contract cancelled', 'job', id, client?.name || job.quote_ref, { reason: why.trim(), previous_status: job.status, quotations: r.quoteRefs })
      toast('Contract cancelled', 'success', { detail: 'Let the auditor, Accounts and the client know' })
    } catch (e) { failed("Couldn't cancel", e.message) }
    await loadJob(); setMode('view')
  }
  async function reopenJob() {
    if (!window.confirm('Reopen this job? Its quotation goes back to Signed.')) return
    const back = job.audit_plan_sent_at || job.plan_sent_date ? 'Scheduled' : 'Confirmed'
    try {
      const r = await reopenContract({ job, status: back, by: profile?.name })
      await logAction('Contract reopened', 'job', id, client?.name || job.quote_ref, { status: back, quotations: r.quoteRefs })
      toast('Job reopened', 'success')
    } catch (e) { failed("Couldn't reopen", e.message) }
    await loadJob()
  }

  // ── Plan & Prepare readiness (presentation-only; reuses collectRequiredErrors, no new logic) ──
  // The same required-field checks that gate Send, surfaced calmly: a per-field marker + a quiet count.
  const reqErrors = collectRequiredErrors()
  const planMissing = [
    !job?.auditor_id && 'auditor',
    reqErrors.audit_date && 'audit date',
    reqErrors.startTime && 'start time',
    reqErrors.rmLeaderName && 'RM Leader name',
    reqErrors.rmLeaderPhone && 'RM Leader mobile',
    reqErrors.auditSiteAddress && 'audit site',
    reqErrors.mandayJustification && 'audit duration justification',
    reqErrors.planningBasisReason && 'planning basis reason',
    reqErrors.auditDateDay2 && 'Day 2 date',
    highRiskUnresolved && 'resolve the High-risk COI',
  ].filter(Boolean)
  const planSendDisabled = planMissing.length > 0 || planFieldsDirty
  // The same checks, named as the fields appear on the page, in page order — for the "Still needed" line only.
  const planMissingLabels = [
    reqErrors.auditSiteAddress && 'Audit site address',
    reqErrors.rmLeaderName && 'RM Leader name',
    reqErrors.rmLeaderPhone && 'RM Leader mobile',
    !job?.auditor_id && 'Auditor 1',
    reqErrors.mandayJustification && 'Audit duration justification',
    reqErrors.planningBasisReason && 'Planning-basis reason',
    reqErrors.audit_date && 'Day 1 date',
    reqErrors.startTime && 'Start',
    reqErrors.auditDateDay2 && 'Day 2 date',
    highRiskUnresolved && 'Resolve the High-risk COI',
  ].filter(Boolean)
  // The checks "Send audit plan" makes on Auditor 1, shown as soon as the auditor is picked. Display only: Send
  // still runs its own checks (and raises the QM escalations) exactly as before.
  const auditor1Issues = (() => {
    const a = allAuditors.find(x => x.id === job?.auditor_id)
    if (!a) return []
    const out = []
    if (a.approval_status !== 'Approved') out.push(`${a.name} is not approved yet — get Quality Manager approval first`)
    else if (!auditScopeCheck(a.approved_scopes || [], job.audit_type).pass) out.push(`${a.name} isn't approved for this audit type — sending will ask the QM for approval`)
    const expired = d => !!d && Math.floor((new Date(d) - new Date()) / 86400000) < 0
    if (expired(a.wsh_auditor_expiry)) out.push(`${a.name}'s WSH Auditor approval has expired — renew it before assigning`)
    if (a.wsho_status && expired(a.wsho_licence_expiry)) out.push(`${a.name}'s WSHO licence has expired — renew it before assigning`)
    return out
  })()
  // Quiet inline "required, still empty" marker — a small dot, not a banner.
  const miss = k => reqErrors[k] ? <span title="Required" style={{ color: 'var(--danger)', marginLeft: 5, fontWeight: 700 }}>•</span> : null

  // ── Workflow tabs (presentational only — derived from existing state, no logic change) ──
  const ws = job ? workflowState(job) : null
  const defaultTab = (ws === 'Completed' || ws === 'Pending WSHC Outcome') ? 'finalise'
    : (ws === 'Pending Office Review' || ws === 'Returned for Revision' || job?.report_submitted) ? 'report'
    : 'plan'
  const planDot = !!(needsBizSAFEOrSHMS && job && !(job.plan_sent_date || job.audit_plan_sent_at))
  const validateDot = !!((mpReport?.manpower_control?.mismatch && !mpReport?.manpower_decision)
    || (coiConflicts.length > 0))
  const reportDot = ws === 'Pending Office Review' || ws === 'Returned for Revision'
  const finaliseDot = ws === 'Pending WSHC Outcome'
  // "Validate & Send" only carries COI decisions + manpower-mismatch decisions, both conditional. Hide the
  // whole tab when there is nothing to validate, so it's never a dead/blank tab.
  const validateHasContent = coiConflicts.length > 0 || (!!mpReport?.manpower_control?.mismatch && role !== 'Auditor')
  const JTABS = [
    ['plan', 'Plan & Prepare', planDot],
    ...(validateHasContent ? [['validate', 'Validate & Send', validateDot]] : []),
    ['report', 'Audit & Report', reportDot],
    ['finalise', 'Finalise', finaliseDot],
  ]
  // Never land on a hidden tab — fall back to the default for this workflow state.
  const tab = (jtab && JTABS.some(t => t[0] === jtab)) ? jtab : defaultTab


  return (
    <div>
      {/* Report viewer — top-level so "View report" in the command bar works from any tab. */}
      {showPackage && <PackageViewer jobId={id} job={job} client={client} onClose={() => setShowPackage(false)} />}
      {/* Hidden background preparer — mounts when the self-heal effect (or office Retry) needs to bring the
          report's prepared copy up to date; unmounts when done. */}
      {genKey > 0 && (
        <PackageGenerator key={genKey} jobId={id} job={job} client={client}
          onComplete={async (res) => {
            setGenKey(0)
            await loadReportAck()   // refresh readiness for the report-tab status line
            if (res && !res.ok && res.error && res.error !== 'disabled') toast('Report preparation failed', 'error', { detail: 'Retry from the report tab' })
          }} />
      )}
      {job.qm_clearance && job.status !== 'Scheduled' && job.status !== 'Completed' && (
        <div style={{ background: '#EAF3DE', border: '1px solid #639922', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
          <strong><span style={{ color: 'var(--brand)' }}>✓</span> QM clearance granted</strong> — all escalations resolved. You can now send the audit plan.
        </div>
      )}
      <div className="page-header">
        <div>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 4 }}>
            <span style={{ cursor: 'pointer' }} onClick={() => navigate('/jobs')}>Jobs</span> /
          </div>
          <h1>{client?.name || 'Job detail'}</h1>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {mode === 'view'
            ? <span className="badge badge-gray" style={{ fontSize: 13 }}>{job.status}</span>
            : (
              <select value={job.status} onChange={e => updateField('status', e.target.value)} style={{ fontSize: 13, width: 'auto' }}>
                {/* Cancelling goes through "Cancel job" (asks for the reason) — not this list. */}
                {STATUSES.filter(s => s !== 'Cancelled' || job.status === 'Cancelled').map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
          {mode === 'view'
            ? job.status === 'Cancelled'
              ? <button className="sm" onClick={reopenJob}>Reopen</button>
              : <>
                  <button className="primary sm" onClick={() => { setJtab('plan'); setMode('edit') }}>Edit</button>
                  {canCancel && <button className="sm" onClick={cancelJob}>Cancel contract</button>}
                </>
            : (
              <>
                {/* Header stays quiet: the loud Save/Send live in the Plan the audit action area. */}
                <button className="sm" onClick={saveAndView} style={{ background: 'none', color: 'var(--text2)' }}>Save &amp; close</button>
                <button className="sm" onClick={cancelEdit}>Cancel</button>
              </>
            )}
        </div>
      </div>

      {/* ── Sticky command bar: workflow state · owner · next action · go-to ── */}
      {job.id !== 'new' && (() => {
        const cancelled = job.status === 'Cancelled'
        const ds = ws && !cancelled ? displayState(job) : null
        const pText = cancelled ? null : portalText   // new WSHC-portal process wording (null = usual)
        const chip = pText ? portalChip(pText, job) : ds ? stateChip(ds) : { bg: '#f0f0f0', color: '#555' }
        const stateLabel = pText || ds || job.status
        const nextAction = cancelled ? null
          : pText ? portalNextAction(job, client?.bizsafe_expiry_date)
          : ws ? nextActionOf(job, reportAckValid)
          : !job.client_ack_token ? 'Complete planning & send the audit plan'
          // After the (last) audit day the audit is done — the report is what we're waiting for (same words as the card below).
          : (job.audit_date_day2 || job.audit_date) && (job.audit_date_day2 || job.audit_date).slice(0, 10) < todaySg() ? 'Waiting for the auditor’s report'
          : 'Awaiting audit'
        return (
          <div style={{ position: 'sticky', top: 0, zIndex: 20, background: 'var(--bg)', borderBottom: '1px solid var(--border)', padding: '8px 0 10px', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, padding: '4px 12px', borderRadius: 20, fontWeight: 600, background: chip.bg, color: chip.color }}>{stateLabel}</span>
            {ws && ownerOf(job) && <span style={{ fontSize: 14, color: 'var(--text2)' }}>Owner: <strong>{ownerOf(job)}</strong>{job.assigned_to_id && job.assigned_to_id === profile?.id && ownerOf(job) === job.assigned_to_name ? ' (you)' : ''}</span>}
            {nextAction && <span style={{ fontSize: 14, color: 'var(--text)' }}>Next: <strong>{nextAction}</strong></span>}
            {/* Reference action, attached to the workflow context — sits right after "Next", not floating far-right. */}
            {ws && <button className="sm" onClick={openReport}>📄 View report</button>}
            {cancelled && cancelNote && <span style={{ fontSize: 14, color: 'var(--text2)' }}>{cancelNote}</span>}
          </div>
        )
      })()}

      {/* ── Workflow tabs ── */}
      <div className="page-tabs">
        {JTABS.map(([v, l, dot]) => (
          <button key={v} onClick={() => setJtab(v)} className={tab === v ? 'page-tab active' : 'page-tab'}>
            {l}{dot && <span title="Action pending" className="page-tab-dot" />}
          </button>
        ))}
      </div>

      {/* View/Edit toggle (UI only): in view mode the .view-mode class makes INPUTS non-interactive
          (pointer-events:none) and renders them as plain text, while BUTTONS/LINKS stay clickable.
          No fieldset[disabled] — so View declaration / View certificate / Copy link remain usable. */}
      <fieldset className={mode === 'view' ? 'view-mode' : undefined}
        style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}>
      <div className="jd-main-grid">
      <div className="jd-main jd-rows" style={{ minWidth: 0 }}>
      {alerts.map(alert => (
        <div key={alert.id} style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 'var(--radius)', padding: '12px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, fontSize: 14, color: 'var(--danger)' }}>⚠️ {alert.message}</div>
          <button className="sm danger" onClick={() => resolveAlert(alert.id)}>Dismiss</button>
        </div>
      ))}

      {/* ── COI conflict decision (proceed-with-justification or replace) ── */}
      {tab === 'validate' && coiConflicts.length > 0 && (
        <div className="card" style={{ marginBottom: 12, border: '1px solid #E24B4A' }}>
          <h2 style={{ marginBottom: 4, color: '#791F1F' }}>Conflict of interest decision</h2>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
            A conflict of interest has been declared. Please decide whether to proceed.
          </p>
          {coiConflicts.map(c => {
            const existing = coiDecisions.find(d => d.auditor_slot === c.slot && d.auditor_name === c.name && d.decision === 'Proceed')
            const form = coiDecisionForm[c.slot] || {}
            if (existing) return (
              <div key={c.slot} style={{ padding: '10px 14px', background: '#EAF3DE', borderRadius: 8, border: '1px solid #639922', marginBottom: 8, fontSize: 14 }}>
                <div style={{ fontWeight: 500 }}>Decision: Proceed despite conflict — {c.name} ({c.risk} risk)</div>
                <div style={{ marginTop: 4 }}>Justification: {existing.justification}</div>
                <div style={{ fontSize: 14, color: 'var(--text2)', marginTop: 4 }}>
                  Recorded by {existing.decided_by_name} ({existing.decided_by_role}){existing.created_at ? ` on ${format(parseISO(existing.created_at), 'd MMM yyyy, HH:mm')}` : ''}
                </div>
              </div>
            )
            return (
              <div key={c.slot} style={{ padding: '12px 14px', background: '#FDECEA', borderRadius: 8, border: '1px solid #E24B4A', marginBottom: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: '#791F1F', marginBottom: 8 }}>
                  {c.name} declared a {c.risk}-risk conflict of interest.
                </div>
                {mode === 'view' ? (
                  // View mode = fully read-only. No clickable radios/buttons; recording happens in Edit mode.
                  <div style={{ fontSize: 14, color: '#791F1F' }}>
                    Switch to <strong>Edit</strong> mode to record a decision (proceed with justification, or replace the auditor).
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: 13 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <input type="radio" name={`coi-dec-${c.slot}`} checked={form.choice === 'Proceed'}
                          onChange={() => setCoiDecisionForm(f => ({ ...f, [c.slot]: { ...f[c.slot], choice: 'Proceed' } }))} style={{ width: 'auto' }} />
                        Proceed despite conflict
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <input type="radio" name={`coi-dec-${c.slot}`} checked={form.choice === 'Replace'}
                          onChange={() => setCoiDecisionForm(f => ({ ...f, [c.slot]: { ...f[c.slot], choice: 'Replace' } }))} style={{ width: 'auto' }} />
                        Do not proceed (replace auditor)
                      </label>
                    </div>
                    {form.choice === 'Proceed' && (
                      <div className="field field-vert">
                        <label style={{ color: 'var(--danger)' }}>Justification *<span className="lbl-hint">Required to proceed with this auditor</span></label>
                        <textarea rows={2} value={form.justification || ''}
                          onChange={e => setCoiDecisionForm(f => ({ ...f, [c.slot]: { ...f[c.slot], justification: e.target.value } }))}
                          placeholder="Explain why it is acceptable to proceed with this auditor despite the declared conflict…" />
                        <button className="primary sm" disabled={savingCoiDecision || !(form.justification || '').trim()}
                          onClick={() => recordCoiProceed(c)}>Record decision</button>
                      </div>
                    )}
                    {form.choice === 'Replace' && (
                      <div>
                        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>
                          The auditor will be unassigned. Select a replacement under “Assigned auditor(s)”, then re-send the audit plan.
                        </p>
                        <button className="sm danger" disabled={savingCoiDecision} onClick={() => recordCoiReplace(c)}>
                          Confirm — unassign &amp; replace
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}

      {tab === 'plan' && (
      <div className="plan-cards">

          {/* ── 1 · Client & quotation: read-only reference ── */}
          <div className="card">
            <h2 className="card-title">Client &amp; quotation</h2>
            <div className="info-rows">
              <div className="info-row"><div className="info-label">Contact</div>
                <div>{client?.contact_name || job.form_data?.pic_name || '—'}
                {(() => {
                  const ph = client?.contact_phone || job.form_data?.pic_phone
                  return ph ? <> · <a href={`https://teams.microsoft.com/l/call/0/0?users=4:${String(ph).replace(/[^\d+]/g, '')}`} title="Call via Microsoft Teams" style={{ color: 'var(--brand)', textDecoration: 'none', fontWeight: 600 }}>{ph}</a></> : null
                })()}
                {(client?.contact_email || job.form_data?.pic_email) ? ` · ${client?.contact_email || job.form_data?.pic_email}` : ''}</div></div>
              <div className="info-row"><div className="info-label">Quotation</div>
                <div>{(() => {
                  const signedDate = job.signed_date || signedQuote?.signed_date
                  const token = job.signature_token || signedQuote?.signature_token
                  if (!signedDate && !token) return '—'
                  return <>{signedDate ? `Signed ${format(parseISO(signedDate), 'd MMM yyyy')}` : 'Signed'}
                    {token && <button className="sm" style={{ marginLeft: 8 }} onClick={() => window.open(`${window.location.origin}/sign/${token}`, '_blank')}>View agreement</button>}</>
                })()}</div></div>
              <div className="info-row"><div className="info-label">HQ address</div><div>{hqAddress || '—'}</div></div>
              <div className="info-row"><div className="info-label">Business nature · UEN</div><div>{job.form_data?.business_nature || client?.notes || '—'} · {client?.uen || '—'}</div></div>
            </div>
          </div>

          {/* ── 2 · Audit plan: everything the office fills in, in the order it is decided ── */}
          <div className="card plan-form">
            <h2 className="card-title">Audit plan</h2>

            {calc?.requiresLeadAuditor && (
              <div style={{ fontSize: 13, color: '#854F0B', marginBottom: 8 }}>CT17: Lead Auditor required (&gt;200 employees).</div>
            )}
            {eligibilityWarning && (
              <div style={{ fontSize: 13, color: '#854F0B', marginBottom: 8, padding: '6px 10px', background: '#FFF3E0', borderRadius: 6 }}>
                ⚠ No auditor has this scope approved — showing all approved auditors.
              </div>
            )}
            {allAuditors.length > 0 && allAuditors.filter(isApproved).length === 0 && (
              <div style={{ fontSize: 13, color: '#791F1F', marginBottom: 8, padding: '6px 10px', background: '#FDECEA', borderRadius: 6 }}>
                No approved auditors. Approve auditors in Personnel first.
              </div>
            )}

            {/* Audit site: NEVER filled in from HQ — typed, or copied with "Copy HQ address" (explicit action only). */}
            <div className="field">
              <label style={{ ...errLabel('auditSiteAddress') }}>
                <span>Audit site address *{miss('auditSiteAddress')}</span>
                {hqAddress && <span className="edit-only" style={{ color: 'var(--brand)', cursor: 'pointer', fontWeight: 400 }}
                  onClick={() => { setAuditSiteAddress(oneLine(hqAddress)); setCopiedFromHq(true); clearErr('auditSiteAddress'); setPlanFieldsDirty(true) }}>Copy HQ address{copiedFromHq ? ' ✓' : ''}</span>}
              </label>
              {mode === 'view'
                ? <div className="plan-text">{oneLine(auditSiteAddress) || '—'}</div>
                : <input value={oneLine(auditSiteAddress)} style={errInput('auditSiteAddress')} onChange={e => { setAuditSiteAddress(oneLine(e.target.value)); setCopiedFromHq(false); clearErr('auditSiteAddress'); setPlanFieldsDirty(true) }} placeholder={mode === 'view' ? '—' : 'Enter audit site or copy from HQ'} />}
            </div>
            <div className="field">
              <label style={errLabel('rmLeaderName')}>RM Leader name *{miss('rmLeaderName')}</label>
              <input value={rmLeaderName} style={errInput('rmLeaderName')} onChange={e => { setRmLeaderName(e.target.value); clearErr('rmLeaderName'); setPlanFieldsDirty(true) }} placeholder="Full name of RM Leader" />
            </div>
            <div className="field">
              <label style={errLabel('rmLeaderPhone')}>RM Leader mobile *{miss('rmLeaderPhone')}</label>
              <input className="plan-w-md" value={rmLeaderPhone} style={errInput('rmLeaderPhone')} onChange={e => { setRmLeaderPhone(e.target.value); clearErr('rmLeaderPhone'); setPlanFieldsDirty(true) }} placeholder="e.g. 9123 4567" />
            </div>

            <div className="field">
              <label>Auditor 1 *{!job.auditor_id && <span title="Required" style={{ color: 'var(--danger)', marginLeft: 5, fontWeight: 700 }}>•</span>}</label>
              <select value={leadAuditorViolation ? '' : (job.auditor_id || '')} onChange={e => updateField('auditor_id', e.target.value)}>
                <option value="">Select auditor…</option>
                {auditor1Options.map(a => <option key={a.id} value={a.id}>{a.name}{a.grade === 'Lead Auditor' ? ' (Lead)' : ''}</option>)}
              </select>
              {leadAuditorViolation && (
                <div style={{ fontSize: 13, color: '#791F1F', marginTop: 6, padding: '6px 10px', background: '#FDECEA', borderRadius: 6 }}>
                  ⚠ A <strong>Lead Auditor</strong> is required (&gt;200 employees){assignedAuditor1 ? ` — ${assignedAuditor1.name} is not a Lead Auditor` : ''}.
                </div>
              )}
              {noLeadAvailable && (
                <div style={{ fontSize: 13, color: '#854F0B', marginTop: 6, padding: '6px 10px', background: '#FFF3E0', borderRadius: 6 }}>
                  ⚠ No Lead Auditor with this scope is available.
                </div>
              )}
              {auditor1Issues.map(t => <div key={t} style={{ fontSize: 13, color: '#854F0B', marginTop: 6 }}>⚠ {t}</div>)}
              {job.auditor_id && coiLine('auditor1', coi1, job.auditor_name)}
            </div>
            <div className="field">
              <label>Auditor 2 (if any)</label>
              <select value={hasAuditor2 ? job.auditor2_id : ''} onChange={e => updateField('auditor2_id', e.target.value)}>
                <option value="">None</option>
                {(() => {
                  const opts = eligibleAuditors.filter(a => a.id !== job.auditor_id)
                  if (auditor2Resolved && !opts.some(a => a.id === auditor2Resolved.id)) opts.push(auditor2Resolved)
                  return opts.map(a => <option key={a.id} value={a.id}>{a.name}{a.grade === 'Lead Auditor' ? ' (Lead)' : ''}</option>)
                })()}
              </select>
              {hasAuditor2 && coiLine('auditor2', coi2, job.auditor2_name)}
            </div>

            {/* COI status gate banner — only for actionable risk */}
            {job.auditor_id && job.status === 'Scheduled' && (anyHighRisk || anyMediumRisk) && (() => {
              const red = highRiskUnresolved
              const resolvedHigh = anyHighRisk && !highRiskUnresolved
              const amber = !red && anyMediumRisk
              const bg = red ? '#FDECEA' : amber ? '#FFF3E0' : '#EAF3DE'
              const bd = red ? '#E24B4A' : amber ? '#E6A817' : '#639922'
              const fg = red ? '#791F1F' : amber ? '#854F0B' : 'var(--text)'
              return (
                <div style={{ margin: '8px 0', padding: '10px 14px', borderRadius: 8, background: bg, border: `1px solid ${bd}` }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: fg }}>COI status: {coiStatusLabel}</div>
                  <div style={{ fontSize: 14, color: fg, marginTop: 2 }}>
                    {red && 'A High-risk conflict is unresolved — record “Proceed despite conflict” (with justification) or replace the auditor.'}
                    {resolvedHigh && 'High-risk conflict resolved — proceed recorded with justification.'}
                    {amber && 'A Medium conflict was declared — QM review is required before the audit.'}
                  </div>
                </div>
              )
            })()}

            <div className="field">
              <label>Man-days</label>
              <div className="plan-with-action">
                <input className="plan-w-sm" type="number" step="0.5" value={job.mandays || ''} onChange={e => { mandaysTouchedRef.current = true; updateField('mandays', parseFloat(e.target.value)) }} onWheel={e => e.currentTarget.blur()} />
                {calc && <span className="edit-only" style={{ color: 'var(--brand)', cursor: 'pointer', fontWeight: 400, fontSize: 13 }} onClick={() => { mandaysTouchedRef.current = false; updateField('mandays', calc.totalMandays || calc.mandays) }}>Use CT17 ({calc.totalMandays || calc.mandays})</span>}
              </div>
            </div>
            <div className="field">
              <label>No. of locations</label>
              <select className="plan-w-md" value={numLocations} onChange={e => { setNumLocations(parseInt(e.target.value)); setPlanFieldsDirty(true) }}>
                <option value={1}>1 location</option>
                <option value={2}>2 locations</option>
              </select>
            </div>

            {/* CT17 basis — compact one line; details expand (and force-open when a reason is required) */}
            <div className="plan-ct17">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13, color: 'var(--text2)' }}>
                {(() => {
                  const cplx = parseComplexity()
                  return (
                    <span>
                      {calc
                        ? <>CT17 · <strong>{calc.totalMandays ?? calc.mandays}</strong> man-day{(calc.totalMandays ?? calc.mandays) === 1 ? '' : 's'} · <strong>{calc.numSitesToVisit}</strong> site{calc.numSitesToVisit === 1 ? '' : 's'} · Lead {calc.requiresLeadAuditor ? 'required' : 'not required'}</>
                        : <>CT17 · —</>}
                      {cplx?.complexity && <> · Complexity: <strong>{cplx.complexity}</strong>{cplx.override_reason ? ' (overridden)' : ''}</>}
                    </span>
                  )
                })()}
                {!(planningDiffers() || mandayDeviation().changed) && (
                  <button className="sm" style={{ fontSize: 13 }} onClick={() => setCt17Open(v => !v)}>{ct17Open ? '▴ hide' : '▸ details'}</button>
                )}
                <CT17Reference />
              </div>
              {(ct17Open || planningDiffers() || mandayDeviation().changed) && calc && (
                <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--accent-bg)', border: '1px solid rgba(26,95,168,0.2)', borderRadius: 6 }}>
                  <div style={{ fontSize: 13, color: 'var(--accent-text)', display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 8 }}>
                    <div>Base man-days: <strong>{calc.baseMandays}</strong> · Sites to visit (10% min): <strong>{calc.numSitesToVisit}</strong> · Total: <strong>{calc.totalMandays}</strong></div>
                    {(() => { const cplx = parseComplexity(); return cplx?.complexity
                      ? <div>Complexity: <strong>{cplx.complexity}</strong> — {complexityBasisText(cplx)}</div> : null })()}
                  </div>
                  <label style={{ fontSize: 13, color: 'var(--accent-text)', display: 'block', marginBottom: 4 }}>
                    Current workforce for audit planning
                    {planningDiffers() && <span style={{ marginLeft: 6, color: 'var(--warn)', fontWeight: 600 }}>· differs from questionnaire</span>}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <input type="number" min="0" value={planningEmployees} onChange={e => { setPlanningEmployees(e.target.value === '' ? '' : parseInt(e.target.value)); setPlanFieldsDirty(true) }} onWheel={e => e.currentTarget.blur()} style={{ width: 120 }} />
                    <span style={{ fontSize: 13, color: 'var(--text3)' }}>
                      Questionnaire: <strong>{job.form_data?.num_employees ?? '—'}</strong>
                      {planningDiffers() && (
                        <button className="sm edit-only" style={{ marginLeft: 8, fontSize: 13 }} onClick={() => { setPlanningEmployees(job.form_data?.num_employees ?? ''); setPlanningBasisReason(''); clearErr('planningBasisReason'); setPlanFieldsDirty(true) }}>Reset to questionnaire</button>
                      )}
                    </span>
                  </div>
                  {planningDiffers() && (
                    <div style={{ marginTop: 8 }}>
                      <label style={{ fontSize: 13, display: 'block', marginBottom: 4, ...(errLabel('planningBasisReason') || { color: 'var(--accent-text)' }) }}>Planning-basis reason *{miss('planningBasisReason')}</label>
                      <input value={planningBasisReason} style={errInput('planningBasisReason')} onChange={e => { setPlanningBasisReason(e.target.value); clearErr('planningBasisReason'); setPlanFieldsDirty(true) }} placeholder="e.g. Project TOP — reduced active workforce" />
                    </div>
                  )}
                  {mandayDeviation().changed && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed rgba(26,95,168,0.3)' }}>
                      <div style={{ fontSize: 13, marginBottom: 8, display: 'flex', gap: 24, color: hasErr('mandayJustification') ? 'var(--danger)' : 'var(--accent-text)' }}>
                        <div>Current man-days: <strong>{job.mandays ?? '—'}</strong></div>
                        <div>CT 17 baseline: <strong>{mandayDeviation().base}</strong></div>
                      </div>
                      <div className="field field-vert">
                        <label style={{ color: hasErr('mandayJustification') ? 'var(--danger)' : 'var(--accent-text)' }}>Audit duration justification{justificationFromQuote ? ' (from the quotation)' : ''} *{miss('mandayJustification')}</label>
                        <textarea value={mandayJustification} style={{ minHeight: '48px', ...errInput('mandayJustification') }} onChange={e => { setMandayJustification(e.target.value); setJustificationFromQuote(false); clearErr('mandayJustification'); setPlanFieldsDirty(true) }} rows={2} placeholder="e.g. Reduced to 0.5 man-day as operations are simple with minimal hazards…" />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Audit days (the schedule template follows man-days + auditors, as before) ── */}
            {(() => {
              const numAud = (job.auditor_id ? 1 : 0) + (hasAuditor2 ? 1 : 0)   // distinct auditors only
              const md = parseFloat(job.mandays) || 0
              const { splitAllowed, effective } = structureStatus()
              const TIMES = ['0800','0830','0900','0930','1000','1030','1300','1400','1430','1500']
              const startTimeField = (val, setVal, errKey, top = 6) => (
                <select value={val} onChange={e => { setVal(e.target.value); setPlanFieldsDirty(true); if (errKey) clearErr(errKey) }} style={{ width: 120, marginTop: top, display: 'block', ...(errKey ? errInput(errKey) : {}) }}>
                  {TIMES.map(t => <option key={t} value={t}>{t.slice(0,2)}:{t.slice(2)}</option>)}
                </select>
              )
              const head = { fontSize: 13, color: 'var(--text3)' }

              // ───────── 1 auditor: one tidy row per day, boxes on the same edge as every other field ─────────
              if (numAud < 2) {
                const oneAud15 = numAud === 1 && md > 1 && md <= 1.5
                return (
                  <div className="plan-days">
                    <span className="plan-days-title">Audit days</span><span style={head}>Date</span><span style={{ ...head, ...errLabel('startTime') }}>Start{miss('startTime')}</span>
                    <b style={{ fontSize: 13, ...errLabel('audit_date') }}>Day 1{miss('audit_date')}</b>
                    <input type="date" value={job.audit_date || ''} style={errInput('audit_date')} onChange={e => { updateField('audit_date', e.target.value); clearErr('audit_date') }} />
                    {startTimeField(startTime, setStartTime, 'startTime', 0)}
                    {twoDaySpan && <>
                      <b style={{ fontSize: 13, ...errLabel('auditDateDay2') }}>Day 2{miss('auditDateDay2')}</b>
                      <input type="date" value={auditDateDay2} style={errInput('auditDateDay2')} onChange={e => { setAuditDateDay2(e.target.value); clearErr('auditDateDay2'); setPlanFieldsDirty(true) }} />
                      {startTimeField(day2Start, setDay2Start, null, 0)}
                    </>}
                    {oneAud15 && (
                      <span style={{ gridColumn: '2 / -1', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text2)' }}>· Half day on
                        <select value={halfDayPosition} onChange={e => { setHalfDayPosition(e.target.value); setPlanFieldsDirty(true) }} style={{ fontSize: 13, width: 'auto' }}>
                          <option value="Day2">Day 2</option><option value="Day1">Day 1</option>
                        </select></span>
                    )}
                  </div>
                )
              }

              // ───────── 2 auditors ─────────
              const blockedLow = md > 0 && md < 1     // 0.5 — no second-auditor share
              const blockedHigh = md > 2              // beyond approved templates
              if (blockedLow || blockedHigh) {
                return (
                  <div style={{ margin: '10px 0', padding: '8px 10px', background: '#FDECEA', border: '1px solid #E24B4A', borderRadius: 6, fontSize: 13, color: '#791F1F' }}>
                    {blockedLow
                      ? 'No approved schedule for 2 auditors below 1.0 man-day. Use 1 auditor, or check the man-days.'
                      : 'No approved schedule for 2 auditors beyond 2 man-days. Check the man-days.'}
                  </div>
                )
              }

              const isSplit = effective === 'Split'
              const a1 = job.auditor_name || 'Auditor 1'
              const a2 = (auditor2Resolved && auditor2Resolved.name) || job.auditor2_name || 'Auditor 2'
              const tdS = { border: '1px solid var(--border)', padding: '8px 10px', verticalAlign: 'top', fontSize: 13 }
              const thS = { ...tdS, background: 'var(--bg2)', color: 'var(--text2)', fontWeight: 600, textAlign: 'left' }
              const chip = (kind, txt) => <span style={{ display: 'inline-block', fontSize: 13, padding: '2px 8px', borderRadius: 6, background: kind === 'full' ? '#E6F1FB' : '#FAEEDA', color: kind === 'full' ? '#0C447C' : '#854F0B' }}>{txt}</span>
              const dash = () => <span style={{ color: 'var(--text3)' }}>—</span>
              const same = <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4 }}>Same time as {a1}</div>
              const dateCell = (label, val, on, errKey) => (
                <td style={tdS}>
                  <div style={{ fontWeight: 500, marginBottom: 4, ...(errKey ? errLabel(errKey) : {}) }}>{label}</div>
                  <input type="date" value={val || ''} onChange={on} style={{ width: '100%', ...(errKey ? errInput(errKey) : {}) }} />
                </td>
              )
              const d1 = label => dateCell(label, job.audit_date, e => { updateField('audit_date', e.target.value); clearErr('audit_date') }, 'audit_date')
              const d2 = label => dateCell(label, auditDateDay2, e => { setAuditDateDay2(e.target.value); clearErr('auditDateDay2'); setPlanFieldsDirty(true) }, 'auditDateDay2')
              const a1FullStart = <td style={tdS}>{chip('full', 'Full day')}{startTimeField(startTime, setStartTime, 'startTime')}</td>

              let body
              if (md <= 1) {
                body = isSplit ? (
                  <tr>{d1('Day 1')}
                    <td style={tdS}>{chip('half', 'Half day')}{startTimeField(startTime, setStartTime, 'startTime')}</td>
                    <td style={tdS}>{chip('half', 'Half day')}{startTimeField(day2Start, setDay2Start)}</td>
                  </tr>
                ) : (
                  <tr>{d1('Day 1')}
                    <td style={tdS}>{chip('half', 'Half day')}{startTimeField(startTime, setStartTime, 'startTime')}</td>
                    <td style={tdS}>{chip('half', 'Half day')}{same}</td>
                  </tr>
                )
              } else if (md <= 1.5) {
                body = isSplit ? (
                  <>
                    <tr>{d1('Day 1')}{a1FullStart}<td style={tdS}>{dash()}</td></tr>
                    <tr>{d2('Day 2')}<td style={tdS}>{dash()}</td>
                      <td style={tdS}>{chip('half', 'Half day')}{startTimeField(day2Start, setDay2Start)}</td>
                    </tr>
                  </>
                ) : (
                  <tr>{d1('Day 1')}{a1FullStart}
                    <td style={tdS}>{chip('half', 'Half day')}{startTimeField(day2Start, setDay2Start)}</td>
                  </tr>
                )
              } else { // md <= 2
                body = isSplit ? (
                  <>
                    <tr>{d1('Day 1')}{a1FullStart}<td style={tdS}>{dash()}</td></tr>
                    <tr>{d2('Day 2')}<td style={tdS}>{dash()}</td>
                      <td style={tdS}>{chip('full', 'Full day')}{startTimeField(day2Start, setDay2Start)}</td>
                    </tr>
                  </>
                ) : (
                  <tr>{d1('Day 1')}{a1FullStart}
                    <td style={tdS}>{chip('full', 'Full day')}{same}</td>
                  </tr>
                )
              }

              return (
                <div className="plan-days-2">
                  <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    Audit days ·
                    <select value={auditStructure} onChange={e => { setAuditStructure(e.target.value); setPlanFieldsDirty(true) }} style={{ width: 'auto' }}>
                      <option value="Parallel">{md <= 1 ? 'Both auditors, same half-day' : 'Both auditors, same day'}</option>
                      <option value="Split" disabled={!splitAllowed}>{md <= 1 ? `${a1} morning, ${a2} afternoon` : 'One auditor per day'}{!splitAllowed ? ' (needs a Lead each session)' : ''}</option>
                    </select>
                  </div>
                  {!splitAllowed && isSplit && (
                    <div style={{ fontSize: 13, color: 'var(--warn)', marginBottom: 6 }}>A split schedule needs a Lead Auditor for each session — assign a Lead as Auditor 2, or use “both auditors”.</div>
                  )}
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={{ ...thS, width: '34%' }}>Day / date</th><th style={thS}>{a1}</th><th style={thS}>{a2}</th></tr></thead>
                    <tbody>{body}</tbody>
                  </table>
                </div>
              )
            })()}

            {/* Justification for the audit approach (e.g. no site visit) — printed on the audit plan and in the report's
                "Justifications (if any)". */}
            <div className="field">
              <label>Audit approach notes / Justifications (if any)</label>
              {mode === 'view'
                ? <div className="plan-text" style={{ whiteSpace: 'pre-wrap' }}>{auditApproachNote || '—'}</div>
                : <textarea value={auditApproachNote} onChange={e => { setAuditApproachNote(e.target.value); setPlanFieldsDirty(true) }} rows={3}
                    placeholder="e.g. Audit at client office — no site visit because …" />}
            </div>

            {/* More details: the optional fields. Opens by itself whenever bizSAFE needs something filled in there. */}
            {(() => {
              const needed = planningDiffers() || mandayDeviation().changed || hasErr('planningBasisReason') || hasErr('mandayJustification')
              const open = planOpen || needed
              return (
                <>
                  <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {!needed && <button className="sm" onClick={() => setPlanOpen(v => !v)}>{planOpen ? 'Fewer details ▴' : 'More details ▸'}</button>}
                  </div>

                  {open && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                      <div className="field">
                        <label>SAC Observer</label>
                        <input value={job.observer_name || ''} onChange={e => updateField('observer_name', e.target.value)} placeholder="None" />
                      </div>
                      <div className="field">
                        <label>Project name</label>
                        <input value={projectName} onChange={e => { setProjectName(e.target.value); setPlanFieldsDirty(true) }} placeholder="Nil" />
                      </div>
                      <div className="field">
                        <label>Audit objectives</label>
                        <textarea value={auditObjective} onChange={e => { setAuditObjective(e.target.value); setPlanFieldsDirty(true) }} rows={2} style={{ minHeight: '48px' }} />
                      </div>
                      <div className="field">
                        <label>Audit criteria</label>
                        <input value={auditCriteriaField} onChange={e => { setAuditCriteriaField(e.target.value); setPlanFieldsDirty(true) }} />
                      </div>
                      <div className="field">
                        <label>Audit scope</label>
                        <textarea value={auditScopeField} onChange={e => { setAuditScopeField(e.target.value); setPlanFieldsDirty(true) }} rows={2} style={{ minHeight: '48px' }} />
                      </div>
                      <div className="field">
                        <label>Audit type</label>
                        <select className="plan-w-md" value={applicationType} onChange={e => { setApplicationType(e.target.value); setPlanFieldsDirty(true) }}>
                          <option value="New Application">New Application</option>
                          <option value="Renewal">Renewal</option>
                          <option value="Progression">Progression</option>
                        </select>
                      </div>
                      <div className="field">
                        <label>Report reference no.</label>
                        <input value={reportRef} onChange={e => { setReportRef(e.target.value); setPlanFieldsDirty(true) }} placeholder="e.g. RM/2026/001" />
                      </div>
                      <div className="field">
                        <label>Audit checklist URL</label>
                        <input value={checklistUrl} onChange={e => { setChecklistUrl(e.target.value); setPlanFieldsDirty(true) }} />
                      </div>
                      {/* Saves on its own (not with Save draft) — edit mode only, as before. */}
                      {job?.audit_type && mode !== 'view' && <CombinedAuditPanel job={job} onChange={loadJob} />}
                    </div>
                  )}
                </>
              )
            })()}
          </div>

          {/* ── 3 · Audit schedule: built from the audit plan; check it, or edit it by hand ── */}
          {needsBizSAFEOrSHMS && displayRows.length > 0 && (
          <div className="card">
            <h2 className="card-title">Audit schedule</h2>
            {(() => {
              const sm = mode === 'view' ? 'preview' : schedMode   // view mode is always read-only
              return (
                <div>
                  <div style={{ position: 'relative', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px', background: '#fff',
                    ...(schedOpen || sm === 'edit' ? {} : { maxHeight: 240, overflow: 'hidden' }) }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Audit schedule{manuallyEdited ? <span style={{ color: 'var(--warn)', fontSize: 13, fontWeight: 400 }}> · edited</span> : null}</div>
                    {sm === 'edit' && planChanges.length > 0 && (() => {
                      // One banner naming what really changed. Only the auditors → offer to put the new names in
                      // (edits stay). Anything else changes the standard schedule → offer it, or keep the edits.
                      const words = planChanges.map(k => PLAN_CHANGE_LABEL[k])
                      const what = words.length > 1 ? `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}` : words[0]
                      const namesOnly = planChanges.length === 1 && planChanges[0] === 'auditors'
                      return (
                        <div style={{ marginBottom: 12, padding: '10px 14px', background: '#FFF3E0', border: '1px solid #E6A817', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: 220, color: '#854F0B' }}>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>The {what} changed</div>
                            <div style={{ fontSize: 13 }}>
                              {namesOnly ? 'Put the new names in the schedule? Your other edits stay.'
                                : `Use the standard schedule? Your edits will be replaced.${planChanges.includes('auditors') ? ' Keeping your edits still puts in the new names.' : ''}`}
                            </div>
                          </div>
                          {namesOnly ? (
                            <>
                              <button className="primary sm" onClick={() => { updateScheduleNames(); settlePlanBaseline() }} style={{ whiteSpace: 'nowrap', fontSize: 13 }}>Update names</button>
                              <button className="sm" onClick={settlePlanBaseline} style={{ whiteSpace: 'nowrap', fontSize: 13 }}>Leave as is</button>
                            </>
                          ) : (
                            <>
                              <button className="primary sm" onClick={applyStandardSchedule} style={{ whiteSpace: 'nowrap', fontSize: 13 }}>Use standard schedule</button>
                              <button className="sm" onClick={keepScheduleEdits} style={{ whiteSpace: 'nowrap', fontSize: 13 }}>Keep my edits</button>
                            </>
                          )}
                        </div>
                      )
                    })()}
                    {sm === 'edit' && blockedReason && !blocked2Aud && (
                      <div style={{ marginBottom: 8, padding: '10px 12px', background: '#FDECEA', border: '1px solid #E24B4A', borderRadius: 6, fontSize: 13, color: '#791F1F' }}>
                        No approved schedule for this combination. {blockedReason}
                      </div>
                    )}
                    {sm === 'edit' && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8, gap: 8 }}>
                        {manuallyEdited && <button className="sm" onClick={resetToAuto} style={{ fontSize: 13 }}>Reset to auto</button>}
                        <button className="sm" onClick={() => { const rows = [...(editedRows || planRows)]; rows.push({ time: '', activity: '', by: '' }); setEditedRows(rows); setManuallyEdited(true); setPlanFieldsDirty(true) }} style={{ fontSize: 13 }}>+ Add row</button>
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: SCHED_GRID, gap: 6, padding: '4px 0', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 600, color: 'var(--text2)' }}>
                      <div>Time</div>
                      <div>{schedHasParallel ? `Auditor 1${job.auditor_name ? ` · ${job.auditor_name}` : ''}` : 'Activity'}</div>
                      <div>{schedHasParallel ? `Auditor 2${job.auditor2_name ? ` · ${job.auditor2_name}` : ''}` : 'By'}</div>
                      <div />
                    </div>
                    {displayRows.map((row, i) => {
                      if (row.isDayHeader) return (
                        <div key={i} style={{ padding: '6px 0 2px', fontWeight: 700, fontSize: 13, color: 'var(--brand)', borderTop: i > 0 ? '2px solid var(--brand)' : 'none', marginTop: i > 0 ? 6 : 0 }}>— {row.time} —</div>
                      )
                      if (row.time === 'PARALLEL_HEADER') return null
                      // ── Preview: read-only text ──
                      if (sm !== 'edit') return (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: SCHED_GRID, gap: 6, padding: '4px 0', borderBottom: '1px solid var(--border)', fontSize: 13, alignItems: 'start', ...(row.parallel ? { background: 'rgba(26,95,168,0.04)' } : {}) }}>
                          <div style={{ color: 'var(--text2)' }}>{row.time || '—'}</div>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{row.activity || '—'}</div>
                          <div style={{ color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>{row.parallel ? (row.parallelActivity || '—') : (row.by || '—')}</div>
                          <div />
                        </div>
                      )
                      // ── Edit: inputs ──
                      if (row.parallel) return (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: SCHED_GRID, gap: 6, padding: '4px 0', borderBottom: '1px solid var(--border)', background: 'rgba(26,95,168,0.04)', alignItems: 'center' }}>
                          <input value={row.time || ''} onChange={e => updateRow(i, 'time', e.target.value)} style={{ fontSize: 13, height: 32, padding: '0 8px' }} />
                          <textarea value={row.activity || ''} onChange={e => updateRow(i, 'activity', e.target.value)} rows={1} style={cellTA} />
                          <textarea value={row.parallelActivity || ''} onChange={e => updateRow(i, 'parallelActivity', e.target.value)} rows={1} placeholder="—" style={cellTA} />
                          <button title="Delete this row" onClick={() => deleteRow(i)} style={{ fontSize: 15, width: 32, height: 32, padding: 0, color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer', opacity: 0.6 }}>🗑</button>
                        </div>
                      )
                      return (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: SCHED_GRID, gap: 6, padding: '4px 0', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                          <input value={row.time || ''} onChange={e => updateRow(i, 'time', e.target.value)} style={{ fontSize: 13, height: 32, padding: '0 8px' }} />
                          {schedHasParallel ? (
                            <div style={{ gridColumn: '2 / 4', display: 'flex', gap: 6, alignItems: 'start' }}>
                              <textarea value={row.activity || ''} onChange={e => updateRow(i, 'activity', e.target.value)} rows={1} style={{ ...cellTA, flex: 1 }} />
                              {row.by ? <span title={row.by} style={{ fontSize: 13, color: row.by === "Auditee's Representative" ? 'var(--text3)' : 'var(--brand)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 90, padding: '4px 2px' }}>{row.by}</span> : null}
                            </div>
                          ) : (
                            <>
                              <textarea value={row.activity || ''} onChange={e => updateRow(i, 'activity', e.target.value)} rows={1} style={cellTA} />
                              <span title={row.by || ''} style={{ fontSize: 13, color: row.by === "Auditee's Representative" ? 'var(--text3)' : 'var(--brand)', padding: '4px 2px', display: 'block', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.by || ''}</span>
                            </>
                          )}
                          <button title="Delete this row" onClick={() => deleteRow(i)} style={{ fontSize: 15, width: 32, height: 32, padding: 0, color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer', opacity: 0.6 }}>🗑</button>
                        </div>
                      )
                    })}
                    {!(schedOpen || sm === 'edit') && <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 70, background: 'linear-gradient(rgba(255,255,255,0), #fff 85%)', pointerEvents: 'none' }} />}
                  </div>
                </div>
              )
            })()}
            <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {needsBizSAFEOrSHMS && displayRows.length > 0 && mode !== 'view' && schedMode === 'edit'
                ? <button className="sm" onClick={() => { setSchedMode('preview'); setSchedOpen(false) }}>Done editing schedule</button>
                : <>
                    {needsBizSAFEOrSHMS && displayRows.length > 0 && <button className="sm" onClick={() => setSchedOpen(v => !v)}>{schedOpen ? 'Fold schedule ▴' : 'Show full schedule ▾'}</button>}
                    {needsBizSAFEOrSHMS && displayRows.length > 0 && <button className="sm edit-only" onClick={() => { setSchedMode('edit'); setSchedOpen(true) }}>Edit schedule</button>}
                  </>}
            </div>
          </div>
          )}

          {/* ── 4 · Send & replies ── */}
          {(mode !== 'view' || job.audit_plan_sent_at || job.plan_sent_date || job.client_ack_token) && (
          <div className="card">
            <h2 className="card-title">Send &amp; replies</h2>
            {/* Why Send is greyed out — the same checks that gate Send, in the field names shown above. */}
            {mode !== 'view' && planMissingLabels.length > 0 && (
              <div className="plan-still">Still needed: {planMissingLabels.slice(0, 3).join(', ')}{planMissingLabels.length > 3 ? `, +${planMissingLabels.length - 3} more` : ''}</div>
            )}

            {/* Action area — Save draft · Send audit plan (existing handlers, unchanged; edit mode only) */}
            <div style={{ display: mode === 'view' ? 'none' : 'block' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button onClick={saveAuditPlanFields} style={{ background: planFieldsDirty ? 'var(--warn)' : undefined, color: planFieldsDirty ? '#fff' : undefined, borderColor: planFieldsDirty ? 'var(--warn)' : undefined }}>
                  {planFieldsDirty ? '● Save draft' : 'Save draft'}
                </button>
                <button className="primary" onClick={() => sendAuditPlan().catch(e => { console.error('sendAuditPlan failed', e); failed("Couldn't send the audit plan", e?.message || e) })}
                  disabled={planSendDisabled}
                  style={planSendDisabled ? { background: 'var(--bg2)', color: 'var(--text3)', borderColor: 'var(--border)', cursor: 'not-allowed' } : undefined}>
                  {(job.audit_plan_sent_at || job.plan_sent_date) ? '📤 Send updated audit plan' : '📤 Send audit plan'}
                </button>
                {planFieldsDirty && <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--warn)' }}>● Unsaved changes</span>}
              </div>
            </div>

            {/* One quiet status line (view and edit): last sent · client acknowledgement */}
            {((job.audit_plan_sent_at || job.plan_sent_date) || needsBizSAFEOrSHMS) && (
              <div style={{ marginTop: mode === 'view' ? 0 : 12, fontSize: 14, color: 'var(--text2)', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {(job.audit_plan_sent_at || job.plan_sent_date) && <span>Last sent <strong>{job.audit_plan_sent_at
                  ? format(parseISO(job.audit_plan_sent_at), 'd MMM yyyy, HH:mm')
                  : format(parseISO(job.plan_sent_date), 'd MMM yyyy')}</strong></span>}
                {/* Until the client acknowledges: the plan was only copied — remind that it must be pasted into the email. */}
                {needsBizSAFEOrSHMS && (job.audit_plan_sent_at || job.plan_sent_date) && !(ackResponse && ackValid) && <span style={{ color: 'var(--warn)' }}> · Pasted into the client’s email?</span>}
                {needsBizSAFEOrSHMS && (() => {
                  const token = job.client_ack_token
                  const sep = (job.audit_plan_sent_at || job.plan_sent_date) ? ' · ' : ''
                  if (ackResponse && ackValid) return <span>{sep}Client acknowledgement: <span style={{ fontWeight: 600 }}>Received</span>
                    {ackResponse.submitted_at && <span style={{ color: 'var(--text2)' }}> {format(parseISO(ackResponse.submitted_at), 'd MMM yyyy, HH:mm')}</span>}
                    {token && <button className="sm" style={{ marginLeft: 8, fontSize: 13 }} onClick={() => window.open(`${window.location.origin}/ack/${token}`, '_blank')}>View</button>}</span>
                  if (ackResponse && !ackValid) return <span>{sep}Client acknowledgement: <span style={{ color: 'var(--danger)', fontWeight: 600 }}>Re-acknowledgement required</span>
                    {token && !planFieldsDirty && <button className="sm" style={{ marginLeft: 8, fontSize: 13 }} onClick={() => navigator.clipboard.writeText(`${window.location.origin}/ack/${token}`)}>Copy link</button>}</span>
                  // Nothing to acknowledge before the plan is sent (no link exists yet).
                  if (!(job.audit_plan_sent_at || job.plan_sent_date) && !token) return null
                  return <span>{sep}Client acknowledgement: <span style={{ color: 'var(--text2)' }}>Outstanding</span>
                    {token && !planFieldsDirty && <button className="sm" style={{ marginLeft: 8, fontSize: 13 }} onClick={() => navigator.clipboard.writeText(`${window.location.origin}/ack/${token}`)}>Copy link</button>}</span>
                })()}
              </div>
            )}
          </div>
          )}

      </div>
      )}

      {/* ── Manpower arrangement mismatch — office decision (Stage-1 CT17 control) ── */}
      {tab === 'validate' && mpReport?.manpower_control?.mismatch && role !== 'Auditor' && (() => {
        const mc = mpReport.manpower_control
        const decided = mpReport.manpower_decision
        return (
          <div className="card" style={{ marginTop: 16, borderLeft: '3px solid var(--warn)' }}>
            <h2 className="card-title">Audit arrangement mismatch — manpower verification</h2>
            <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 12 }}>
              On-site manpower verification changed the required CT17 arrangement. The auditor is blocked until the office
              approves the revised arrangement or rejects (stop / reschedule).
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '4px 12px', fontSize: 13, marginBottom: 12 }}>
              <span style={{ color: 'var(--text2)' }}>Declared at planning</span><span>{mc.original_manpower}</span>
              <span style={{ color: 'var(--text2)' }}>Verified on-site</span><span><strong>{mc.verified_manpower}</strong></span>
              <span style={{ color: 'var(--text2)' }}>Required man-days</span><span>{mc.original_mandays ?? mc.planned_mandays} → <strong>{mc.recalc_mandays}</strong></span>
              <span style={{ color: 'var(--text2)' }}>Lead Auditor required</span><span>{mc.original_requires_lead ? 'Yes' : 'No'} → <strong>{mc.recalc_requires_lead ? 'Yes' : 'No'}</strong></span>
              <span style={{ color: 'var(--text2)' }}>Impact</span><span>{(mc.reasons || []).join('; ')}</span>
            </div>
            {decided ? (
              <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 14,
                background: decided === 'Approved' ? '#EAF3DE' : '#FDECEA', color: decided === 'Approved' ? 'var(--text)' : '#791F1F' }}>
                {decided === 'Approved' ? '✓ Revised arrangement approved' : '✗ Rejected — audit to be stopped / rescheduled'}
                {mpReport.manpower_decision_by ? ` by ${mpReport.manpower_decision_by}` : ''}{mpReport.manpower_decision_at ? ` · ${new Date(mpReport.manpower_decision_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}` : ''}.
                {mpReport.manpower_decision_reason && <div style={{ marginTop: 6, fontStyle: 'italic' }}>{mpReport.manpower_decision_reason}</div>}
                <button className="sm" style={{ marginTop: 8 }} onClick={() => setMpReport(r => ({ ...r, manpower_decision: null }))}>Change decision</button>
              </div>
            ) : (
              <div>
                <div className="field field-vert" style={{ margin: 0 }}><label>Reason / updated arrangement (required to approve)</label>
                  <textarea rows={2} value={mpReason} onChange={e => setMpReason(e.target.value)} placeholder="e.g. Man-days increased to 1.5; Lead Auditor assigned; client informed" /></div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button className="primary sm" disabled={mpBusy} onClick={() => decideManpower('Approved')}>{mpBusy ? 'Saving…' : 'Approve revised arrangement'}</button>
                  <button className="sm danger" disabled={mpBusy} onClick={() => decideManpower('Rejected')}>Reject — stop / reschedule</button>
                </div>
              </div>
            )}
          </div>
        )
      })()}

      {/* ══ Audit & Report — state-driven action panel: "what do I need to do now?" ══
            Replaces the old Audit-report-status + Office-review cards. View Audit Report is the hero anchor;
            exactly one workflow action (or a clear "waiting") shows per state. Progress lives in the sidebar
            Status Checklist (single source), not a second spine. All handlers reused unchanged. ── */}
      {tab === 'report' && needsBizSAFEOrSHMS && (workflowState(job) || job.client_ack_token || job.status === 'Scheduled' || job.status === 'In progress') && (() => {
        const ws = workflowState(job)
        const ds = ws ? displayState(job) : null
        const fmtDT = (x) => x ? new Date(x).toLocaleString('en-SG', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Singapore' }) : ''
        // Card heading per state. The next step and the owner are not repeated here — the sticky bar above shows them.
        const T = {
          'Pending Office Review': 'Pending office review',
          'Ready to Send Report': 'Ready to send client acknowledgement',
          'Awaiting Client Acknowledgement': 'Awaiting client acknowledgement',
          'Ready for WSHC Submission': 'Ready for WSHC submission',
          'Pending WSHC Outcome': 'Submitted to WSHC',
          'Returned for Revision': 'Returned for revision',
          'Completed': 'Finalised',
        }
        const title = ws ? (portalText || T[ds] || ds) : 'Waiting for the auditor’s report'
        const invoiceLine = job.invoice_clearance_checked_at ? (
          <div style={{ fontSize: 14, margin: '8px 0' }}>
            {job.invoice_cleared_confirmed
              ? <span style={{ fontWeight: 600 }}><span style={{ color: 'var(--brand)' }}>✓</span> Cleared</span>
              : <span style={{ color: '#791F1F', fontWeight: 600 }}>✕ Audit report released before clearance</span>}
            {!job.invoice_cleared_confirmed && job.invoice_clearance_reason && <div style={{ color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>Reason: {job.invoice_clearance_reason}</div>}
            <div style={{ color: 'var(--text2)', fontSize: 14, marginTop: 2 }}>Checked by {job.invoice_clearance_checked_by_name || '—'} · {fmtDT(job.invoice_clearance_checked_at)}</div>
          </div>
        ) : null
        // Checkpoint 6: gate the FIRST issue of the ack link on a 'ready' package. Exempt = POSITIVE signals
        // only: an already-issued link (resend) or an already-acknowledged version. A NEW / pre-ack job with any
        // non-'ready' status — including 'none' (not generated) — is blocked until it is generated.
        const alreadyIssuedLink = !!job.report_ack_sent_at && !!job.report_ack_token && job.report_ack_version === reportVersion
        const ackSendBlocked = pkgFlag && !reportAckValid && !alreadyIssuedLink && pkgSnap && pkgSnap.package_status !== 'ready'
        // Checkpoint 7: gate WSHC submission on the STORED package for a NEW official-package job. Legacy job
        // (acknowledged snapshot never generated → 'none') submits via the old flow and is NOT blocked; a NEW
        // job whose package is preparing/error is blocked. Reads pkgSnap only — never rebuilds.
        // Blocked while preparing/failed OR while the stored copy predates the client's signature (unsigned).
        const wshcBlocked = pkgFlag && pkgSnap &&
          ((pkgSnap.package_status !== 'ready' && pkgSnap.package_status !== 'none') || pkgStale)
        const sendBlock = (
          <div>
            {invoiceLine}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="field" style={{ margin: 0 }}><label>Client email</label>
                <input type="email" value={ackEmail} onChange={e => setAckEmail(e.target.value)} placeholder={job.report_ack_sent_to || client?.contact_email || job?.form_data?.pic_email || 'client@company.com'} style={{ minWidth: 240 }} /></div>
              <button className="primary sm" disabled={ackSending || ackSendBlocked} title={ackSendBlocked ? 'The report is still being prepared' : ''} onClick={() => sendReportToClient()}>{ackSending ? 'Copying…' : (ds === 'Awaiting Client Acknowledgement' ? 'Copy Link' : 'Send acknowledgement link')}</button>
            </div>
            {ackSendBlocked && <div style={{ fontSize: 14, color: 'var(--warn)', marginTop: 6 }}>The report is still being prepared — you’ll be able to send the acknowledgement link once it’s ready.</div>}
            {job.report_ack_sent_to && (() => {
              const sentAt = job.report_ack_sent_at ? new Date(job.report_ack_sent_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' }) : ''
              if (job.report_ack_version != null && job.report_ack_version < reportVersion) {
                return <div style={{ fontSize: 14, color: '#854F0B', background: '#FFF3E0', border: '1px solid #E6A817', borderRadius: 6, padding: '5px 8px', marginTop: 6 }}>
                  ⚠ Revised report not yet sent to the client. Previous version was sent to {job.report_ack_sent_to}{sentAt ? ` on ${sentAt}` : ''}.
                </div>
              }
              return <div style={{ fontSize: 14, color: 'var(--text2)', marginTop: 6 }}>
                Link sent to {job.report_ack_sent_to}{sentAt ? ` · ${sentAt}` : ''}{job.report_ack_version === reportVersion ? ' (this version)' : ''}.
              </div>
            })()}
          </div>
        )
        const returnControl = (
          <div>
            {!returnForm.open ? (
              <button className="sm" onClick={() => setReturnForm({ open: true, reason: '' })}>↩ Return to auditor</button>
            ) : (
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                {reportAckValid && <div style={{ fontSize: 14, color: '#854F0B', marginBottom: 8 }}>The client’s acknowledgement will be withdrawn.</div>}
                <div className="field field-vert" style={{ margin: 0 }}><label>Return reason / review comments *</label>
                  <textarea rows={2} value={returnForm.reason} onChange={e => setReturnForm(f => ({ ...f, reason: e.target.value }))} placeholder="What must the auditor revise…" /></div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button className="sm danger" disabled={officeBusy} onClick={returnToAuditor}>{officeBusy ? 'Returning…' : 'Confirm return'}</button>
                  <button className="sm" onClick={() => setReturnForm({ open: false, reason: '' })}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )
        return (
        <div className="card action-live" style={{ marginTop: 16 }}>
          {job.auditor_closed && role !== 'Auditor' && (
            <button className="sm" style={{ marginBottom: 10 }} onClick={reopenAuditorAccess}>Reopen auditor access</button>
          )}
          <h2 className="card-title">{title}</h2>

          {/* Outstanding items the auditor logged, read-only — so the office can see WHY the report isn't
              submitted yet without logging into the auditor's account. Shown only while items are still Open;
              once the auditor closes them all (report can then be submitted), this disappears. */}
          {(() => {
            const openItems = outstandingItems.filter(o => o.status !== 'Closed' && (o.description || '').trim())
            if (!openItems.length) return null
            return (
              <div style={{ border: '1px solid #E6A817', background: '#FFF8EC', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#854F0B', marginBottom: 2 }}>
                  {openItems.length} outstanding item{openItems.length > 1 ? 's' : ''} the auditor is still resolving
                </div>
                <div style={{ fontSize: 14, color: '#854F0B', marginBottom: 8 }}>
                  Logged on-site by {job.auditor_name || 'the auditor'}. The report can’t be submitted until these are closed.
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, color: 'var(--text)' }}>
                  {openItems.map(o => <li key={o.id} style={{ marginBottom: 3, whiteSpace: 'pre-wrap' }}>{o.description}</li>)}
                </ul>
              </div>
            )
          })()}

          {/* Report status in plain language. Nothing shown when ready/plain (View report sits in the command
              bar). Only the transient "Preparing report…" or a failure + office Retry appear here. */}
          {ws && reportPrep.state === 'preparing' && (
            <div style={{ fontSize: 13, marginBottom: 12, color: 'var(--text3)' }}>Preparing report…</div>
          )}
          {ws && reportPrep.state === 'failed' && (
            <div style={{ fontSize: 14, marginBottom: 12, color: 'var(--danger)' }}>
              Report preparation failed
              {role !== 'Auditor' && (
                <button className="sm" style={{ marginLeft: 8, fontSize: 13 }} onClick={startRender}>Retry</button>
              )}
            </div>
          )}

          {/* Client's bizSAFE application details (submitted at report endorsement) — office reads/uses these
              to complete the WSHC online application. Shown in EVERY post-acknowledgement state (not just the
              ack-send step) so it is always available at the WSHC-submission and outcome stages. */}
          {job.bizsafe_application && (
            <div style={{ marginBottom: 14 }}>
              <BizsafeApplicationPanel app={{ ...job.bizsafe_application, application_type: job.bizsafe_application.application_type || job.application_type || job.form_data?.application_type || '' }} busy={officeBusy}
                onSave={async (newApp) => {
                  setOfficeBusy(true)
                  const { error } = await supabase.from('jobs').update({ bizsafe_application: newApp }).eq('id', id)
                  setOfficeBusy(false)
                  if (error) { failed("Couldn't save", error.message); return }
                  setJob(j => ({ ...j, bizsafe_application: newApp }))
                  toast('Application details updated', 'success')
                }} />
            </div>
          )}

          {reportSnapOld && (ws === 'Pending Office Review' || ws === 'Returned for Revision') && (
            <div style={{ padding: '10px 14px', background: '#FFF3E0', borderRadius: 8, border: '1px solid #E6A817', fontSize: 14, color: '#854F0B', marginBottom: 14 }}>
              ⚠ This report was generated with an older version — some N/A questions may be missing. Return it to the auditor to re-submit for the full checklist.
            </div>
          )}

          {ds === 'Returned for Revision' && (
            <div style={{ padding: '10px 14px', background: '#FDECEA', borderRadius: 8, border: '1px solid #E24B4A', fontSize: 14, color: '#791F1F' }}>
              ↩ Returned{job.return_source ? ` (source: ${job.return_source})` : ''}{job.returned_by ? ` by ${job.returned_by}` : ''}{job.returned_at ? ` · ${new Date(job.returned_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}` : ''}.
              {job.return_reason && <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}><strong>Reason:</strong> {job.return_reason}</div>}
            </div>
          )}

          {ds === 'Pending Office Review' && (
            isAuditorOnJob() ? (
              <div style={{ padding: '10px 14px', background: '#FFF3E0', borderRadius: 8, border: '1px solid #E6A817', fontSize: 14, color: '#854F0B' }}>
                ⚠ You conducted this audit. The office review must be done by a different authorised Decision-Maker.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ border: '1px solid #639922', borderRadius: 8, padding: 12, background: '#FAFCF6' }}>
                  {!dmGate.ok && (
                    <div style={{ padding: '8px 12px', background: '#FFF3E0', borderRadius: 8, fontSize: 14, color: '#854F0B', marginBottom: 10 }}>
                      You are not yet authorised to approve: <strong>{dmGate.gaps.join(' · ')}</strong>
                    </div>
                  )}
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 }}>Items reviewed &amp; approved</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 8 }}>
                    {REVIEW_ITEMS.map(it => (
                      <label key={it} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '2px 0', cursor: 'pointer' }}>
                        <input type="checkbox" checked={reviewChecks[it] !== false} onChange={e => setReviewChecks(c => ({ ...c, [it]: e.target.checked }))} />
                        <span>{it}</span>
                      </label>
                    ))}
                  </div>
                  <div className="field" style={{ margin: '0 0 6px' }}><label>Route the next steps to (office handler)</label>
                    <select value={routeTo} onChange={e => setRouteTo(e.target.value)} style={{ maxWidth: 320 }}>
                      <option value="">— Keep with me ({profile?.name || role}) —</option>
                      {handlers.map(h => <option key={h.id} value={h.id}>{h.name}{h.role ? ` — ${h.role}` : ''}</option>)}
                    </select></div>
                  <div style={{ fontSize: 13, color: 'var(--text3)', margin: '0 0 8px' }}>
                    Recording as <strong>{profile?.name || role}</strong>. Approving records the certification decision and routes the next step to the selected office handler. You can still do any step yourself.
                  </div>
                  {!REVIEW_ITEMS.every(it => reviewChecks[it] !== false) && (
                    <div style={{ fontSize: 13, color: 'var(--danger)', marginBottom: 6 }}>Tick all four review items to approve.</div>
                  )}
                  <button className="primary sm" disabled={officeBusy || !dmGate.ok || !REVIEW_ITEMS.every(it => reviewChecks[it] !== false)} onClick={() => approveAndSendToClient()}>{officeBusy ? 'Approving…' : 'Approve report'}</button>
                </div>
                {returnControl}
              </div>
            )
          )}

          {(ds === 'Ready to Send Report' || ds === 'Awaiting Client Acknowledgement') && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {ds === 'Awaiting Client Acknowledgement' && reportAck && reportAck.report_version < reportVersion && (
                <div style={{ fontSize: 14, color: '#854F0B' }}>The client acknowledged v{reportAck.report_version}, but the current report is v{reportVersion}. Resend for re-acknowledgement.</div>
              )}
              {sendBlock}
              {isPortalJob(job) && canEndJob(job) && (
                <div style={{ fontSize: 14, color: '#854F0B' }}>The client hasn’t signed for 14 days. If they won’t sign, you can end the job in the <strong>Finalise</strong> tab.</div>
              )}
              {returnControl}
            </div>
          )}

          {/* New WSHC-portal process: the client applies on WSHC themselves. Signed → AuditPM emails the person who
              will apply (automatically; renewals on the day the 2-month window opens). Nothing for the office to
              send; the office can correct who applies until that email has gone. */}
          {ds === 'Ready for WSHC Submission' && isPortalJob(job) && (() => {
            const expiry = client?.bizsafe_expiry_date
            const windowOpen = isWshcWindowOpen(expiry)
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ border: '1px solid #639922', borderRadius: 8, padding: 12, background: '#FAFCF6', fontSize: 14 }}>
                  <div style={{ fontWeight: 600 }}><span style={{ color: 'var(--brand)' }}>✓</span> Client signed the report</div>
                  {job.invoice_clearance_checked_at && !job.invoice_cleared_confirmed && (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ color: '#791F1F', fontWeight: 600 }}>⚠ Report released before invoice clearance</div>
                      {job.invoice_clearance_reason && <div style={{ color: 'var(--text2)', marginTop: 2, whiteSpace: 'pre-wrap' }}>Reason: {job.invoice_clearance_reason}</div>}
                    </div>
                  )}
                  <div style={{ marginTop: 10 }}>
                    <div style={{ color: 'var(--text2)' }}>Assigned staff (applies on WSHC for the client)</div>
                    {!applicantForm.open ? (
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 2 }}>
                        {job.wshc_applicant_email
                          ? <span>{job.wshc_applicant_name || '—'} · {job.wshc_applicant_email}</span>
                          : <span style={{ color: '#854F0B', fontWeight: 600 }}>Not given — add it so the apply email can go out</span>}
                        <button className="sm" onClick={() => setApplicantForm({ open: true, name: job.wshc_applicant_name || '', email: job.wshc_applicant_email || '' })}>{job.wshc_applicant_email ? 'Change' : 'Add'}</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6, maxWidth: 360 }}>
                        <div className="field field-vert" style={{ margin: 0 }}><label>Name</label>
                          <input value={applicantForm.name} onChange={e => setApplicantForm(f => ({ ...f, name: e.target.value }))} /></div>
                        <div className="field field-vert" style={{ margin: 0 }}><label>Email</label>
                          <input type="email" value={applicantForm.email} onChange={e => setApplicantForm(f => ({ ...f, email: e.target.value }))} /></div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="primary sm" disabled={officeBusy} onClick={saveApplicant}>{officeBusy ? 'Saving…' : 'Save'}</button>
                          <button className="sm" onClick={() => setApplicantForm({ open: false, name: '', email: '' })}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: 10, padding: '8px 10px', background: '#fff', border: '1px solid #d7dce6', borderRadius: 6 }}>
                    <strong>Next:</strong>{' '}
                    {!job.wshc_applicant_email
                      ? <>add the client’s assigned staff above — AuditPM can’t email the steps without it.</>
                      : windowOpen
                        ? <>AuditPM emails the WSHC steps to {job.wshc_applicant_name || job.wshc_applicant_email} as soon as the signed report is ready (usually within minutes), copying the person we sent the report to and bizSAFE@. If it hasn’t gone yet, it goes at the latest the next morning. <strong>Nothing for you to do.</strong></>
                        : <>WSHC accepts this renewal from <strong>{fmtDay(wshcWindowOpensOn(expiry))}</strong> (2 months before the current expiry, {fmtDay(expiry)}). On that morning AuditPM emails the WSHC steps to {job.wshc_applicant_name || job.wshc_applicant_email}. <strong>Nothing for you to do until then.</strong></>}
                  </div>
                </div>
                {returnControl}
              </div>
            )
          })()}

          {ds === 'Ready for WSHC Submission' && !isPortalJob(job) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Go/no-go readiness only — names, versions and timestamps live in the Audit Trail below,
                  never repeated here. The invoice-clearance EXCEPTION stays visible (a control issue, not history). */}
              <div style={{ border: '1px solid #639922', borderRadius: 8, padding: 12, background: '#FAFCF6' }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}><span style={{ color: 'var(--brand)' }}>✓</span> Client acknowledgement received</div>
                {job.invoice_clearance_checked_at && (
                  job.invoice_cleared_confirmed
                    ? <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}><span style={{ color: 'var(--brand)' }}>✓</span> Invoice clearance confirmed</div>
                    : <div style={{ fontSize: 14, marginTop: 6 }}>
                        <div style={{ color: '#791F1F', fontWeight: 600 }}>⚠ Report released before invoice clearance</div>
                        {job.invoice_clearance_reason && <div style={{ color: 'var(--text2)', marginTop: 2, whiteSpace: 'pre-wrap' }}>Reason: {job.invoice_clearance_reason}</div>}
                      </div>
                )}
                {wshcBlocked && <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 6 }}>Preparing report…</div>}
                {wshcTooEarly ? (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 14, color: '#791F1F', fontWeight: 600, padding: '10px 14px', background: '#FDECEA', border: '1px solid #E24B4A', borderRadius: 8 }}>
                      🔒 WSHC submission locked. Submission can only be done on or after {wshcOpenLabel}.
                    </div>
                    <button className="sm" style={{ marginTop: 8 }} disabled={officeBusy || !reportAckValid || wshcBlocked} onClick={() => { setWshcOverrideReason(''); setWshcOverrideModal(true) }}>Override — record submission early</button>
                  </div>
                ) : (
                  <div style={{ marginTop: 12 }}>
                    <button className="primary sm" disabled={officeBusy || !reportAckValid || wshcBlocked} onClick={() => submitToWSHC()} title={!reportAckValid ? 'Waiting for client acknowledgement' : (wshcBlocked ? 'The report is still being prepared' : '')}>{officeBusy ? 'Recording…' : 'Record WSHC submission'}</button>
                  </div>
                )}
              </div>
              {returnControl}
            </div>
          )}

          {ds === 'Pending WSHC Outcome' && isPortalJob(job) && (() => {
            const days = daysSinceApplyEmail(job)
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ fontSize: 14, lineHeight: 1.7 }}>
                  <div>Apply steps emailed to <strong>{job.wshc_apply_sent_to || '—'}</strong>{job.wshc_apply_email_sent_at ? ` on ${fmtDay(job.wshc_apply_email_sent_at)}` : ''}{days != null ? ` (day ${days})` : ''}.</div>
                  {job.client_wshc_submitted_at
                    ? <div><span style={{ color: 'var(--brand)' }}>✓</span> Client says they applied on {fmtDay(job.client_wshc_submitted_at)}.</div>
                    : <div style={{ color: days != null && days >= 28 ? '#854F0B' : 'var(--text2)', fontWeight: days != null && days >= 28 ? 600 : 400 }}>
                        {days != null && days >= 28 ? 'No reply after 28 days — please call the client.' : 'Client hasn’t confirmed applying yet (AuditPM reminds them on day 7 and day 21).'}
                      </div>}
                </div>
                <div style={{ fontSize: 14, padding: '8px 10px', border: '1px solid #d7dce6', borderRadius: 6 }}>
                  <strong>Next:</strong> the client applies on WSHC themselves (AuditPM reminds them on day 7 and day 21). In your weekly WSHC check, once their new certificate is issued, go to <strong>Finalise</strong> and record the new expiry. If no certificate is coming, end the job in the Finalise tab.
                </div>
                <button className="primary sm" style={{ alignSelf: 'flex-start' }} onClick={() => setJtab('finalise')}>Go to Finalise →</button>
                {!job.client_wshc_submitted_at && (!resendForm.open ? (
                  <button className="sm" style={{ alignSelf: 'flex-start' }} onClick={() => setResendForm({ open: true, name: job.wshc_applicant_name || '', email: job.wshc_apply_sent_to || job.wshc_applicant_email || '' })}>Wrong person or address? Send the apply email again</button>
                ) : (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 380 }}>
                    <div style={{ fontSize: 13, color: 'var(--text2)' }}>Who should receive the WSHC steps?</div>
                    <div className="field field-vert" style={{ margin: 0 }}><label>Name</label>
                      <input value={resendForm.name} onChange={e => setResendForm(f => ({ ...f, name: e.target.value }))} /></div>
                    <div className="field field-vert" style={{ margin: 0 }}><label>Email</label>
                      <input type="email" value={resendForm.email} onChange={e => setResendForm(f => ({ ...f, email: e.target.value }))} /></div>
                    <div style={{ fontSize: 13, color: 'var(--text3)' }}>The apply email goes out again straight away, and the day 7 / day 21 reminders restart from then.</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="primary sm" disabled={officeBusy} onClick={resendApplyEmail}>{officeBusy ? 'Saving…' : 'Send again'}</button>
                      <button className="sm" onClick={() => setResendForm({ open: false, name: '', email: '' })}>Cancel</button>
                    </div>
                  </div>
                ))}
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                  <div className="field field-vert" style={{ margin: 0 }}><label>WSHC returned the report? (the client will tell you) — reason</label>
                    <textarea rows={2} value={wshcForm.reason} onChange={e => setWshcForm({ reason: e.target.value })} placeholder="Reason WSHC returned the report…" /></div>
                  <button className="sm danger" style={{ marginTop: 8 }} disabled={officeBusy} onClick={wshcReturn}>{officeBusy ? 'Saving…' : 'Returned by WSHC → send back to auditor'}</button>
                </div>
              </div>
            )
          })()}

          {ds === 'Pending WSHC Outcome' && !isPortalJob(job) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 14, color: 'var(--text2)' }}>WSHC accepted → record the new bizSAFE expiry in the <strong>Finalise</strong> tab (closes the cycle). If WSHC returned the package, record it below.</div>
              <button className="primary sm" style={{ alignSelf: 'flex-start' }} onClick={() => setJtab('finalise')}>Go to Finalise →</button>
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                <div className="field field-vert" style={{ margin: 0 }}><label>WSHC return reason</label>
                  <textarea rows={2} value={wshcForm.reason} onChange={e => setWshcForm({ reason: e.target.value })} placeholder="Reason WSHC returned the package…" /></div>
                <button className="sm danger" style={{ marginTop: 8 }} disabled={officeBusy} onClick={wshcReturn}>{officeBusy ? 'Saving…' : 'Returned by WSHC → send back to auditor'}</button>
              </div>
            </div>
          )}

          {ds === 'Completed' && isEnded(job) && (
            <div style={{ padding: '10px 14px', background: '#f5f5f5', borderRadius: 8, fontSize: 14, color: 'var(--text2)' }}>
              Ended without a new certificate{job.wshc_ended_at ? ` on ${fmtDay(job.wshc_ended_at)}` : ''}{job.wshc_ended_by ? ` by ${job.wshc_ended_by}` : ''}. See the Finalise tab.
            </div>
          )}

          {ds === 'Completed' && !isEnded(job) && (
            <div style={{ padding: '10px 14px', background: '#EAF3DE', borderRadius: 8, fontSize: 14 }}>
              <span style={{ color: 'var(--brand)' }}>✓</span> Completed.{job.wshc_new_expiry ? ` WSHC accepted — new bizSAFE expiry ${format(parseISO(job.wshc_new_expiry), 'd MMM yyyy')}.` : ''}
            </div>
          )}
        </div>
        )
      })()}

      {/* ══ RECORDS & HISTORY — quiet footer: supporting + historical info, not the "what do I do now" action.
            Audit trail (who/when for every milestone) lives here so SAC traceability is preserved after the
            old Office-review card was replaced by the action panel above. ══ */}
      {tab === 'report' && needsBizSAFEOrSHMS && role !== 'Auditor' && workflowState(job) && (() => {
        const fmtDT = (x) => x ? new Date(x).toLocaleString('en-SG', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Singapore' }) : ''
        const rows = [
          job.report_submitted_by && `Report submitted by ${job.report_submitted_by}${job.report_submitted_at ? ` · ${fmtDT(job.report_submitted_at)}` : ''}`,
          job.office_review_at && `Report reviewed & approved by ${job.office_review_by || '—'} · ${fmtDT(job.office_review_at)}`,
          job.invoice_clearance_checked_at && `${job.invoice_cleared_confirmed ? 'Invoice cleared' : 'Report released before invoice clearance'} — checked by ${job.invoice_clearance_checked_by_name || '—'} · ${fmtDT(job.invoice_clearance_checked_at)}`,
          job.report_ack_sent_at && `Acknowledgement link sent${job.report_ack_sent_to ? ` to ${job.report_ack_sent_to}` : ''} · ${fmtDT(job.report_ack_sent_at)}`,
          reportAckValid && reportAck && `Report acknowledged${reportAck.ack_name ? ` by ${reportAck.ack_name}` : ''}${reportAck.report_version ? ` · v${reportAck.report_version}` : ''}${reportAck.submitted_at ? ` · ${fmtDT(reportAck.submitted_at)}` : ''}`,
          job.submitted_to_wshc_at && `Submitted to WSHC by ${job.submitted_to_wshc_by_name || job.assigned_to_name || '—'} · ${fmtDT(job.submitted_to_wshc_at)}`,
          // New WSHC-portal process (the client applies on WSHC themselves)
          job.wshc_applicant_email && `Assigned staff (applies on WSHC): ${job.wshc_applicant_name || '—'} · ${job.wshc_applicant_email}`,
          job.wshc_apply_email_sent_at && `Apply steps emailed to ${job.wshc_apply_sent_to || '—'} (cc signer + bizSAFE@) · ${fmtDT(job.wshc_apply_email_sent_at)}`,
          job.client_wshc_submitted_at && `Client says they applied on WSHC · ${fmtDT(job.client_wshc_submitted_at)}`,
          job.wshc_new_expiry && `WSHC accepted — new bizSAFE expiry ${format(parseISO(job.wshc_new_expiry), 'd MMM yyyy')}`,
          isEnded(job) && `Ended — no new certificate${job.wshc_ended_by ? ` by ${job.wshc_ended_by}` : ''} · ${fmtDT(job.wshc_ended_at)}${job.wshc_end_reason ? ` · ${job.wshc_end_reason}` : ''}`,
        ].filter(Boolean)
        if (!rows.length) return null
        return (
        <div className="card" style={{ marginTop: 24 }}>
          <h2 className="card-title">Records &amp; history</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.map((r, i) => <div key={i} style={{ fontSize: 14 }}><span style={{ color: 'var(--brand)' }}>✓</span> {r}</div>)}
          </div>
        </div>
        )
      })()}

      {/* ── Session evidence (INTERNAL ONLY — proof of site attendance; never in the formal report / client view) ── */}
      {tab === 'report' && needsBizSAFEOrSHMS && role !== 'Auditor' && (
        <div className="card" style={{ marginTop: 16 }}>
          {/* Proof the auditor attended site — never in the formal report or shown to the client. */}
          <h2 className="card-title">Session evidence <span className="card-title-note">(internal only)</span></h2>
          {(() => {
            // One line — consent and upload time — then the buttons. Same date style as Records & history.
            const uploaded = sessionPhoto?.created_at ? new Date(sessionPhoto.created_at).toLocaleString('en-SG', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Singapore' }) : ''
            const consent = photoAnswer || photoConsent
            if (!consent && !(sessionPhoto && uploaded)) return null
            return (
              <div style={{ fontSize: 14, marginBottom: 12 }}>
                {consent
                  ? <span title={photoAnswer ? undefined : 'Yes only if every opening and closing meeting participant consented'}>Photo consent: <strong>{consent}</strong></span>
                  : 'Photo uploaded'}
                {sessionPhoto && uploaded && <span style={{ color: 'var(--text2)' }}>{consent ? ` (uploaded ${uploaded})` : ` ${uploaded}`}</span>}
              </div>
            )
          })()}
          {sessionPhoto ? (
            <div style={{ display: 'flex', gap: 8 }}>
              {sessionPhotoUrl && <button className="sm" onClick={() => window.open(sessionPhotoUrl, '_blank')}>View</button>}
              <button className="sm" onClick={() => downloadEvidence(sessionPhoto.storage_path, sessionPhoto.file_name)}>Download</button>
            </div>
          ) : noPhotoReason ? (
            <div style={{ padding: '10px 14px', background: '#FFF3E0', border: '1px solid #E6A817', borderRadius: 8, fontSize: 14, color: '#6b4e00' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Session photo not uploaded</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{noPhotoReason}</div>
              {(job.report_submitted_by || job.report_submitted_at) && (
                <div style={{ fontSize: 14, color: 'var(--text2)', marginTop: 6 }}>
                  Recorded by {job.report_submitted_by || '—'}{job.report_submitted_at ? ` · ${new Date(job.report_submitted_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}` : ''}
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: '10px 14px', background: '#FDECEA', border: '1px solid #E24B4A', borderRadius: 8, fontSize: 14, color: '#791F1F' }}>
              ⚠ No session photo or reason recorded.
            </div>
          )}
        </div>
      )}

      {/* ── WSHC early-submission override modal — bypasses the 2-month window lock with a logged reason ── */}
      {wshcOverrideModal && (
        <div className="modal-overlay" onClick={() => !officeBusy && setWshcOverrideModal(false)}>
          <div className="modal action-live" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: 14 }}>Override WSHC submission lock</h2></div>
            <p style={{ fontSize: 13, color: 'var(--text2)', margin: '4px 0 12px' }}>
              WSHC does not accept renewal submissions earlier than 2 months before expiry — this client's window opens {wshcOpenLabel}. Only override if you have confirmed the submission will be accepted. This is recorded against the job.
            </p>
            <div className="field" style={{ margin: 0 }}>
              <label>Reason for submitting early *</label>
              <textarea rows={3} value={wshcOverrideReason} onChange={e => setWshcOverrideReason(e.target.value)} placeholder="Example: WSHC confirmed the expiry on their record differs; approved by …" />
              {!wshcOverrideReason.trim() && <div style={{ fontSize: 13, color: 'var(--danger)', marginTop: 4 }}>A reason is required to override.</div>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button className="sm" disabled={officeBusy} onClick={() => setWshcOverrideModal(false)}>Cancel</button>
              <button className="primary sm" disabled={officeBusy || !wshcOverrideReason.trim()}
                onClick={() => { wshcOverrideRef.current = wshcOverrideReason.trim(); setWshcOverrideModal(false); submitToWSHC() }}>
                Confirm override &amp; continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Invoice Clearance Check modal — gates "Approve & send to client" ── */}
      {invoiceModal && (
        <div className="modal-overlay" onClick={() => !officeBusy && setInvoiceModal(false)}>
          <div className="modal action-live" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: 14 }}>Invoice Clearance Check</h2></div>
            <p style={{ fontSize: 13, color: 'var(--text2)', margin: '4px 0 12px' }}>
              Before {invoiceAction === 'wshc' ? 'submitting this report to WSHC' : 'sending the report to the client'}, please confirm invoice/payment status.
            </p>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer', marginBottom: 10 }}>
              <input type="checkbox" checked={invCleared} onChange={e => setInvCleared(e.target.checked)} />
              All invoices are cleared.
            </label>
            {!invCleared && (
              <div className="field" style={{ margin: 0 }}>
                <label>Reason for releasing the report before invoice clearance *</label>
                <textarea rows={3} value={invReason} onChange={e => setInvReason(e.target.value)} placeholder="Example: Client urgent submission deadline, management approval obtained, etc." />
                {!invReason.trim() && <div style={{ fontSize: 13, color: 'var(--danger)', marginTop: 4 }}>A reason is required when invoices are not cleared.</div>}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button className="sm" disabled={officeBusy} onClick={() => setInvoiceModal(false)}>Cancel</button>
              <button className="primary sm" disabled={officeBusy || ackSending || (!invCleared && !invReason.trim())}
                onClick={() => (invoiceAction === 'wshc' ? submitToWSHC : sendReportToClient)({ cleared: invCleared, reason: invReason.trim() })}>
                {(officeBusy || ackSending) ? 'Submitting…' : (invoiceAction === 'wshc' ? 'Confirm & record WSHC submission' : 'Confirm & send to client')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revision history — internal/SAC traceability of each report version and why it was revised. Shown only
          once a revision has actually happened; stays visible in every state (incl. Completed) for later review.
          Never part of the issued report. */}
      {tab === 'report' && (revisions.length > 1 || revisions.some(r => r.return_reason)) && (
        <div className="card" style={{ marginTop: 16 }}>
          <h2 className="card-title">Revision history</h2>
          <p style={{ fontSize: 13, color: 'var(--text3)', margin: '0 0 12px' }}>Internal record of each report version and the reason it was revised — for SAC traceability. Not part of the issued report.</p>
          <div style={{ display: 'grid', gridTemplateColumns: '70px 1.1fr 1.6fr', gap: 8, fontSize: 13, color: 'var(--text2)', paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>
            <div>Version</div><div>Submitted by · when</div><div>Returned for revision</div>
          </div>
          {revisions.map((r, i) => (
            <div key={r.version} style={{ display: 'grid', gridTemplateColumns: '70px 1.1fr 1.6fr', gap: 8, fontSize: 14, padding: '8px 0', borderBottom: i < revisions.length - 1 ? '0.5px solid var(--border)' : 'none', alignItems: 'start' }}>
              <div><strong>v{r.version}</strong>{i === revisions.length - 1 && <span style={{ marginLeft: 4, fontSize: 13, color: 'var(--text2)' }}>(current)</span>}</div>
              <div>{r.submitted_by || '—'}<div style={{ color: 'var(--text2)', fontSize: 14 }}>{r.submitted_at ? new Date(r.submitted_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' }) : ''}</div></div>
              <div>{r.return_reason ? (<>
                <div style={{ whiteSpace: 'pre-wrap' }}>{r.return_reason}</div>
                <div style={{ color: 'var(--text2)', fontSize: 14 }}>{r.return_source || 'Office'} · {r.returned_by || '—'}{r.returned_at ? ` · ${new Date(r.returned_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}` : ''}</div>
              </>) : <span style={{ color: 'var(--text3)' }}>—</span>}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── New WSHC-portal process: Finalise tab asks ONE question first, so the two endings can't be mixed up.
            Yes → today's finalise form (unchanged). No → "End job — no new certificate" (reason + what will happen).
            The answer is never remembered (re-opening the tab asks again). Admin can reopen a finished job. ── */}
      {tab === 'finalise' && isPortalJob(job) && role !== 'Auditor' && job.client_id && (() => {
        const ended = isEnded(job)
        const finished = job.status === 'Completed'
        const late = ended && pfAnswer === 'late'            // client got a certificate after the job was ended
        const offerEnd = canEndJob(job)
        const neverSigned = !job.wshc_apply_email_sent_at
        const days = daysSinceApplyEmail(job)
        const radioRow = (val, text) => (
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', border: `1px solid ${pfAnswer === val ? 'var(--accent, #006363)' : 'var(--border)'}`, borderRadius: 8, padding: '10px 12px', cursor: 'pointer', fontSize: 14 }}>
            <input type="radio" name="pf-answer" checked={pfAnswer === val} onChange={() => setPfAnswer(val)} style={{ marginTop: 3, width: 'auto' }} />
            <span>{text}</span>
          </label>
        )
        const dayInputs = (
          <div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>On-site days per auditor (filled in as an equal share of {job.mandays ?? '—'} man-days — change if different)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div className="field" style={{ margin: 0 }}>
                <label>{job.auditor_name || 'Auditor 1'} — on-site days</label>
                <input type="number" step="0.5" min="0" value={a1Days} onChange={e => setA1Days(e.target.value)} onWheel={e => e.currentTarget.blur()} style={{ width: 130 }} />
              </div>
              {job.auditor2_id && (
                <div className="field" style={{ margin: 0 }}>
                  <label>{job.auditor2_name || 'Auditor 2'} — on-site days</label>
                  <input type="number" step="0.5" min="0" value={a2Days} onChange={e => setA2Days(e.target.value)} onWheel={e => e.currentTarget.blur()} style={{ width: 130 }} />
                </div>
              )}
            </div>
          </div>
        )
        const yesForm = (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {late ? <div style={{ fontSize: 14, color: 'var(--text2)' }}>The auditors’ days are already in their experience log.</div> : dayInputs}
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="field" style={{ margin: 0 }}>
                <label>New bizSAFE expiry date</label>
                <input type="date" value={newExpiry} onChange={e => setNewExpiry(e.target.value)} />
              </div>
              <button className="primary" disabled={!newExpiry || finalising} onClick={finaliseAudit}>{finalising ? 'Saving…' : 'Record expiry & finalise'}</button>
            </div>
          </div>
        )
        const reopenBlock = role === 'Admin' && (
          <div style={{ marginTop: 14 }}>
            {!reopenForm.open ? (
              <button className="sm" onClick={() => setReopenForm({ open: true, reason: '' })}>Reopen job (Admin)</button>
            ) : (
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                <div className="field field-vert" style={{ margin: 0 }}><label>Why reopen? *</label>
                  <textarea rows={2} value={reopenForm.reason} onChange={e => setReopenForm(f => ({ ...f, reason: e.target.value }))} placeholder="e.g. WSHC returned the report / ended by mistake" /></div>
                <div style={{ fontSize: 13, color: 'var(--text3)', margin: '6px 0' }}>The job goes back to waiting for the client. The auditors’ experience-log entries are kept; the client’s expiry is not changed.</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="sm" disabled={officeBusy} onClick={reopenPortalJob}>{officeBusy ? 'Reopening…' : 'Reopen job'}</button>
                  <button className="sm" onClick={() => setReopenForm({ open: false, reason: '' })}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )
        return (
          <div className="card action-live" style={{ marginTop: 16 }}>
            <h2 className="card-title">Finalise audit cycle — new bizSAFE expiry</h2>
            {ended ? (
              <>
                <div style={{ padding: '10px 14px', background: '#FFF8EC', border: '1px solid #E6A817', borderRadius: 8, fontSize: 14, color: '#854F0B', lineHeight: 1.6 }}>
                  Ended without a new certificate{job.wshc_ended_at ? ` on ${fmtDay(job.wshc_ended_at)}` : ''}{job.wshc_ended_by ? ` by ${job.wshc_ended_by}` : ''}.
                  {job.wshc_end_reason && <div style={{ whiteSpace: 'pre-wrap' }}>Reason: {job.wshc_end_reason}</div>}
                  <div>Client’s bizSAFE expiry unchanged{client?.bizsafe_expiry_date ? ` (${fmtDay(client.bizsafe_expiry_date)})` : ''}.</div>
                </div>
                {!late
                  ? <button className="sm" style={{ marginTop: 12 }} onClick={() => setPfAnswer('late')}>Client got a certificate after all → Finalise</button>
                  : <div style={{ marginTop: 14 }}>{yesForm}</div>}
                {reopenBlock}
              </>
            ) : finished ? (
              <>
                <div style={{ padding: '10px 14px', background: '#EAF3DE', borderRadius: 8, fontSize: 14 }}>
                  <span style={{ color: 'var(--brand)' }}>✓</span> Finalised{client?.bizsafe_expiry_date ? ` — new bizSAFE expiry ${fmtDay(client.bizsafe_expiry_date)}` : ''}. Audit closed to the auditor and added to the CT17 maintenance log.
                </div>
                {reopenBlock}
              </>
            ) : !auditFinalised && !offerEnd ? (
              <div style={{ padding: '10px 14px', background: '#FFF3E0', borderRadius: 8, border: '1px solid #E6A817', fontSize: 14, color: '#854F0B' }}>
                Not yet — this is available once the client has been emailed the steps to apply on WSHC.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6 }}>
                  {job.wshc_apply_email_sent_at
                    ? <div>Apply steps emailed {fmtDay(job.wshc_apply_email_sent_at)} to {job.wshc_apply_sent_to || '—'}{days != null ? ` (day ${days})` : ''}</div>
                    : <div>The client has not signed the report (sent {fmtDay(job.report_ack_sent_at)}).</div>}
                  <div>{job.client_wshc_submitted_at ? `Client says they applied on ${fmtDay(job.client_wshc_submitted_at)}` : (job.wshc_apply_email_sent_at ? 'Client has not confirmed applying' : '')}</div>
                </div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Has the client received their new bizSAFE certificate?</div>
                {auditFinalised && radioRow('yes', 'Yes — I’ve checked WSHC and the new certificate is issued')}
                {offerEnd && radioRow('no', 'No — end the job without a new certificate')}
                {pfAnswer === 'yes' && auditFinalised && yesForm}
                {pfAnswer === 'no' && offerEnd && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {dayInputs}
                    <div className="field field-vert" style={{ margin: 0 }}><label>Why is the job ending without a certificate? *</label>
                      <textarea rows={2} value={endReason} onChange={e => setEndReason(e.target.value)} placeholder={neverSigned ? 'e.g. Client never signed the report' : 'e.g. No reply after reminders and 2 calls'} /></div>
                    {job.wshc_apply_email_sent_at && days != null && days < 28 && !job.client_wshc_submitted_at && (
                      <div style={{ fontSize: 14, color: '#854F0B' }}>Reminders are still running. End now only if the client has told you they won’t apply.</div>
                    )}
                    <div style={{ background: 'var(--bg2, #f7f7f5)', borderRadius: 8, padding: '10px 12px', fontSize: 14, lineHeight: 1.7 }}>
                      <div style={{ fontWeight: 600 }}>What will happen</div>
                      {neverSigned && <div>• The client never signed the report</div>}
                      <div>• Audit days are added to each auditor’s experience log (CT17)</div>
                      <div>• The client’s bizSAFE expiry stays {client?.bizsafe_expiry_date ? fmtDay(client.bizsafe_expiry_date) : 'as it is (none on file)'}</div>
                      <div>• No feedback request is sent</div>
                      <div>• You can still finalise later if the client gets a certificate</div>
                    </div>
                    <button className="sm" style={{ alignSelf: 'flex-start' }} disabled={ending} onClick={endJobNoCert}>{ending ? 'Ending…' : 'End job — no new certificate'}</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })()}

      {/* ── Finalise audit cycle — office records the new bizSAFE expiry (the single finalisation point) ── */}
      {tab === 'finalise' && job.audit_type === AUDIT_TYPES.BIZSAFE && role !== 'Auditor' && job.client_id && !isPortalJob(job) && (
        <div className="card action-live" style={{ marginTop: 16 }}>
          {/* Saving updates the client's bizSAFE expiry (and every expiry-driven page), closes the audit to the auditor
              and creates the auditor's CT17 maintenance record. */}
          <h2 className="card-title">Finalise audit cycle — new bizSAFE expiry</h2>
          {job.auditor_closed ? (
            <div style={{ padding: '10px 14px', background: '#EAF3DE', borderRadius: 8, fontSize: 14 }}>
              <span style={{ color: 'var(--brand)' }}>✓</span> Finalised{client?.bizsafe_expiry_date ? ` — new bizSAFE expiry ${format(parseISO(client.bizsafe_expiry_date), 'd MMM yyyy')}` : ''}. Audit closed to the auditor and added to the CT17 maintenance log.
            </div>
          ) : !auditFinalised ? (
            <div style={{ padding: '10px 14px', background: '#FFF3E0', borderRadius: 8, border: '1px solid #E6A817', fontSize: 14, color: '#854F0B' }}>
              ⚠ Not finalised yet. {workflowState(job)
                ? <>Approve the report and record the WSHC submission first.</>
                : <>Record the certification decision in the Decision record below first.</>}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>
                  On-site days per auditor (filled in as an equal share of {job.mandays ?? '—'} man-days — change if different)
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <div className="field" style={{ margin: 0 }}>
                    <label>{job.auditor_name || 'Auditor 1'} — on-site days</label>
                    <input type="number" step="0.5" min="0" value={a1Days} onChange={e => setA1Days(e.target.value)} onWheel={e => e.currentTarget.blur()} style={{ width: 130 }} />
                  </div>
                  {job.auditor2_id && (
                    <div className="field" style={{ margin: 0 }}>
                      <label>{job.auditor2_name || 'Auditor 2'} — on-site days</label>
                      <input type="number" step="0.5" min="0" value={a2Days} onChange={e => setA2Days(e.target.value)} onWheel={e => e.currentTarget.blur()} style={{ width: 130 }} />
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div className="field" style={{ margin: 0 }}>
                  <label>New bizSAFE expiry date</label>
                  <input type="date" value={newExpiry} onChange={e => setNewExpiry(e.target.value)} />
                </div>
                <button className="primary" disabled={!newExpiry || finalising} onClick={finaliseAudit}>{finalising ? 'Saving…' : 'Record expiry & finalise'}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Review & approval (SAC — LEGACY only). For workflow-tracked jobs the approval is captured in the
            Office review checkpoint, so this redundant card is hidden (only shown for legacy submissions). ── */}
      {tab === 'finalise' && needsBizSAFEOrSHMS && job.report_submitted && !workflowState(job) && (
        <div className="card" style={{ marginTop: 16 }}>
          <h2 className="card-title">Review &amp; approval{workflowState(job) && <span className="badge badge-gray" style={{ marginLeft: 8, fontSize: 13 }}>SAC record</span>}</h2>
          <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 14 }}>
            {workflowState(job)
              ? 'Supporting SAC record only. The approval decision is made once, in the Office review checkpoint above — this card does not create a separate decision path.'
              : 'Record who reviewed and approved this audit before the certification decision. The reviewer and approver may be the same person, but cannot be the auditor who conducted the audit.'}
          </p>

          {job.reviewed_by && job.approved_by && (
            <div style={{ padding: '12px 14px', background: '#EAF3DE', borderRadius: 8, border: '1px solid #639922', marginBottom: 14 }}>
              <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 10 }}><span style={{ color: 'var(--brand)' }}>✓</span> Review &amp; approval recorded</div>
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '6px 12px', fontSize: 14 }}>
                <span style={{ fontWeight: 500 }}>Reviewed by</span>
                <span>{job.reviewed_by}{job.reviewed_at ? ` · ${new Date(job.reviewed_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}` : ''}</span>
                <span style={{ fontWeight: 500 }}>Approved by</span>
                <span>{job.approved_by}{job.approved_at ? ` · ${new Date(job.approved_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}` : ''}</span>
              </div>
            </div>
          )}

          {/* When the lean workflow is active the editable reviewer/approver form is suppressed — approval
              happens in the Office review checkpoint. The card stays only as a read-only SAC record. */}
          {workflowState(job) ? (
            !(job.reviewed_by && job.approved_by) && (
              <div style={{ padding: '10px 14px', background: 'var(--bg2)', borderRadius: 8, fontSize: 14, color: 'var(--text2)' }}>
                Approval is captured by the Decision-Maker in the <strong>Office review</strong> checkpoint above (Approve &amp; submit to WSHC). No separate entry needed here.
              </div>
            )
          ) : isAuditorOnJob() ? (
            <div style={{ padding: '10px 14px', background: '#FFF3E0', borderRadius: 8, border: '1px solid #E6A817', fontSize: 14, color: '#854F0B' }}>
              ⚠ You conducted this audit. Review &amp; approval must be recorded by a different person (auditor cannot review or approve their own audit).
            </div>
          ) : (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                <div className="field">
                  <label>Reviewed by *</label>
                  <input value={reviewForm.reviewedBy}
                    onChange={e => setReviewForm(f => ({ ...f, reviewedBy: e.target.value }))}
                    placeholder={profile?.name || 'Reviewer name'} />
                </div>
                <div className="field">
                  <label>Approved by *</label>
                  <input value={reviewForm.approvedBy}
                    onChange={e => setReviewForm(f => ({ ...f, approvedBy: e.target.value }))}
                    placeholder={profile?.name || 'Approver name'} />
                </div>
              </div>
              <button className="primary sm" onClick={saveReviewApproval}
                disabled={savingReview || !reviewForm.reviewedBy.trim() || !reviewForm.approvedBy.trim()}>
                {savingReview ? 'Saving…' : (job.reviewed_by && job.approved_by ? 'Update review & approval' : 'Record review & approval')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Decision record (ISO 17021) — legacy card; the Office review checkpoint replaces it for
            workflow-tracked jobs, so only show it for legacy submissions without a workflow state. ── */}
      {tab === 'finalise' && needsBizSAFEOrSHMS && job.report_submitted && !workflowState(job) && (
        <div className="card" style={{ marginTop: 16 }}>
          <h2 className="card-title">Decision record</h2>
          <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 14 }}>
            ISO 17021 requires an independent decision to be recorded by a competent person who did not conduct the audit.
            This is a certification decision — not report approval.
          </p>
          {auditDecision ? (
            <div style={{ padding: '12px 14px', background: '#EAF3DE', borderRadius: 8, border: '1px solid #639922' }}>
              <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 10 }}>
                <span style={{ color: 'var(--brand)' }}>✓</span> Decision recorded — {auditDecision.decision_outcome}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '6px 12px', fontSize: 14 }}>
                <span style={{ fontWeight: 500 }}>Decided by</span>
                <span>{auditDecision.decision_by_name} ({auditDecision.decision_by_role})</span>
                <span style={{ fontWeight: 500 }}>Date & time</span>
                <span>{new Date(auditDecision.decided_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}</span>
                {auditDecision.decision_remarks && (
                  <>
                    <span style={{ fontWeight: 500 }}>Remarks</span>
                    <span>{auditDecision.decision_remarks}</span>
                  </>
                )}
              </div>
            </div>
          ) : (profile?.name === job.auditor_name || profile?.name === job.auditor2_name) ? (
            <div style={{ padding: '10px 14px', background: '#FFF3E0', borderRadius: 8, border: '1px solid #E6A817', fontSize: 14, color: '#854F0B' }}>
              ⚠ You conducted this audit. The decision must be recorded by a different person to satisfy ISO 17021 independence requirements.
            </div>
          ) : (
            <div>
              <div className="field">
                <label>Decision outcome *</label>
                <select
                  value={decisionForm.outcome}
                  onChange={e => setDecisionForm(f => ({ ...f, outcome: e.target.value }))}
                >
                  <option value="Proceed">Proceed — audit conducted satisfactorily</option>
                  <option value="Issue report">Issue report — findings ready for submission to WSHC</option>
                  <option value="Refer back">Refer back — additional audit work required</option>
                  <option value="Withhold">Withhold — significant concerns identified</option>
                </select>
              </div>
              <div className="field field-vert">
                <label>Remarks (optional)</label>
                <textarea
                  value={decisionForm.remarks}
                  onChange={e => setDecisionForm(f => ({ ...f, remarks: e.target.value }))}
                  rows={2}
                  placeholder="Observations, conditions, or notes on the audit outcome…"
                />
              </div>
              <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 10 }}>
                Recording as: <strong>{profile?.name || 'You'}</strong> ({role}) · {new Date().toLocaleDateString('en-SG')}
              </div>
              <button
                className="primary sm"
                onClick={saveDecision}
                disabled={savingDecision}
              >
                {savingDecision ? 'Saving…' : 'Record decision'}
              </button>
            </div>
          )}
        </div>
      )}

      </div>{/* /jd-main */}

      {/* ── Persistent status checklist rail (real-time complete / missing) ── */}
      <div style={{ alignSelf: 'start', position: 'sticky', top: 58 }}>
        <div className="card" style={{ padding: '12px 14px' }}>
          <div className="card-title">Status checklist</div>
          {(() => {
            const ackPlan = !!(job.ack_signature && job.plan_signature && job.ack_signature === job.plan_signature)
            // Ordered to follow the real lifecycle. Each `done` reads the actual backend field — not a guess.
            const items = [
              { label: 'Application accepted (quotation)', done: !!quoteAccept || !!job.auditor_id || !!job.report_submitted || ws === 'Pending WSHC Outcome' || ws === 'Completed', tab: 'plan' },
              { label: 'Auditor assigned', done: !!job.auditor_id, tab: 'plan' },
              { label: 'Audit plan sent', done: !!(job.plan_sent_date || job.audit_plan_sent_at), tab: 'plan' },
              // COI is only "complete" once an auditor is assigned AND every assigned auditor's COI is in
              // (an empty list with no auditor must NOT read as done). N/A for non-bizSAFE/SHMS audits.
              { label: 'COI completed', done: needsBizSAFEOrSHMS ? (!!job.auditor_id && ((job.combined_group_id && !job.is_combined_primary) ? combinedCoiOk : coiMissingList().length === 0)) : true, tab: 'plan' },
              { label: 'Client acknowledged plan', done: ackPlan, tab: 'plan' },
              { label: 'Report submitted by auditor', done: !!job.report_submitted, tab: 'report' },
              { label: 'Report reviewed & approved', done: !!job.office_review_at, tab: 'report' },
              { label: 'Client acknowledged report', done: !!reportAckValid, tab: 'report' },
              { label: isPortalJob(job) ? 'Apply steps emailed to client' : 'Submitted to WSHC', done: ws === 'Pending WSHC Outcome' || ws === 'Completed', tab: 'report' },
              { label: isEnded(job) ? 'Ended — no new certificate' : 'Finalised (expiry recorded)', done: ws === 'Completed' || job.auditor_closed, tab: 'finalise' },
            ]
            return items.map((it, i) => (
              <div key={i} onClick={() => setJtab(it.tab)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 13, cursor: 'pointer', borderTop: i ? '0.5px dotted var(--border)' : 'none' }}>
                <span style={{ width: 16, textAlign: 'center', color: it.done ? 'var(--brand)' : 'var(--text3)' }}>{it.done ? '✓' : '○'}</span>
                <span style={{ color: it.done ? 'var(--text)' : 'var(--text2)' }}>{it.label}</span>
              </div>
            ))
          })()}
        </div>
      </div>

      </div>{/* /grid */}
    </fieldset>
    </div>
  )
}
